// Configs for pages/Accounts/ConfigVerification.jsx — one entry per legacy verification screen that
// is a plain "queue → detail → verify/approve" flow. See the contract at the top of
// ConfigVerification.jsx. CONFIG_VERIFICATION_ROUTES maps inbox paths / categories to a config key.
import { ScrollText, Coins, Users, UserSquare, FileText, Factory, ShoppingBag, AlertOctagon, BookOpen, BookMarked, FolderTree, FolderOpen, Folders, Library, Percent, Hash, Wallet, PieChart, Hammer, Banknote, NotebookPen, Landmark, PackageOpen, Recycle, PackageCheck, ArrowLeftRight, Truck, Forklift, Combine, PackagePlus, Store, Archive, HandCoins, ReceiptText, Barcode, FileDiff, BadgePercent, Eraser, Building2, Ship, FileSpreadsheet, FilePen, PackageSearch } from 'lucide-react';
import { formatIndianCurrency } from '../../utilities/amountToTextHelper';
import {
    CheckedItemsTable, allItemsChecked, TradeItemSummaryModal, Block, SimpleTable, fmt, WIPPartyEditor,
    ItemsTransferItemsTable, itemsTransferDepOptions, itemsTransferDep, itemsTransferApprovalAtDefine, itemsTransferIsConsumable, RouteTableModal,
    VendorCMSVerifyGrid, downloadVendorCMSExcel,
} from './verificationParts';
import { buildBadDebtUrl, buildCostCenterDocUrl, buildCCBudgetDocUrl, buildVendorInvoiceUrl, S3_FOLDERS } from '../../config/s3Config';
import { uploadFileToS3 } from '../../api/commonAPI/awsUploadAPI';
import { postRoute } from '../../api/AccountsAPI/configVerificationAPI';

const money = (v) => (v != null && v !== '' ? `₹ ${formatIndianCurrency(v)}` : null);
// Cost center types whose budget is per financial year (legacy: Non-Performing / Capital / Other Capital).
const isNonPerformingCC = (t) => ['Non-Performing', 'Capital', 'Other Capital'].includes(t);

// Cost center PDFs from Getccuploadocsexists (returned only for users in Budget View Attachment Config).
// execPath overrides the lookup's approved-budget-for-execution file (the CC budget record carries its own).
const CCDocsBlock = ({ docs, execPath }) => {
    const doc = Array.isArray(docs) ? docs[0] : docs;
    const shown = doc && doc.contractscopeisexists === 'Yes';
    const exec = execPath !== undefined ? execPath : shown && doc.Approvedbudgetexecution;
    const links = [
        shown && doc.contractscope && ['Scope check list approved by contracts', buildCostCenterDocUrl(doc.contractscope)],
        shown && doc.contractpretenderBudget && ['Client Work Order confirmation with T & C approved by contracts', buildCostCenterDocUrl(doc.contractpretenderBudget)],
        exec && ['Approved budget for execution', buildCCBudgetDocUrl(exec)],
    ].filter(Boolean);
    if (!links.length) return null;
    return (
        <Block title="Documents">
            <div className="flex flex-wrap gap-3">
                {links.map(([label, url]) => (
                    <a key={label} href={url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold shadow hover:shadow-lg">
                        <FileText className="w-4 h-4" /> {label}
                    </a>
                ))}
            </div>
        </Block>
    );
};

// Legacy VerifyCCSEPPayView display names for SEPPayments.PaymentFor.
const SEP_PAYMENT_LABELS = {
    StaffPF: 'Staff PF', LabourPF: 'Labour PF', StaffESI: 'Staff ESI', LabourESI: 'Labour ESI', LabourAdvance: 'Labour Advance',
};
const sepPaymentLabel = (v) => SEP_PAYMENT_LABELS[v] || v;

// Items Transfer inbox paths carry ?CCType=PCC|NPCC; fall back to the category's (PCC)/(NPCC).
const itemsTransferCCType = (path, category) => {
    const m = /cctype=(n?pcc)/i.exec(path || '');
    if (m) return m[1].toUpperCase();
    return /\(npcc\)/i.test(category || '') ? 'NPCC' : 'PCC';
};

// Store Closing verification: the verifier may change the dates / alert note (legacy VerifyStoreClosingView).
// Reschedule (type 3) defaults to today, between the requested closing date and 30 days out; suspend types
// (2, 4) keep the requested window, never earlier than the requested start. Values are ISO dates (yyyy-mm-dd).
const storeCloseBounds = (d) => {
    const today = isoDate(new Date());
    const plus30 = isoDate(new Date(Date.now() + 30 * 86400000));
    return { today, plus30, closeMin: isoDate(d.SCCClosingDate), startMin: isoDate(d.SCCStartDate) };
};
const storeCloseValues = (d, ext) => {
    const { today, closeMin } = storeCloseBounds(d);
    const type = String(d.SClosingTypeid);
    const defClose = type === '3' ? (closeMin && closeMin > today ? closeMin : today) : isoDate(d.SCCClosingDate);
    return {
        closingDate: ext.closingDate ?? defClose,
        startDate: ext.startDate ?? isoDate(d.SCCStartDate),
        endDate: ext.endDate ?? isoDate(d.SCCEndDate),
        alertNote: ext.alertNote ?? (d.SAlertnote || ''),
    };
};
const StoreCloseEditor = ({ d, ext, setExt }) => {
    const type = String(d.SClosingTypeid);
    const v = storeCloseValues(d, ext);
    const { plus30, closeMin, startMin } = storeCloseBounds(d);
    const set = (k) => (e) => { const val = e.target.value; setExt((p) => ({ ...p, [k]: val })); };
    const input = 'w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-60';
    const label = 'block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1';
    return (
        <Block title="Closing Details">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {(type === '1' || type === '3') && (
                    <div>
                        <label className={label}>Closing Date</label>
                        <input type="date" className={input} value={v.closingDate} disabled={type === '1'}
                            min={type === '3' ? closeMin : undefined} max={type === '3' ? plus30 : undefined} onChange={set('closingDate')} />
                    </div>
                )}
                {(type === '2' || type === '4') && (
                    <>
                        <div>
                            <label className={label}>Store Suspend Start Date</label>
                            <input type="date" className={input} value={v.startDate} min={startMin} onChange={set('startDate')} />
                        </div>
                        <div>
                            <label className={label}>Store Suspend End Date</label>
                            <input type="date" className={input} value={v.endDate} min={v.startDate || startMin} onChange={set('endDate')} />
                        </div>
                    </>
                )}
                <div>
                    <label className={label}>Alert Note <span className="text-red-500">*</span></label>
                    <input type="text" className={`${input} uppercase`} maxLength={50} value={v.alertNote} onChange={set('alertNote')} />
                </div>
            </div>
        </Block>
    );
};

// LC/BG type as the legacy view spells it (LC → Letter of Credit, anything else → Bank Guarantee).
const lcbgTypeName = (t) => (t === 'LC' ? 'Letter of Credit' : t ? 'Bank Guarantee' : '');

// SP invoice tax total as legacy VerifySPPOInvoiceView computes it: the TaxList sum when there are tax lines, else the stored total.
const sppoTaxTotal = (d) => ((d.TaxList || []).length ? d.TaxList.reduce((a, x) => a + (Number(x.Amount) || 0), 0) : d.TaxTotal);

// Item Code Updation verification: the requested values, editable by the verifier (legacy VerifyItemCodeUpdationView).
// ext holds the verifier's edits; untouched fields fall back to the request.
const itemCodeUpdValues = (d, ext) => ({
    Itemname: ext.Itemname ?? (d.Itemname || ''),
    Basicprice: ext.Basicprice ?? (d.Basicprice != null ? String(d.Basicprice) : ''),
    HSNCode: ext.HSNCode ?? (d.HSNCode != null ? String(d.HSNCode) : ''),
    Specification: ext.Specification ?? (d.Specification || ''),
    Units: ext.Units ?? (d.Units != null ? String(d.Units) : ''),
});
const ItemCodeUpdationEditor = ({ d, aux, ext, setExt }) => {
    const v = itemCodeUpdValues(d, ext);
    const hsn = Array.isArray(aux.icuHsn) ? aux.icuHsn : [];
    const units = Array.isArray(aux.icuUnits) ? aux.icuUnits : [];
    const set = (k, filter) => (e) => {
        const val = e.target.value;
        if (filter && val !== '' && !filter.test(val)) return;
        setExt((p) => ({ ...p, [k]: val }));
    };
    const input = 'w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white';
    const label = 'block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1';
    return (
        <Block title="Requested Changes (editable)">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                    <label className={label}>Item Code</label>
                    <input className={input} value={d.ItemCode || ''} disabled />
                </div>
                <div>
                    <label className={label}>Item Name <span className="text-red-500">*</span></label>
                    <input className={`${input} uppercase`} value={v.Itemname} onChange={set('Itemname')} />
                </div>
                <div>
                    <label className={label}>Basic Price <span className="text-red-500">*</span></label>
                    <input className={input} value={v.Basicprice} onChange={set('Basicprice', /^\d*\.?\d{0,2}$/)} />
                </div>
                <div className="sm:col-span-2 lg:col-span-3">
                    <label className={label}>HSN Code <span className="text-red-500">*</span></label>
                    <select className={input} value={v.HSNCode} onChange={set('HSNCode')}>
                        <option value="">-Please Select-</option>
                        {hsn.map((h) => <option key={h.HSNID} value={String(h.HSNID)}>{h.HSNCode}</option>)}
                    </select>
                </div>
                <div className="sm:col-span-2">
                    <label className={label}>Specification <span className="text-red-500">*</span></label>
                    <input className={input} value={v.Specification} onChange={set('Specification')} />
                </div>
                <div>
                    <label className={label}>Units <span className="text-red-500">*</span></label>
                    <select className={input} value={v.Units} onChange={set('Units')}>
                        <option value="">-Please Select-</option>
                        {units.map((u) => <option key={u.Unitsval} value={String(u.Unitsval)}>{u.Unitstext}</option>)}
                    </select>
                </div>
            </div>
        </Block>
    );
};

// Sum of the amounts typed in an account-head budget editor ({ code: { amount, checked } }).
const dcaSubTotal = (dca) => Object.values(dca).reduce((a, x) => a + (parseFloat(x.amount) || 0), 0);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Dates the API hands back as DateTime.ToString() text → 'dd-MMM-yyyy' for display / 'yyyy-mm-dd' for date inputs.
const parseDate = (v) => {
    if (!v) return null;
    // 'dd-MMM-yyyy' (SQL style 106) is not portable through Date(), so read it explicitly.
    const m = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/.exec(String(v).trim());
    const mi = m ? MONTHS.findIndex((x) => x.toLowerCase() === m[2].toLowerCase()) : -1;
    const dt = m && mi >= 0 ? new Date(Number(m[3]), mi, Number(m[1])) : new Date(v);
    return Number.isNaN(dt.getTime()) || dt.getFullYear() < 1900 ? null : dt;
};
const pad = (n) => String(n).padStart(2, '0');
const displayDate = (v) => { const dt = parseDate(v); return dt ? `${pad(dt.getDate())}-${MONTHS[dt.getMonth()]}-${dt.getFullYear()}` : v || null; };
const isoDate = (v) => { const dt = parseDate(v); return dt ? `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}` : ''; };

// Client invoice read-only sections (Client / Manufacturing / Trading invoice views share this layout).
const invoiceSections = (d) => [
    {
        title: 'Invoice',
        fields: [
            ['Invoice Number', d.ClientInvoiceNo], ['PO Number', d.PONumber], ['Cost Center', d.CCCode],
            ['Invoice Sub Type', d.InvoiceSubType], ['Client', d.ClientName], ['Sub Client', d.SubClientName],
            ['Running Account No', d.RANO], ['Invoice Date', d.VerificationInvDate], ['Invoice Making Date', d.VerificationMakingDate],
            ['Basic Value', money(d.BasicValue)], ['Company GST', d.CompanyGST], ['Client GST', d.ClientGST],
            ['GST Applicable', d.IsGstApplicable], ['TCS Applicable', d.IsTCSApplicable],
        ],
    },
    d.IsGstApplicable === 'Yes' && {
        title: 'GST Taxes',
        fields: [
            ['GST Type', d.GSTType], ['Account Head', d.Taxdcas],
            ...(d.Statecheck
                ? [['CGST Sub Account Head', d.Cgstsdca], ['CGST Amount', money(d.Cgstsdcaamt)],
                    ['SGST Sub Account Head', d.Sgstsdca], ['SGST Amount', money(d.Sgstsdcaamt)]]
                : [['IGST Sub Account Head', d.Igstsdca], ['IGST Amount', money(d.Igstsdcaamt)]]),
            ['Total Tax', money(d.TaxTotal)],
        ],
    },
    d.IsTCSApplicable === 'Yes' && {
        title: 'TCS',
        fields: [['Account Head', d.TCSDCA], ['Sub Account Head', d.TCSSDCA], ['Amount', money(d.TCSAmount)]],
    },
    { fields: [['Transaction Amount', money(d.Total)], d.InvoiceRemarks && ['Remarks', d.InvoiceRemarks, true]] },
].filter(Boolean);

const invoiceHeader = {
    title: (r, d) => d.ClientInvoiceNo,
    subtitle: (r, d) => money(d.Total),
    chips: (r, d) => [`PO ${d.PONumber}`, d.CCCode, d.InvoiceSubType],
};

export const VERIFICATION_CONFIGS = {
    // Legacy /AccountsApproval/VerifyShareIssuance
    ShareIssuance: {
        title: 'Share Issuance Verification',
        successLabel: 'Share Issuance',
        noun: 'share issuance',
        icon: ScrollText,
        searchPlaceholder: 'Search by transaction no, scheme, date…',
        queue: { route: 'Accounts/VerifyShareIssuanceGrid', params: ({ roleId }) => ({ Roleid: roleId }) },
        itemKey: (r) => r.Refno,
        card: { title: (r) => r.SchemeName, subtitle: (r) => `Transaction ${r.Refno}`, meta: (r) => r.Date, amount: (r) => (r.SharesAllocated ? `${r.SharesAllocated} shares` : null) },
        searchText: (r) => `${r.Refno} ${r.SchemeName} ${r.Date}`,
        detail: { route: 'Accounts/VerifyShareIssuanceView', params: (r) => ({ Refno: r.Refno }) },
        moid: (r, d) => d.MOID,
        remarksKey: (r, d) => d.Refno,
        showReturn: 'No',
        header: {
            title: (r, d) => d.SchemeName || r.SchemeName,
            subtitle: (r, d) => `Transaction ${d.Refno}`,
            chips: (r, d) => [d.ShareIssuanceType, r.Date && `Date: ${r.Date}`],
        },
        sections: (r, d) => [{
            fields: [
                ['Share Issuance Type', d.ShareIssuanceType],
                ['Scheme Name', d.SchemeName],
                ['Start Date', d.StartDate],
                ['End Date', d.EndDate],
                ['Shares Allocated', d.SharesAllocated],
                ['Scheme Description', d.SchemeDescription, true],
            ],
        }],
        // spApproveShareIssuance reads the action from Status and the note from Remarks.
        approve: {
            route: 'Accounts/ApproveShareIssuance',
            payload: (r, d, { action, note, user, roleId }) => ({ Refno: d.Refno, Status: action, Remarks: note, RoleId: roleId, CreatedBy: user }),
            ok: ['Submitted'],
        },
    },

    // Legacy /AccountsApproval/VerifyESOPFaceValueReceipt
    ESOPFaceValueReceipt: {
        title: 'ESOP Face Value Receipt Verification',
        successLabel: 'ESOP Face Value Receipt',
        noun: 'ESOP receipt',
        icon: Coins,
        searchPlaceholder: 'Search by transaction no, option ID, date…',
        queue: { route: 'Accounts/VerifyESOPFaceValueReceiptGrid', params: ({ roleId }) => ({ Roleid: roleId }) },
        itemKey: (r) => r.ExerciseRefno,
        card: { title: (r) => `Option ${r.ExerciseOptionID}`, subtitle: (r) => `Transaction ${r.ExerciseRefno}`, meta: (r) => r.Date, amount: (r) => money(r.ExerciseAmount) },
        searchText: (r) => `${r.ExerciseRefno} ${r.ExerciseOptionID} ${r.Date} ${r.ExerciseAmount}`,
        detail: { route: 'Accounts/VerifyESOPFaceValueReceiptView', params: (r) => ({ Refno: r.ExerciseRefno }) },
        moid: (r, d) => d.MOID,
        remarksKey: (r, d) => d.Refno,
        showReturn: 'No',
        header: {
            title: (r, d) => d.EmployeeName || `Option ${d.ExerciseOptionID}`,
            subtitle: (r, d) => money(d.ExerciseAmount),
            chips: (r, d) => [`Option ${d.ExerciseOptionID}`, d.Bank],
        },
        sections: (r, d) => [
            {
                title: 'Employee & Exercise',
                fields: [
                    ['Option ID', d.ExerciseOptionID], ['Employee ID', d.EmployeeId], ['Employee Name', d.EmployeeName],
                    ['Department', d.Department], ['Position', d.Position], ['Exercise Date', d.ExerciseDate],
                    ['Shares Exercised', d.NumberofSharesExercised], ['Exercise Price', money(d.ExercisePrice)],
                ],
            },
            {
                title: 'Receipt',
                fields: [
                    ['Bank', d.Bank], ['Mode of Pay', d.ModeofPay], ['No', d.No], ['Date', d.Date],
                    ['Amount', money(d.ExerciseAmount)], d.Remarks && ['Remarks', d.Remarks, true],
                ],
            },
        ],
        // The data layer reads RoleID / Createdby on this entity.
        approve: {
            route: 'Accounts/ApproveESOPFaceValueReceipt',
            payload: (r, d, { action, note, user, roleId }) => ({ Refno: d.Refno, Status: action, Remarks: note, RoleID: roleId, Createdby: user }),
            ok: ['Submitted'],
        },
    },

    // Legacy /AccountsApproval/VerifyClient (+ VerifyClientGrid / VerifyClientView)
    Client: {
        title: 'Client Verification',
        successLabel: 'Client',
        noun: 'client',
        icon: Users,
        searchPlaceholder: 'Search by client code, name, nature of group…',
        queue: { route: 'Accounts/GetVerifyClientDetails', params: ({ roleId }) => ({ Roleid: roleId }) },
        itemKey: (r) => r.Client_Code,
        card: { title: (r) => r.Client_Name, subtitle: (r) => r.Client_Code, meta: (r) => r.NatureGroupName },
        searchText: (r) => `${r.Client_Code} ${r.Client_Name} ${r.NatureGroupName}`,
        detail: { route: 'Accounts/GetVerifyClientDetailsbyCode', params: (r) => ({ ClientCode: r.Client_Code }) },
        moid: (r, d) => d.MOID,
        remarksKey: (r, d) => d.Client_Code,
        showReturn: 'Yes',
        // Legacy drops Return and Reject when the client is being closed.
        excludeActions: (r, d) => (d.ClientStatus === 'Closed' ? ['return', 'reject'] : []),
        isReturned: (r) => String(r.Status) === '0',
        header: {
            title: (r, d) => d.Client_Name,
            subtitle: (r, d) => d.Client_Code,
            chips: (r, d) => [d.NatureGroupName, d.ClientStatus && `Client Status: ${d.ClientStatus}`],
        },
        sections: (r, d) => [
            {
                fields: [
                    ['Client Code', d.Client_Code], ['Client Name', d.Client_Name], ['Nature of Group', d.NatureGroupName],
                    ['Contact Person', d.Contact_Person_Name], ['Phone No', d.Person_PhoneNo],
                    ['TIN Number', d.TINNo], ['PAN Number', d.PANNo], ['TAN Number', d.TANNo],
                    ['Client Status', d.ClientStatus], ['Address', d.Address, true],
                ],
            },
            {
                fields: [
                    ['Group Name', d.GroupName],
                    d.SubGroupId && d.SubGroupId !== '0' && ['Sub Group Name', d.SubGroupName],
                    d.LedgerId > 0 && ['Opening Balance', money(d.OpeningBalance)],
                    d.LedgerId > 0 && ['Balance As On Date', d.BalanceAsOnDate],
                    d.LedgerId > 0 && ['Ledger Value Type', d.LedgerValueType],
                ],
            },
        ],
        approve: {
            route: 'Accounts/ApproveClient',
            payload: (r, d, { action, note, user, roleId }) => ({
                Client_Code: d.Client_Code, VerificationType: action, ApprovalNote: note,
                RoleId: roleId, Createdby: user, ClientStatus: d.ClientStatus,
            }),
            ok: ['Submited', 'Submitted'],
        },
        // Legacy UpdateClientData → spInsertUpdateClient (Action 2, CheckUpdationType 'ReturnUpdate').
        resubmit: {
            fields: [
                { key: 'Client_Name', label: 'Client Name', required: true },
                { key: 'TINNo', label: 'TIN No', required: true },
                { key: 'PANNo', label: 'PAN No', required: true },
                { key: 'TANNo', label: 'TAN No', required: true },
                { key: 'Contact_Person_Name', label: 'Contact Person Name', required: true },
                { key: 'Person_PhoneNo', label: 'Contact Person No', required: true, filter: /^\d{0,10}$/, maxLength: 10 },
                { key: 'hasLedger', label: '', show: () => false },
                { key: 'OpeningBalance', label: 'Opening Balance', required: true, filter: /^-?\d*\.?\d{0,2}$/, show: (v) => v.hasLedger },
                { key: 'BalanceAsOnDate', label: 'Balance As On Date', required: true, readOnly: true, show: (v) => v.hasLedger },
                {
                    key: 'LedgerValueType', label: 'Ledger Value Type', type: 'select', required: true, show: (v) => v.hasLedger,
                    options: [{ value: 'Credit', label: 'Credit' }, { value: 'Debit', label: 'Debit' }],
                },
                { key: 'Address', label: 'Address', type: 'textarea', required: true },
            ],
            initial: (r, d) => ({
                Client_Name: d.Client_Name || '', TINNo: d.TINNo || '', PANNo: d.PANNo || '', TANNo: d.TANNo || '',
                Contact_Person_Name: d.Contact_Person_Name || '', Person_PhoneNo: d.Person_PhoneNo || '', Address: d.Address || '',
                hasLedger: d.LedgerId > 0,
                OpeningBalance: d.OpeningBalance != null ? String(d.OpeningBalance) : '',
                BalanceAsOnDate: d.BalanceAsOnDate || '', LedgerValueType: d.LedgerValueType || '',
            }),
            validate: (v) => (v.Person_PhoneNo && v.Person_PhoneNo.length !== 10 ? ['Enter 10 digit Phone No'] : []),
            route: 'Accounts/UpdateClient',
            payload: (r, d, v, { user, roleId }) => ({
                ClientID: d.ClientID, Client_Code: d.Client_Code, Client_Name: v.Client_Name.trim(),
                TINNo: v.TINNo.trim(), PANNo: v.PANNo.trim(), TANNo: v.TANNo.trim(),
                Contact_Person_Name: v.Contact_Person_Name.trim(), Person_PhoneNo: v.Person_PhoneNo, Address: v.Address.trim(),
                Action: 2, Createdby: user, RoleId: roleId, CheckUpdationType: 'ReturnUpdate',
                LedgerValueType: v.hasLedger ? v.LedgerValueType : null,
                OpeningBalance: v.hasLedger ? parseFloat(v.OpeningBalance) || 0 : 0,
                BalanceAsOnDate: v.hasLedger ? v.BalanceAsOnDate : null,
            }),
            ok: ['Updated', 'Submited'],
        },
    },

    // Legacy /AccountsApproval/VerifySubClient (+ VerifySubClientGrid / VerifySubClientView)
    SubClient: {
        title: 'Sub Client Verification',
        successLabel: 'Sub Client',
        noun: 'sub client',
        icon: UserSquare,
        searchPlaceholder: 'Search by sub client, client, branch…',
        queue: { route: 'Accounts/GetVerificationSubClient', params: ({ roleId }) => ({ Roleid: roleId }) },
        itemKey: (r) => r.SubClientCode,
        card: { title: (r) => r.Branch || r.SubClientCode, subtitle: (r) => r.SubClientCode, meta: (r) => `${r.ClientCode} · ${r.ClientName}` },
        searchText: (r) => `${r.SubClientCode} ${r.ClientCode} ${r.ClientName} ${r.Branch}`,
        detail: { route: 'Accounts/GetVerificationSubClientbycode', params: (r) => ({ SubClientcode: r.SubClientCode }) },
        // Legacy loads the GST numbers only when GST applies (TaxFor = the sub client code).
        rowAux: [{
            name: 'subClientTaxes',
            route: 'Accounts/GetVerifyClientTaxes',
            params: (r) => ({ ClientCode: r.SubClientCode }),
            when: (r, d) => String(d.GST_Applicable || '').toUpperCase() === 'YES',
        }],
        moid: (r, d) => d.MOID,
        remarksKey: (r, d) => d.SubClientCode,
        showReturn: 'Yes',
        excludeActions: (r, d) => (d.SubClientStatus === 'Closed' ? ['return', 'reject'] : []),
        isReturned: (r) => String(r.Status) === '0',
        // The returned-row form edits a multi-row GST table (states / numbers) — resubmitted from the
        // Sub Client screen instead.
        returnedNotice: 'Correct and resubmit it from the Sub Client screen — there is nothing to verify until it is resubmitted.',
        header: {
            title: (r, d) => d.Branch || d.SubClientCode,
            subtitle: (r, d) => d.SubClientCode,
            chips: (r, d) => [`Client ${d.ClientCode}`, d.SubClientStatus && `Status: ${d.SubClientStatus}`],
        },
        sections: (r, d) => [
            {
                fields: [
                    ['Client Code', d.ClientCode], ['Sub Client Code', d.SubClientCode], ['Nature of Group', d.NatureGroupName],
                    ['Branch Name', d.Branch], ['Contact Person', d.SC_Person_Name], ['Phone Number', d.SC_Phone_No],
                    ['TIN Number', d.SC_TINNo], ['PAN Number', d.SC_PANNo], ['TAN Number', d.SC_TANNo],
                    ['Sub Client Status', d.SubClientStatus], ['GST Applicable', d.GST_Applicable], ['Address', d.Address, true],
                ],
            },
            d.LedgerId > 0 && {
                fields: [
                    ['Opening Balance', money(d.OpeningBalance)], ['Balance As On Date', d.BalanceAsOnDate], ['Ledger Value Type', d.LedgerValueType],
                ],
            },
        ].filter(Boolean),
        extra: (r, d, { aux }) => {
            const taxes = aux.subClientTaxes;
            if (String(d.GST_Applicable || '').toUpperCase() !== 'YES' || !taxes || !taxes.length) return null;
            return (
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 border-gray-200 dark:border-gray-700">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">GST Details</p>
                    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-100 dark:bg-gray-800">
                                    <th className="px-4 py-2 text-center text-[10px] font-bold text-gray-500 uppercase">GST No</th>
                                    <th className="px-4 py-2 text-center text-[10px] font-bold text-gray-500 uppercase">State</th>
                                </tr>
                            </thead>
                            <tbody>
                                {taxes.map((t, i) => (
                                    <tr key={i} className="border-t border-gray-100 dark:border-gray-700">
                                        <td className="px-4 py-2 text-center text-gray-700 dark:text-gray-300">{t.TaxNo}</td>
                                        <td className="px-4 py-2 text-center text-gray-700 dark:text-gray-300">{t.State}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        },
        approve: {
            route: 'Accounts/ApproveSubClient',
            payload: (r, d, { action, note, user, roleId }) => ({
                SubClientCode: d.SubClientCode, ClientCode: d.ClientCode, Verificationtype: action, ApprovalNote: note,
                RoleId: roleId, Createdby: user, SubClientStatus: d.SubClientStatus,
            }),
            ok: ['Submited', 'Submitted'],
        },
    },

    // Legacy /AccountsApproval/VerifyClientInvoice (+ VerifyClientInvoiceGrid / VerifyClientInvoiceView)
    ClientInvoice: {
        title: 'Client Invoice Verification',
        successLabel: 'Client Invoice',
        noun: 'invoice',
        icon: FileText,
        searchPlaceholder: 'Search by invoice no, PO no, cost center…',
        queue: { route: 'Accounts/GetVerificationClientInvoice', params: ({ roleId }) => ({ Roleid: roleId }) },
        itemKey: (r) => r.ClientInvoiceNo,
        card: { title: (r) => r.ClientInvoiceNo, subtitle: (r) => `PO ${r.PONumber}`, meta: (r) => `${r.CCCode} · ${r.InvoiceSubType}` },
        searchText: (r) => `${r.ClientInvoiceNo} ${r.PONumber} ${r.CCCode} ${r.InvoiceSubType}`,
        detail: { route: 'Accounts/GetVerificationClientInvoicebyNo', params: (r) => ({ InvoiceNo: r.ClientInvoiceNo }) },
        moid: (r, d) => d.MOID,
        remarksKey: (r, d) => d.ClientInvoiceNo,
        showReturn: 'Yes',
        isReturned: (r) => String(r.Status) === '0',
        header: invoiceHeader,
        sections: (r, d) => invoiceSections(d),
        // spApproveClientInvoice returns 'Submitted+<InvoiceNo>'.
        approve: {
            route: 'Accounts/ApproveClientInvoice',
            payload: (r, d, { action, note, user, roleId }) => ({
                ClientInvoiceNo: d.ClientInvoiceNo, Action: action, ApprovalNote: note, CreatedBy: user, Roleid: roleId,
            }),
            ok: (status) => typeof status === 'string' && status.startsWith('Submitted'),
        },
        // Legacy UpdateClientInvoiceCreation → spClientInvoiceCreation (Action 'Update'). Dates stay as issued
        // (legacy disabled their pickers); Total = Basic + GST + TCS.
        resubmit: {
            fields: [
                { key: 'RANO', label: 'Running Account No', required: true },
                { key: 'InvoiceDate', label: 'Invoice Date', readOnly: true },
                { key: 'InvoiceMakingDate', label: 'Invoice Making Date', readOnly: true },
                { key: 'BasicValue', label: 'Basic Value', required: true, filter: /^\d*\.?\d{0,2}$/ },
                { key: 'gst', label: '', show: () => false },
                { key: 'Cgstsdcaamt', label: 'CGST Amount', required: true, filter: /^\d*\.?\d{0,2}$/, show: (v) => v.gst === 'CS' },
                { key: 'Sgstsdcaamt', label: 'SGST Amount', required: true, filter: /^\d*\.?\d{0,2}$/, show: (v) => v.gst === 'CS' },
                { key: 'Igstsdcaamt', label: 'IGST Amount', required: true, filter: /^\d*\.?\d{0,2}$/, show: (v) => v.gst === 'I' },
                { key: 'TCSAmount', label: 'TCS Amount', required: true, filter: /^\d*\.?\d{0,2}$/, show: (v) => v.tcs },
                { key: 'Total', label: 'Invoice Value', readOnly: true },
                { key: 'InvoiceRemarks', label: 'Remarks', type: 'textarea', required: true },
            ],
            initial: (r, d) => {
                const s = (x) => (x != null ? String(x) : '');
                const gstOn = d.IsGstApplicable === 'Yes';
                return {
                    RANO: d.RANO || '', InvoiceDate: d.VerificationInvDate || '', InvoiceMakingDate: d.VerificationMakingDate || '',
                    BasicValue: s(d.BasicValue), InvoiceRemarks: d.InvoiceRemarks || '',
                    gst: !gstOn ? '' : d.Statecheck ? 'CS' : 'I', tcs: d.IsTCSApplicable === 'Yes',
                    Cgstsdcaamt: s(d.Cgstsdcaamt), Sgstsdcaamt: s(d.Sgstsdcaamt), Igstsdcaamt: s(d.Igstsdcaamt), TCSAmount: s(d.TCSAmount),
                };
            },
            derive: (v) => {
                const n = (x) => parseFloat(x) || 0;
                const gst = v.gst === 'CS' ? n(v.Cgstsdcaamt) + n(v.Sgstsdcaamt) : v.gst === 'I' ? n(v.Igstsdcaamt) : 0;
                return { ...v, Total: (n(v.BasicValue) + gst + (v.tcs ? n(v.TCSAmount) : 0)).toFixed(2) };
            },
            validate: (v) => {
                const zero = (x) => !(parseFloat(x) > 0);
                const e = [];
                if (zero(v.BasicValue)) e.push('Enter Basic Value');
                if (v.gst === 'CS' && zero(v.Cgstsdcaamt)) e.push('Enter CGST Amount');
                if (v.gst === 'CS' && zero(v.Sgstsdcaamt)) e.push('Enter SGST Amount');
                if (v.gst === 'I' && zero(v.Igstsdcaamt)) e.push('Enter IGST Amount');
                if (v.tcs && zero(v.TCSAmount)) e.push('Enter TCS Amount');
                return e;
            },
            route: 'Accounts/UpdateClientInvoiceCreation',
            payload: (r, d, v, { user, roleId }) => {
                const n = (x) => parseFloat(x) || 0;
                return {
                    PONumber: d.PONumber, RANO: v.RANO.trim(), CCCode: d.CCCode, ClientInvoiceNo: d.ClientInvoiceNo,
                    InvoiceDate: v.InvoiceDate, InvoiceMakingDate: v.InvoiceMakingDate,
                    BasicValue: n(v.BasicValue), Total: n(v.Total), InvoiceRemarks: v.InvoiceRemarks.trim(),
                    Clientcode: d.Clientcode, SubClientcode: d.SubClientcode, InvoiceSubType: d.InvoiceSubType,
                    CreatedBy: user, Roleid: roleId, Statecheck: !!d.Statecheck,
                    IsGstApplicable: d.IsGstApplicable, IsTCSApplicable: d.IsTCSApplicable,
                    Cgstsdcaamt: v.gst === 'CS' ? n(v.Cgstsdcaamt) : 0, Sgstsdcaamt: v.gst === 'CS' ? n(v.Sgstsdcaamt) : 0,
                    Igstsdcaamt: v.gst === 'I' ? n(v.Igstsdcaamt) : 0, TCSAmount: v.tcs ? n(v.TCSAmount) : 0,
                    Action: 'Update',
                };
            },
            ok: ['Submited', 'Submitted'],
        },
    },

    // Legacy /AccountsApproval/VerifyClientManufacturingInvoice (+ Grid / View / ItemCodeGrid)
    ClientManufacturingInvoice: {
        title: 'Client Manufacturing Invoice Verification',
        successLabel: 'Manufacturing Invoice',
        noun: 'invoice',
        icon: Factory,
        searchPlaceholder: 'Search by invoice no, PO no, cost center…',
        queue: { route: 'Accounts/GetVerifyClientManufacturingInvoiceGrid', params: ({ roleId, userId }) => ({ Roleid: roleId, UID: userId }) },
        itemKey: (r) => r.ClientInvoiceNo,
        card: { title: (r) => r.ClientInvoiceNo, subtitle: (r) => `PO ${r.PONumber}`, meta: (r) => `${r.CCCode} · ${r.InvoiceSubType}` },
        searchText: (r) => `${r.ClientInvoiceNo} ${r.PONumber} ${r.CCCode} ${r.InvoiceSubType}`,
        detail: { route: 'Accounts/GetVerificationClientManufacturingTypeInvoicebyNo', params: (r) => ({ InvoiceNo: r.ClientInvoiceNo }) },
        rowAux: [{ name: 'mfgItems', route: 'Accounts/VerifyClientManufacturingItemCodeGrid', params: (r) => ({ InvoiceNo: r.ClientInvoiceNo }) }],
        moid: (r, d) => d.MOID,
        remarksKey: (r, d) => d.ClientInvoiceNo,
        // Legacy strips Return from the status list on this screen.
        showReturn: 'No',
        excludeActions: () => ['return'],
        header: invoiceHeader,
        sections: (r, d) => invoiceSections(d),
        extra: (r, d, { aux, ext, setExt }) => {
            const items = aux.mfgItems;
            if (!items) return null;
            const isMfg = (it) => it.Ofromtable === 'Manufacturing';
            return (
                <CheckedItemsTable
                    title="Item Codes"
                    items={items}
                    ext={ext}
                    setExt={setExt}
                    rowClass={(it) => (isMfg(it) ? 'bg-emerald-50/70 dark:bg-emerald-900/10' : 'bg-amber-50/70 dark:bg-amber-900/10')}
                    columns={[
                        { label: 'Item Type', render: (it) => (isMfg(it) ? 'MANUFACTURE' : 'STOCK') },
                        { label: 'Item Code', render: (it) => it.OItemcode },
                        { label: 'Item Name', render: (it) => it.OItemname, align: 'left' },
                        { label: 'Specification', render: (it) => it.OItemspecification, align: 'left' },
                        { label: 'Unit', render: (it) => it.OItemunit },
                        { label: 'Quantity', render: (it) => it.OItemqty },
                        // Stock rows are components of the manufactured item — legacy hides their rate / amount.
                        { label: 'Selling Price', render: (it) => (isMfg(it) ? fmt(it.OSellingBasicprice) : ''), align: 'right' },
                        { label: 'Amount', render: (it) => (isMfg(it) ? fmt(it.OItemamt) : ''), align: 'right' },
                    ]}
                    footer={(
                        <>
                            <td colSpan={8} className="px-3 py-2 text-right text-xs font-bold text-emerald-600">Total</td>
                            <td className="px-3 py-2 text-right text-xs font-bold text-emerald-600">{fmt(items.reduce((a, it) => a + (Number(it.OItemamt) || 0), 0))}</td>
                        </>
                    )}
                />
            );
        },
        beforeAction: (action, r, d, { aux, ext }) => {
            const items = aux.mfgItems;
            if (!items || !items.length) return ['Invalid Submission — no item codes found for this invoice'];
            return allItemsChecked(items, ext) ? [] : ['Please Verify ItemCodes'];
        },
        approve: {
            route: 'Accounts/ApproveClientManufactureInvoice',
            payload: (r, d, { action, note, user, roleId }) => ({
                ClientInvoiceNo: d.ClientInvoiceNo, Action: action, ApprovalNote: note, CreatedByM: user, Roleid: roleId,
            }),
            ok: (status) => typeof status === 'string' && status.split('+')[0] === 'Submitted',
        },
    },

    // Legacy /AccountsApproval/VerifyClientTradingInvoice (+ Grid / View / ItemCodeGrid / TradeItemCodeSummaryPopup)
    ClientTradingInvoice: {
        title: 'Client Trading Invoice Verification',
        successLabel: 'Trading Invoice',
        noun: 'invoice',
        icon: ShoppingBag,
        searchPlaceholder: 'Search by invoice no, PO no, cost center…',
        queue: { route: 'Accounts/GetVerifyClientTradingInvoiceGrid', params: ({ roleId, userId }) => ({ Roleid: roleId, UID: userId }) },
        itemKey: (r) => r.ClientInvoiceNo,
        card: { title: (r) => r.ClientInvoiceNo, subtitle: (r) => `PO ${r.PONumber}`, meta: (r) => `${r.CCCode} · ${r.InvoiceSubType}` },
        searchText: (r) => `${r.ClientInvoiceNo} ${r.PONumber} ${r.CCCode} ${r.InvoiceSubType}`,
        detail: { route: 'Accounts/GetVerificationClientTradingTypeInvoicebyNo', params: (r) => ({ InvoiceNo: r.ClientInvoiceNo }) },
        rowAux: [{ name: 'tradeItems', route: 'Accounts/GetClientTradingItemCodeGridDetails', params: (r) => ({ InvoiceNo: r.ClientInvoiceNo }) }],
        moid: (r, d) => d.MOID,
        remarksKey: (r, d) => d.ClientInvoiceNo,
        showReturn: 'No',
        excludeActions: () => ['return'],
        header: invoiceHeader,
        sections: (r, d) => invoiceSections(d),
        extra: (r, d, { aux, ext, setExt }) => {
            const items = aux.tradeItems;
            if (!items) return null;
            const has = (k) => items.some((it) => it[k] != null);
            const sum = (k) => fmt(items.reduce((a, it) => a + (Number(it[k]) || 0), 0));
            const cols = [
                { label: 'Item Code', render: (it) => it.OItemcode, link: true },
                { label: 'Item Name', render: (it) => it.OItemname, align: 'left' },
                { label: 'Specification', render: (it) => it.OItemspecification, align: 'left' },
                { label: 'Units', render: (it) => it.OUnits },
                { label: 'Quantity', render: (it) => it.OItemqty },
                { label: 'Selling Rate', render: (it) => fmt(it.OSellingBasicprice), align: 'right' },
                { label: 'Amount', render: (it) => fmt(it.OItemamt), align: 'right', total: 'OItemamt' },
                has('Ocgstper') && { label: 'CGST%', render: (it) => it.Ocgstper },
                has('Ocgstper') && { label: 'CGST Amt', render: (it) => fmt(it.Ocgstamt), align: 'right', total: 'Ocgstamt' },
                has('Osgstper') && { label: 'SGST%', render: (it) => it.Osgstper },
                has('Osgstper') && { label: 'SGST Amt', render: (it) => fmt(it.Osgstamt), align: 'right', total: 'Osgstamt' },
                has('OIgstper') && { label: 'IGST%', render: (it) => it.OIgstper },
                has('OIgstper') && { label: 'IGST Amt', render: (it) => fmt(it.OIgstamt), align: 'right', total: 'OIgstamt' },
                { label: 'HSN Code', render: (it) => it.OHSN },
            ].filter(Boolean);
            return (
                <>
                    <CheckedItemsTable
                        title="Item Codes"
                        items={items}
                        ext={ext}
                        setExt={setExt}
                        columns={cols}
                        onItemClick={(it) => setExt((p) => ({ ...p, popup: it.OItemcode }))}
                        footer={(
                            <>
                                <td />
                                {cols.map((c, i) => (
                                    <td key={c.label} className="px-3 py-2 text-right text-xs font-bold text-emerald-600">
                                        {c.total ? sum(c.total) : (cols[i + 1] && cols[i + 1].label === 'Amount' ? 'Total' : '')}
                                    </td>
                                ))}
                            </>
                        )}
                    />
                    {ext.popup && (
                        <TradeItemSummaryModal
                            itemCode={ext.popup}
                            invoiceNo={d.ClientInvoiceNo}
                            onClose={() => setExt((p) => ({ ...p, popup: null }))}
                        />
                    )}
                </>
            );
        },
        beforeAction: (action, r, d, { aux, ext }) => {
            const items = aux.tradeItems;
            if (!items || !items.length) return ['Invalid Submission — no item codes found for this invoice'];
            return allItemsChecked(items, ext) ? [] : ['Please Verify ItemCodes'];
        },
        approve: {
            route: 'Accounts/ApproveTradeClientInvoice',
            payload: (r, d, { action, note, user, roleId }) => ({
                ClientInvoiceNo: d.ClientInvoiceNo, Action: action, ApprovalNote: note, CreatedBy: user, Roleid: roleId,
            }),
            ok: (status) => typeof status === 'string' && status.split('+')[0] === 'Submitted',
        },
    },

    // Legacy /AccountsApproval/VerifyClientBadDebtRecievable — one queue, three record types by Paytype:
    // 'Invoice Service' (bad-debt receivable), 'Retention' and 'Hold' (bad-debt write-off of those balances).
    ClientBadDebt: {
        title: 'Client Bad Debt Verification',
        successLabel: 'Bad Debt',
        noun: 'bad debt',
        icon: AlertOctagon,
        searchPlaceholder: 'Search by transaction id, invoice, cost center, type…',
        queue: { route: 'Accounts/VerifyClientBadDebtRecievableGrid', params: ({ roleId, userId }) => ({ Roleid: roleId, UID: userId }) },
        itemKey: (r) => r.Refno,
        card: {
            title: (r) => r.InvoiceNo || `Transaction ${r.Refno}`,
            subtitle: (r) => `${r.Paytype} · Transaction ${r.Refno}`,
            meta: (r) => `${r.CCCode || ''}${r.TransactionDate ? ` · ${r.TransactionDate}` : ''}`,
            amount: (r) => money(r.Amount),
        },
        searchText: (r) => `${r.Refno} ${r.InvoiceNo} ${r.CCCode} ${r.Paytype} ${r.TransactionDate} ${r.Amount}`,
        detail: {
            route: (r) => (r.Paytype === 'Retention' ? 'Accounts/VerifyRetentionBadDebtPaymentView'
                : r.Paytype === 'Hold' ? 'Accounts/GetVerificationBDHoldDetailsbyRefno'
                    : 'Accounts/GetVerificationClientBDRecievablebyTransId'),
            params: (r) => (r.Paytype === 'Retention' || r.Paytype === 'Hold' ? { TransRefno: r.Refno } : { TransactionId: r.Refno }),
            // The receivable view returns a list (one row per transaction).
            select: (data) => (Array.isArray(data) ? data[0] || null : data),
        },
        rowAux: [
            { name: 'bdTcs', route: 'Accounts/GetCleintInvoiceTCSData', params: (r) => ({ Invoiceno: r.InvoiceNo }), when: (r) => r.Paytype === 'Invoice Service' },
            { name: 'bdTaxes', route: 'Accounts/GetClientInvTaxDetailsbyInvno', params: (r) => ({ InvNo: r.InvoiceNo }), when: (r) => r.Paytype === 'Invoice Service' },
        ],
        moid: (r, d) => d.MOID,
        remarksKey: (r) => r.Refno,
        showReturn: 'Yes',
        // Legacy drops Return on the receivable screen only; Retention / Hold keep it.
        excludeActions: (r) => (r.Paytype === 'Invoice Service' ? ['return'] : []),
        isReturned: (r) => String(r.Status) === '0',
        returnedNotice: 'Correct and resubmit it from the Client Bad Debt screen — there is nothing to verify until it is resubmitted.',
        header: {
            title: (r, d) => (r.Paytype === 'Invoice Service' ? d.InvoiceNo : d.ClientName) || `Transaction ${r.Refno}`,
            subtitle: (r, d) => money(r.Paytype === 'Invoice Service' ? d.Amount : d.PaymentAmount),
            chips: (r) => [r.Paytype, `Transaction ${r.Refno}`, r.CCCode],
        },
        sections: (r, d) => (r.Paytype === 'Invoice Service'
            ? [{
                title: 'Invoice',
                fields: [
                    ['PO Number', d.PONo], ['Invoice No', d.InvoiceNo], ['Invoice Date', d.InvoiceDate],
                    ['Invoice Making Date', d.InvoiceMakingDate], ['Cost Center', d.CCCode], ['Client', d.Client],
                    ['Sub Client', d.SubClient], ['Invoice Category', d.InvoiceCategory], ['RA No', d.RANo],
                    ['Basic Value', money(d.Basic_Value)],
                ],
            }, {
                title: 'Bad Debt',
                fields: [['Transaction Amount', money(d.Amount)], ['Transaction Date', d.TransactionDate]],
            }]
            : [{
                fields: [
                    ['Client', d.ClientName], ['Sub Client', d.SubClientName],
                    ['Transaction Date', r.Paytype === 'Hold' ? d.ReturnPayDate : d.PaymentDate],
                    ['Transaction Amount', money(d.PaymentAmount)],
                ],
            }]),
        extra: (r, d, { aux }) => {
            const doc = d.Docpath && (
                <a href={buildBadDebtUrl(d.Docpath)} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold shadow hover:shadow-lg">
                    <FileText className="w-4 h-4" /> View Document
                </a>
            );
            if (r.Paytype !== 'Invoice Service') {
                const list = (r.Paytype === 'Hold' ? d.HoldInvoiceList : d.RetInvDetailsList) || [];
                const bal = (it) => (r.Paytype === 'Hold' ? it.HoldBalance : it.RetBalance);
                return (
                    <>
                        <Block title="Invoices">
                            <SimpleTable
                                heads={['Invoice No', 'PO Number', 'Invoice Date', 'Amount']}
                                rows={list.map((it) => [it.ClientInvoiceNo, it.PONumber, it.InvoiceDate, fmt(bal(it))])}
                                alignRight={[3]}
                            />
                        </Block>
                        {doc}
                    </>
                );
            }
            const tcs = aux.bdTcs;
            const taxes = aux.bdTaxes || [];
            const taxTotal = taxes.reduce((a, t) => a + (Number(t.TaxValue) || 0), 0);
            return (
                <>
                    {tcs && tcs.IsTCSApplicable === 'Yes' && (
                        <Block title="TCS">
                            <SimpleTable heads={['Account Head', 'Sub Account Head', 'Amount']} rows={[[tcs.TCSDCA, tcs.TCSSDCA, fmt(tcs.TCSAmount)]]} alignRight={[2]} />
                        </Block>
                    )}
                    {taxes.length > 0 && (
                        <Block title="Taxes">
                            <SimpleTable
                                heads={['Tax Number', 'Cost Center', 'Account Head', 'Sub Account Head', 'Tax Value', 'Types of Tax']}
                                rows={taxes.map((t) => [t.TaxNo, t.CCCode, t.DCACode, t.SubDCACode, fmt(t.TaxValue), t.TypesOfTaxName])}
                                alignRight={[4]}
                                foot={['', '', '', 'Tax Total', fmt(taxTotal), '']}
                            />
                        </Block>
                    )}
                    {doc}
                </>
            );
        },
        approve: {
            route: (r) => (r.Paytype === 'Retention' ? 'Accounts/ApproveBDRetentionPayment'
                : r.Paytype === 'Hold' ? 'Accounts/ApproveBadDebtHoldPayment'
                    : 'Accounts/ApproveClientBadDebtRecievable'),
            payload: (r, d, { action, note, user, roleId }) => {
                if (r.Paytype === 'Invoice Service') {
                    return {
                        Refno: r.Refno, Action: action, ApprovalNote: note, InvoiceDate: d.InvoiceDate, InvoiceNo: d.InvoiceNo,
                        Amount: d.Amount, Createdby: user, Roleid: roleId,
                    };
                }
                // Legacy posts the invoice list as trailing-comma CSVs.
                const hold = r.Paytype === 'Hold';
                const list = (hold ? d.HoldInvoiceList : d.RetInvDetailsList) || [];
                const nos = list.map((it) => `${it.ClientInvoiceNo},`).join('');
                const amts = list.map((it) => `${hold ? it.HoldBalance : it.RetBalance},`).join('');
                return {
                    BankTransactionRefNo: r.Refno, InvoiceNos: nos, [hold ? 'PaidHoldAmounts' : 'PaidRetAmounts']: amts,
                    Action: action, ApprovalNote: note, PaymentAmount: d.PaymentAmount, Createdby: user, Roleid: roleId,
                };
            },
            ok: ['Submitted'],
        },
    },

    // Legacy /AccountsApproval/VerifyDCA (+ VerifyDcaGrid / VerifyDcaView)
    DCA: {
        title: 'Account Head Verification',
        successLabel: 'Account Head',
        noun: 'account head',
        icon: BookOpen,
        searchPlaceholder: 'Search by account head code, name, type…',
        queue: { route: 'Accounts/GetVerificationDCA', params: ({ roleId }) => ({ Roleid: roleId }) },
        itemKey: (r) => r.DCAID,
        card: { title: (r) => r.DCAName, subtitle: (r) => r.DCACode, meta: (r) => `${r.DCATypeName || ''}${r.DCAStatus ? ` · ${r.DCAStatus}` : ''}` },
        searchText: (r) => `${r.DCACode} ${r.DCAName} ${r.DCATypeName} ${r.DCAStatus}`,
        detail: { route: 'Accounts/GetDcabyId', params: (r) => ({ DcaId: r.DCAID, Dcatypeid: r.DCATypeID }) },
        moid: (r) => r.MOID,
        remarksKey: (r) => r.DCAID,
        showReturn: 'Yes',
        excludeActions: (r) => (r.DCAStatus === 'Closed' ? ['return', 'reject'] : []),
        isReturned: (r) => String(r.Status) === '0',
        // Legacy re-opens the full Tax / General account-head editor (CC types, taxes, payment types).
        returnedNotice: 'Correct and resubmit it from the Account Head screen — there is nothing to verify until it is resubmitted.',
        header: {
            title: (r, d) => (d.BasicDCAInfo || {}).DCAName || r.DCAName,
            subtitle: (r, d) => (d.BasicDCAInfo || {}).DCACode || r.DCACode,
            chips: (r, d) => [(d.BasicDCAInfo || {}).DCATypeName || r.DCATypeName, r.DCAStatus && `Status: ${r.DCAStatus}`],
        },
        sections: (r, d) => {
            const b = d.BasicDCAInfo || {};
            return [{
                fields: [
                    ['Account Head Code', b.DCACode], ['Account Head Name', b.DCAName], ['Type', b.DCATypeName],
                    b.ITCodeName && ['IT Code Name', b.ITCodeName],
                    ['Account Head Status', b.DCAStatus],
                    String(b.DCATypeID) === '2' && ['Type of Tax', b.TypeOfTaxName],
                    String(b.DCATypeID) === '2' && ['Nature of Tax', b.NatureOfTaxName],
                    ['Cost Center Types', (d.CCTypeNames || []).join(', '), true],
                ],
            }];
        },
        approve: {
            route: 'Accounts/ApproveDCA',
            payload: (r, d, { action, note, user, roleId }) => ({
                DCATypeID: r.DCATypeID, DCACodeID: r.DCAID, DCACode: r.DCACode, Action: action, RemarksNote: note,
                Createdby: user, RoleId: roleId, DCAStatus: r.DCAStatus,
            }),
            ok: ['Submited', 'Submitted'],
        },
    },

    // Legacy /AccountsApproval/VerifySubDCA (+ VerifySubDcaGrid / VerifySubDcaView) — the grid row is the record.
    SubDCA: {
        title: 'Sub Account Head Verification',
        successLabel: 'Sub Account Head',
        noun: 'sub account head',
        icon: BookMarked,
        searchPlaceholder: 'Search by sub account head, account head, IT code…',
        queue: { route: 'Accounts/GetVerificationSubDCA', params: ({ roleId }) => ({ Roleid: roleId }) },
        itemKey: (r) => r.SubDCACodeID,
        card: { title: (r) => r.SubDCAName, subtitle: (r) => r.SubDCACode, meta: (r) => `${r.DCAName}${r.SubDcaStatus ? ` · ${r.SubDcaStatus}` : ''}` },
        searchText: (r) => `${r.SubDCACode} ${r.SubDCAName} ${r.DCAName} ${r.ITCodeDescription} ${r.SubDcaStatus}`,
        aux: [{ name: 'itCodes', route: 'Accounts/GetITCodes' }],
        moid: (r) => r.MOID,
        remarksKey: (r) => r.SubDCACodeID,
        showReturn: 'Yes',
        excludeActions: (r) => (r.SubDcaStatus === 'Closed' ? ['return', 'reject'] : []),
        isReturned: (r) => String(r.Status) === '0',
        header: {
            title: (r) => r.SubDCAName,
            subtitle: (r) => r.SubDCACode,
            chips: (r) => [`Account Head ${r.DCAName}`, r.SubDcaStatus && `Status: ${r.SubDcaStatus}`],
        },
        sections: (r) => [{
            fields: [
                ['Account Head', r.DCAName], ['Sub Account Head Code', r.SubDCACode], ['Sub Account Head Name', r.SubDCAName],
                ['IT Code', r.ITCodeDescription], ['Sub Account Head Status', r.SubDcaStatus],
            ],
        }],
        approve: {
            route: 'Accounts/ApproveSubDCA',
            payload: (r, d, { action, note, user, roleId }) => ({
                DCACodeID: r.DCACodeID, SubDCACodeID: r.SubDCACodeID, Action: action, RemarksNote: note,
                Createdby: user, ModifiedBy: user, RoleId: roleId, SubDcaStatus: r.SubDcaStatus,
            }),
            ok: ['Submited', 'Submitted'],
        },
        // Legacy UpdateSubDCA → SpUpdateSubDCA (CheckUpdationType 'ReturnUpdate').
        resubmit: {
            fields: [
                { key: 'SubDCACode', label: 'Sub Account Head Code', readOnly: true },
                { key: 'SubDCAName', label: 'Sub Account Head Name', required: true },
                {
                    key: 'ITCodeID', label: 'IT Code', type: 'select', required: true,
                    options: (aux) => (aux.itCodes || []).map((c) => ({ value: String(c.ITCodeID), label: c.ITCodename })),
                },
            ],
            initial: (r) => ({ SubDCACode: r.SubDCACode || '', SubDCAName: r.SubDCAName || '', ITCodeID: r.ITCodeID != null ? String(r.ITCodeID) : '' }),
            route: 'Accounts/UpdateSubDCA',
            payload: (r, d, v, { user, roleId }) => ({
                SubDCACodeID: r.SubDCACodeID, DCACodeID: r.DCACodeID, SubDCACode: r.SubDCACode, SubDCAName: v.SubDCAName.trim(),
                ITCodeID: parseInt(v.ITCodeID, 10), RoleId: roleId, Createdby: user, ModifiedBy: user, CheckUpdationType: 'ReturnUpdate',
            }),
            ok: ['Updated'],
        },
    },

    // Legacy /AccountsApproval/VerifyMasterGroups (+ VerifyMasterGroupsGrid / VerifyMasterGroupView)
    MasterGroups: {
        title: 'Master Group Verification',
        successLabel: 'Master Group',
        noun: 'group',
        icon: FolderTree,
        searchPlaceholder: 'Search by group name, nature of group…',
        queue: { route: 'Accounts/GetVerificationMasterGroups', params: ({ roleId }) => ({ Roleid: roleId }) },
        itemKey: (r) => r.GroupId,
        card: { title: (r) => r.GroupName, subtitle: (r) => r.NatureGroupName, meta: (r) => `Gross Profit Calculation: ${r.GrossProfitCalc || '-'}` },
        searchText: (r) => `${r.GroupName} ${r.NatureGroupName} ${r.GroupId}`,
        detail: { route: 'Accounts/GetVerificationMasterGroupsbyid', params: (r) => ({ GroupId: r.GroupId }) },
        moid: (r, d) => d.MOID,
        remarksKey: (r) => r.GroupId,
        showReturn: 'Yes',
        isReturned: (r) => String(r.Status) === '0',
        header: { title: (r, d) => d.GroupName, subtitle: (r, d) => d.NatureGroupName, chips: () => ['Master Group'] },
        sections: (r, d) => [{
            fields: [['Group Name', d.GroupName], ['Nature of Group', d.NatureGroupName], ['Gross Profit Calculation', d.GrossProfitCalc]],
        }],
        approve: {
            route: 'Accounts/ApproveMasterGroup',
            payload: (r, d, { action, note, user, roleId }) => ({
                GroupId: d.GroupId, GroupName: d.GroupName, Action: action, RoleId: roleId, ApprovalNote: note, Createdby: user,
            }),
            ok: ['Submitted'],
        },
        // Legacy UpdateApprMasterGroup → spAddUpdateGroups (Action 'Update').
        resubmit: {
            fields: [
                { key: 'NatureGroupName', label: 'Nature of Group', readOnly: true },
                { key: 'GroupName', label: 'Group Name', required: true },
            ],
            initial: (r, d) => ({ NatureGroupName: d.NatureGroupName || '', GroupName: d.GroupName || '' }),
            route: 'Accounts/UpdateMasterGroup',
            payload: (r, d, v, { user, roleId }) => ({
                GroupNameList: v.GroupName.trim(), GroupIdList: d.GroupId, Createdby: user, Action: 'Update', RoleId: roleId,
            }),
            ok: ['Submitted'],
        },
    },

    // Legacy /AccountsApproval/VerifySubGroups (+ VerifySubGroupsGrid / VerifySubGroupsView)
    SubGroups: {
        title: 'Sub Group Verification',
        successLabel: 'Sub Group',
        noun: 'sub group',
        icon: FolderOpen,
        searchPlaceholder: 'Search by sub group, master group, nature…',
        queue: { route: 'Accounts/GetVerificationSubGroupDetials', params: ({ roleId }) => ({ Roleid: roleId }) },
        itemKey: (r) => r.Id,
        card: { title: (r) => r.Name, subtitle: (r) => r.MasterGroupName, meta: (r) => r.NatureGroupName },
        searchText: (r) => `${r.Name} ${r.MasterGroupName} ${r.NatureGroupName}`,
        detail: { route: 'Accounts/GetVerificationSubGroupbyId', params: (r) => ({ Id: r.Id }) },
        moid: (r, d) => d.MOID,
        remarksKey: (r) => r.Id,
        showReturn: 'Yes',
        isReturned: (r) => String(r.Status) === '0',
        header: { title: (r, d) => d.Name, subtitle: (r, d) => d.MasterGroupName, chips: (r, d) => [d.NatureGroupName, 'Sub Group'] },
        sections: (r, d) => [{
            fields: [['Sub Group Name', d.Name], ['Master Group', d.MasterGroupName], ['Nature of Group', d.NatureGroupName]],
        }],
        approve: {
            route: 'Accounts/ApproveSubGroup',
            payload: (r, d, { action, note, user, roleId }) => ({
                Id: d.Id, Name: d.Name, Action: action, RoleId: roleId, ApprovalNote: note, Createdby: user, Grouptype: 'Sub',
            }),
            ok: ['Submitted'],
        },
        // Legacy UpdateApprSubGroup → spInsertUpdateSubGroups (Action 'Update').
        resubmit: {
            fields: [
                { key: 'MasterGroupName', label: 'Master Group', readOnly: true },
                { key: 'NatureGroupName', label: 'Nature of Group', readOnly: true },
                { key: 'Name', label: 'Sub Group Name', required: true },
            ],
            initial: (r, d) => ({ MasterGroupName: d.MasterGroupName || '', NatureGroupName: d.NatureGroupName || '', Name: d.Name || '' }),
            route: 'Accounts/UpdateSubGroup',
            payload: (r, d, v, { user, roleId }) => ({
                SubGroupNameList: v.Name.trim(), MasterGroupId: 0, Createdby: user, Id: d.Id, RoleId: roleId, Action: 'Update',
            }),
            ok: ['Submitted'],
        },
    },

    // Legacy /AccountsApproval/VerifyChildGroups (+ VerifyChildGroupsGrid / VerifyChildGroupView)
    ChildGroups: {
        title: 'Child Group Verification',
        successLabel: 'Child Group',
        noun: 'child group',
        icon: Folders,
        searchPlaceholder: 'Search by child group, parent sub group, master group…',
        queue: { route: 'Accounts/GetVerificationChildGroups', params: ({ roleId }) => ({ Roleid: roleId }) },
        itemKey: (r) => r.Id,
        card: { title: (r) => r.Name, subtitle: (r) => `${r.MasterGroupName} › ${r.ParentSubGroup}`, meta: (r) => r.NatureGroupName },
        searchText: (r) => `${r.Name} ${r.MasterGroupName} ${r.ParentSubGroup} ${r.NatureGroupName}`,
        detail: { route: 'Accounts/GetVerificationChildGroupbyId', params: (r) => ({ Id: r.Id }) },
        moid: (r, d) => d.MOID,
        remarksKey: (r) => r.Id,
        showReturn: 'Yes',
        isReturned: (r) => String(r.Status) === '0',
        header: { title: (r, d) => d.Name, subtitle: (r, d) => `${d.MasterGroupName} › ${d.ParentSubGroup}`, chips: (r, d) => [d.NatureGroupName, 'Child Group'] },
        sections: (r, d) => [{
            fields: [
                ['Child Group Name', d.Name], ['Master Group', d.MasterGroupName], ['Parent Sub Group', d.ParentSubGroup],
                ['Nature of Group', d.NatureGroupName],
            ],
        }],
        approve: {
            route: 'Accounts/ApproveSubGroup',
            payload: (r, d, { action, note, user, roleId }) => ({
                Id: d.Id, Name: d.Name, Action: action, RoleId: roleId, ApprovalNote: note, Createdby: user, Grouptype: 'Child',
            }),
            ok: ['Submitted'],
        },
        // Legacy UpdateChildGroup → spInsertUpdateChildGroups (Action 'Update'); the API route is HttpPost.
        resubmit: {
            fields: [
                { key: 'MasterGroupName', label: 'Master Group', readOnly: true },
                { key: 'ParentSubGroup', label: 'Parent Sub Group', readOnly: true },
                { key: 'NatureGroupName', label: 'Nature of Group', readOnly: true },
                { key: 'Name', label: 'Child Group Name', required: true },
            ],
            initial: (r, d) => ({
                MasterGroupName: d.MasterGroupName || '', ParentSubGroup: d.ParentSubGroup || '', NatureGroupName: d.NatureGroupName || '', Name: d.Name || '',
            }),
            method: 'post',
            route: 'Accounts/UpdateChildGroup',
            payload: (r, d, v, { user, roleId }) => ({
                ChildGroupNameList: v.Name.trim(), ChildGroupid: d.Id, Createdby: user, RoleId: roleId, Action: 'Update',
            }),
            ok: ['Submitted'],
        },
    },

    // Legacy /AccountsApproval/VerifyLedger (+ VerifyLedgerGrid / VerifyLedgerView)
    Ledger: {
        title: 'Ledger Verification',
        successLabel: 'Ledger',
        noun: 'ledger',
        icon: Library,
        searchPlaceholder: 'Search by ledger, group, account head…',
        queue: { route: 'Accounts/GetVerificationLedgerDetails', params: ({ roleId }) => ({ Roleid: roleId }) },
        itemKey: (r) => r.Id,
        card: { title: (r) => r.Name, subtitle: (r) => r.GroupName, meta: (r) => r.AccHead },
        searchText: (r) => `${r.Name} ${r.GroupName} ${r.AccHead}`,
        detail: { route: 'Accounts/GetVerificationLedgerbyId', params: (r) => ({ LedgerId: r.Id }) },
        moid: (r, d) => d.MOID,
        remarksKey: (r) => r.Id,
        // Legacy drops Return from this screen's status list (the returned-row editor still exists).
        showReturn: 'No',
        excludeActions: () => ['return'],
        isReturned: (r) => String(r.LStatus) === '0',
        header: {
            title: (r, d) => d.Name,
            subtitle: (r, d) => d.GroupName,
            chips: (r, d) => [d.LedgerTypeName, d.GroupNature],
        },
        sections: (r, d) => [{
            fields: [
                ['Ledger Name', d.Name], ['Group Name', d.GroupName],
                d.SubGroup && d.SubGroup !== '0' && ['Sub Group', d.SubGroup],
                ['Nature of Group', d.GroupNature], ['Account Head', d.AccHead], ['Balance', money(d.Balance)],
                ['Date', displayDate(d.Date)], ['Value Type', d.ValueType], ['Ledger Type', d.LedgerTypeName],
                Number(d.LedgerTypeId) === 8 && ['IT Code', d.TaxItName],
            ],
        }],
        approve: {
            route: 'Accounts/ApproveLedger',
            payload: (r, d, { action, note, user, roleId }) => ({
                LedgerId: d.Id, LedgerName: d.Name, Action: action, RoleId: roleId, ApprovalNote: note, Createdby: user,
            }),
            ok: ['Submitted'],
        },
        // Legacy UpdateApprLedger → spInsertUpdateLedger (Action 'Update').
        resubmit: {
            fields: [
                { key: 'GroupName', label: 'Group', readOnly: true },
                { key: 'SubGroup', label: 'Sub Group', readOnly: true, show: (v) => !!v.SubGroup && v.SubGroup !== '0' },
                { key: 'AccHead', label: 'Account Head', readOnly: true },
                { key: 'LedgerName', label: 'Ledger Name', required: true },
                { key: 'OpeningBalance', label: 'Opening Balance', required: true, filter: /^-?\d*\.?\d{0,2}$/ },
                { key: 'BalanceAsOnDate', label: 'Balance As On Date', type: 'date' },
                {
                    key: 'LedgerValueType', label: 'Ledger Value Type', type: 'select', required: true,
                    options: [{ value: 'Credit', label: 'Credit' }, { value: 'Debit', label: 'Debit' }],
                },
            ],
            initial: (r, d) => ({
                GroupName: d.GroupName || '', SubGroup: d.SubGroup || '', AccHead: d.AccHead || '',
                LedgerName: d.Name || '', OpeningBalance: d.Balance != null ? String(d.Balance) : '',
                BalanceAsOnDate: isoDate(d.Date), LedgerValueType: d.ValueType || '',
            }),
            validate: (v) => (v.OpeningBalance !== '' && Number(v.OpeningBalance) !== 0 && !v.BalanceAsOnDate ? ['Select Balance As on Date'] : []),
            route: 'Accounts/EditLedger',
            payload: (r, d, v, { user, roleId }) => ({
                LedgerValueType: v.LedgerValueType, LedgerName: v.LedgerName.trim(), OpeningBalance: parseFloat(v.OpeningBalance) || 0,
                ...(v.BalanceAsOnDate ? { BalanceAsOnDate: v.BalanceAsOnDate } : {}), Createdby: user, LedgerId: d.Id, RoleId: roleId, Action: 'Update',
            }),
            ok: ['Submitted'],
        },
    },

    // Legacy /AccountsApproval/VerifyTaxGeneral (+ VerifyTaxGeneralGrid / VerifyTaxGeneralView / VerifyTaxGSTView):
    // one queue, TaxType 'GST' or 'General' picks the view and routes.
    TaxGeneral: {
        title: 'Tax Verification',
        successLabel: 'Tax',
        noun: 'tax',
        icon: Percent,
        searchPlaceholder: 'Search by tax number, name, type…',
        queue: { route: 'Accounts/GetVerificationGeneralTaxes', params: ({ roleId }) => ({ Roleid: roleId }) },
        itemKey: (r) => r.TaxId,
        card: { title: (r) => r.TaxName, subtitle: (r) => r.TaxNo, meta: (r) => `${r.TaxType} · ${r.TaxFor}` },
        searchText: (r) => `${r.TaxNo} ${r.TaxName} ${r.TaxFor} ${r.TaxType}`,
        detail: {
            route: (r) => (r.TaxType === 'GST' ? 'Accounts/GetVerificationGstTaxebyid' : 'Accounts/GetVerificationGeneralTaxesbyid'),
            params: (r) => ({ Taxid: r.TaxId }),
        },
        moid: (r, d) => d.MOID,
        remarksKey: (r) => r.TaxId,
        showReturn: 'Yes',
        isReturned: (r) => String(r.Status) === '0',
        header: {
            title: (r, d) => d.TaxName,
            subtitle: (r, d) => d.TaxNo,
            chips: (r, d) => [r.TaxType, d.State],
        },
        sections: (r, d) => {
            if (r.TaxType !== 'GST') {
                return [{
                    fields: [
                        ['Tax Number', d.TaxNo], ['Tax Name', d.TaxName], ['Tax For', d.TaxFor], ['Sub Account Head', d.SubDCA],
                        ['Applicable From', displayDate(d.ApplicableFrom)], d.Remarks && ['Remarks', d.Remarks, true],
                    ],
                }];
            }
            const l = d.Ledger || {};
            return [
                {
                    title: 'GST',
                    fields: [
                        ['GST Number', d.TaxNo], ['Tax Name', d.TaxName], ['GST For', d.TaxFor], ['State', d.State],
                        ['Applicable From', d.AppFrom], ['Wallet Name', d.WalletName], ['Address', d.Address, true],
                    ],
                },
                {
                    title: 'Ledger',
                    fields: [
                        ['IT Name', l.TaxItName], ['Nature of Group', l.GroupNature], ['Group Name', l.GroupName], ['Sub Group', l.SubGroup],
                        ['Ledger Name', l.Name], ['Opening Balance', money(l.Balance)], ['Balance As On Date', displayDate(l.Date)],
                        ['Ledger Value Type', l.ValueType],
                    ],
                },
                d.Remarks && { fields: [['Remarks', d.Remarks, true]] },
            ].filter(Boolean);
        },
        approve: {
            route: (r) => (r.TaxType === 'GST' ? 'Accounts/ApproveTaxGST' : 'Accounts/ApproveTaxGeneral'),
            payload: (r, d, { action, note, user, roleId }) => ({ TaxId: d.TaxId, RoleId: roleId, Action: action, ApprovalNote: note, CreatedBy: user }),
            ok: ['Submited', 'Submitted'],
        },
        // Legacy UpdateApprGeneralTaxes → spInsertUpdateGeneralTax / UpdateApprGstTaxes → spInsertUpdateGstTax (Action 'Update').
        resubmit: {
            fields: [
                { key: 'TaxNo', label: 'Tax Number', readOnly: true },
                { key: 'AppFromText', label: 'Applicable From', readOnly: true },
                { key: 'TaxFor', label: 'Account Head', readOnly: true },
                { key: 'SubDCA', label: 'Sub Account Head', readOnly: true, show: (v) => !v.gst },
                { key: 'State', label: 'State', readOnly: true, show: (v) => v.gst },
                { key: 'TaxName', label: 'Tax Name', required: true },
                { key: 'LedgerName', label: 'Ledger Name', required: true, show: (v) => v.gst },
                { key: 'OpeningBalance', label: 'Opening Balance', required: true, filter: /^-?\d*\.?\d{0,2}$/, show: (v) => v.gst },
                { key: 'BalanceAsOnDate', label: 'Balance As On Date', type: 'date', required: true, show: (v) => v.gst },
                { key: 'Remarks', label: 'Remarks', type: 'textarea', required: true },
            ],
            initial: (r, d) => {
                const l = d.Ledger || {};
                const gst = r.TaxType === 'GST';
                return {
                    gst, TaxNo: d.TaxNo || '', TaxFor: d.TaxFor || '', SubDCA: d.SubDCA || '', State: d.State || '',
                    AppFromText: gst ? d.AppFrom || '' : displayDate(d.ApplicableFrom) || '',
                    TaxName: d.TaxName || '', Remarks: d.Remarks || '',
                    LedgerName: l.Name || '', OpeningBalance: l.Balance != null ? String(l.Balance) : '', BalanceAsOnDate: isoDate(l.Date),
                };
            },
            route: (r) => (r.TaxType === 'GST' ? 'Accounts/UpdateGstTax' : 'Accounts/UpdateGeneralTax'),
            payload: (r, d, v, { user, roleId }) => {
                const base = {
                    TaxName: v.TaxName.trim(), TaxNo: d.TaxNo, TaxType: r.TaxType, TaxFor: d.TaxFor, Remarks: v.Remarks.trim(),
                    CreatedBy: user, Action: 'Update', TaxId: d.TaxId, DcaId: 0, RoleId: roleId,
                };
                if (!v.gst) return { ...base, SubDCA: d.SubDCA, ApplicableFrom: d.ApplicableFrom };
                return {
                    ...base, ApplicableFrom: isoDate(d.AppFrom) || undefined, WalletName: d.WalletName, Address: d.Address,
                    LedgerName: v.LedgerName.trim(), OpeningBalance: parseFloat(v.OpeningBalance) || 0, BalanceAsOnDate: displayDate(v.BalanceAsOnDate),
                };
            },
            ok: ['Submitted', 'Submited'],
        },
    },

    // Legacy /AccountsApproval/ApproveIT (+ ViewApproveITCodesGridView / ItApproval)
    ITCode: {
        title: 'IT Code Verification',
        successLabel: 'IT Code',
        noun: 'IT code',
        icon: Hash,
        searchPlaceholder: 'Search by IT code, name, nature of group…',
        queue: { route: 'Accounts/GetApprovalItCodes', params: ({ roleId }) => ({ Roleid: roleId }) },
        itemKey: (r) => r.Itid,
        card: { title: (r) => r.ItName, subtitle: (r) => r.ItCode, meta: (r) => `${r.NatureGroupName}${r.ITStatus ? ` · ${r.ITStatus}` : ''}` },
        searchText: (r) => `${r.ItCode} ${r.ItName} ${r.NatureGroupName} ${r.ITStatus}`,
        detail: { route: 'Accounts/GetApprovalItCodeDetailsbyId', params: (r) => ({ id: r.Itid }) },
        moid: (r, d) => d.MOID,
        remarksKey: (r) => r.Itid,
        showReturn: 'Yes',
        excludeActions: (r) => (r.ITStatus === 'Closed' ? ['return', 'reject'] : []),
        isReturned: (r) => String(r.Status) === '0',
        header: {
            title: (r, d) => d.ItName,
            subtitle: (r, d) => d.ItCode,
            chips: (r, d) => [d.NatureGroupName, d.ITStatus && `Status: ${d.ITStatus}`],
        },
        sections: (r, d) => {
            const l = d.Ledger || {};
            return [
                {
                    fields: [
                        ['IT Code', d.ItCode], ['IT Name', d.ItName], ['Nature of Group', d.NatureGroupName], ['IT Status', d.ITStatus],
                        ['Group Name', d.GroupName], d.SubGroupId && d.SubGroupId !== '0' && ['Sub Group Name', d.SubGroupName],
                    ],
                },
                d.Ledger && {
                    title: 'Ledger',
                    fields: [
                        ['Ledger Name', l.Name], ['Opening Balance', money(l.Balance)], ['Balance As On Date', displayDate(l.Date)],
                        ['Ledger Value Type', l.ValueType],
                    ],
                },
            ].filter(Boolean);
        },
        approve: {
            route: 'Accounts/ApproveITCode',
            payload: (r, d, { action, note, user, roleId }) => ({
                RoleId: roleId, CreatedBy: user, Itid: d.Itid, Action: action, RemarksNote: note, ITStatus: d.ITStatus,
            }),
            ok: ['Submitted'],
        },
        // Legacy UpdateApprIT → spInsertUpdateITCode (Action 'Update', CheckUpdationType 'ReturnUpdate').
        resubmit: {
            fields: [
                { key: 'ItCode', label: 'IT Code', readOnly: true },
                { key: 'NatureGroupName', label: 'Nature of Group', readOnly: true },
                { key: 'ItName', label: 'IT Name', required: true },
                { key: 'OpeningBalance', label: 'Opening Balance', filter: /^-?\d*\.?\d{0,2}$/ },
                { key: 'BalanceAsOnDate', label: 'Balance As On Date', type: 'date' },
                {
                    key: 'LedgerValueType', label: 'Ledger Value Type', type: 'select',
                    options: [{ value: 'Credit', label: 'Credit' }, { value: 'Debit', label: 'Debit' }],
                },
            ],
            initial: (r, d) => {
                const l = d.Ledger || {};
                return {
                    ItCode: d.ItCode || '', NatureGroupName: d.NatureGroupName || '', ItName: d.ItName || '',
                    OpeningBalance: l.Balance != null ? String(l.Balance) : '', BalanceAsOnDate: isoDate(l.Date), LedgerValueType: l.ValueType || '',
                };
            },
            route: 'Accounts/EditITCode',
            payload: (r, d, v, { user, roleId }) => ({
                Itid: d.Itid, ItName: v.ItName.trim(), Action: 'Update', CheckUpdationType: 'ReturnUpdate',
                GroupId: d.GroupId, SubGroupId: d.SubGroupId, LedgerValueType: v.LedgerValueType,
                OpeningBalance: parseFloat(v.OpeningBalance) || 0, BalanceAsOnDate: displayDate(v.BalanceAsOnDate) || '',
                CreatedBy: user, RoleId: roleId,
            }),
            ok: ['Submitted'],
        },
    },

    // Legacy /AccountsApproval/CostCenterBudgetApproval (+ ViewApprovalCCBudgetDetailsGrid / ApproveCCBudget)
    CCBudget: {
        title: 'Cost Center Budget Verification',
        successLabel: 'Budget',
        noun: 'budget',
        icon: Wallet,
        searchPlaceholder: 'Search by cost center, name, type, year…',
        queue: { route: 'Accounts/GetVerificationCCBudget', params: ({ roleId, userId }) => ({ Roleid: roleId, UID: userId }) },
        itemKey: (r) => r.Budgetid,
        card: {
            title: (r) => `${r.CostCenter}${r.CC_Name ? ` - ${r.CC_Name}` : ''}`,
            subtitle: (r) => [r.CCType, r.SubType].filter(Boolean).join(' · '),
            meta: (r) => [r.BudgetCreationDate, isNonPerformingCC(r.CCType) && r.Year].filter(Boolean).join(' · '),
            amount: (r) => money(r.Amount),
        },
        searchText: (r) => `${r.CostCenter} ${r.CC_Name} ${r.CCType} ${r.SubType} ${r.Year} ${r.Amount}`,
        detail: { route: 'Accounts/GetCCBudgetbyId', params: (r) => ({ BudgetId: r.Budgetid }) },
        // Contract scope / work order PDFs uploaded on the cost center (only for users set up in Budget View Attachment Config).
        rowAux: [
            { name: 'ccDocs', route: 'Accounts/Getccuploadocsexists', params: (r, d, { userId }) => ({ CCCode: r.CostCenter, UID: userId }) },
        ],
        moid: (r, d) => d.MOID,
        remarksKey: (r, d) => d.Refno,
        // Legacy fills the status dropdown straight from GetApprovalstatus, Return included.
        showReturn: 'Yes',
        isReturned: (r) => String(r.Status) === '0',
        header: {
            title: (r, d) => `${d.CostCenter}${d.CC_Name ? ` - ${d.CC_Name}` : ''}`,
            subtitle: (r, d) => money(d.Amount),
            chips: (r, d) => [d.CCType, d.SubType, isNonPerformingCC(d.CCType) && d.Year],
        },
        sections: (r, d) => [{
            fields: [
                ['Cost Center', d.CostCenter], ['Cost Center Name', d.CC_Name], ['Cost Center Type', d.CCType],
                ['Sub Type', d.SubType], ['Budget Value', money(d.Amount)], ['Created Date', d.ReturnCreatedate || d.BudgetCreationDate],
                isNonPerformingCC(d.CCType) && ['Year', d.Year],
                ['Remarks', d.Remarks, true],
            ],
        }],
        extra: (r, d, { aux }) => (
            <CCDocsBlock docs={aux.ccDocs} execPath={d.CCType === 'Performing' ? d.Approvedbudgetexecution || null : null} />
        ),
        approve: {
            route: 'Accounts/ApproveCostCenterBudget',
            payload: (r, d, { action, note, user, roleId, userId }) => ({
                CostCenter: d.CostCenter, Action: action, ApprovalNote: note, Year: d.Year, RoleId: roleId, Createdby: user, UID: userId,
            }),
            ok: ['Submited', 'Submitted'],
        },
        // Legacy UpdateCCBudget → spUpdateCCBudget. The SP keys the row by CC code (+ year for non-performing),
        // so the year stays read-only (legacy showed a year dropdown but always posted the original year).
        resubmit: {
            fields: [
                { key: 'CCType', label: 'Cost Center Type', readOnly: true },
                { key: 'SubType', label: 'Sub Type', readOnly: true },
                { key: 'CostCenter', label: 'Cost Center', readOnly: true },
                { key: 'Year', label: 'Year', readOnly: true, show: (v) => isNonPerformingCC(v.CCType) },
                { key: 'Amount', label: 'Amount', required: true, filter: /^\d*\.?\d{0,2}$/ },
                { key: 'ExecFile', label: 'Upload Approved budget for execution (PDF)', type: 'file', accept: 'application/pdf,.pdf', show: (v) => v.CCType === 'Performing' },
                { key: 'Remarks', label: 'Remarks', type: 'textarea', required: true },
            ],
            initial: (r, d) => ({
                CCType: d.CCType || '', SubType: d.SubType || '', CostCenter: d.CostCenter || '', Year: d.Year || '',
                Amount: d.Amount != null ? String(d.Amount) : '', Remarks: d.Remarks || '', ExecFile: null,
            }),
            validate: (v, r, d) => {
                const errors = [];
                if (!(Number(v.Amount) > 0)) errors.push('Enter Amount');
                if (v.CCType === 'Performing') {
                    if (!d.Approvedbudgetexecution && !v.ExecFile) errors.push('Upload Approved budget for execution');
                    if (v.ExecFile && !/\.pdf$/i.test(v.ExecFile.name)) errors.push('Invalid Upload File. Upload only PDF File');
                }
                return errors;
            },
            route: 'Accounts/UpdateCCBudget',
            payload: (r, d, v, { user, roleId }) => ({
                CostCenter: d.CostCenter, Year: isNonPerformingCC(d.CCType) ? d.Year : '', Amount: parseFloat(v.Amount) || 0,
                Createdby: user, Remarks: v.Remarks.trim(), budgetexecutionexists: v.ExecFile ? 'Yes' : 'No', CCType: d.CCType, RoleId: roleId,
            }),
            // Performing + new file → "Submited,<file name>". The non-performing branch of spUpdateCCBudget never
            // sets its status output, so a blank reply there means the update went through.
            ok: (s, r, d) => /^Submitt?ed(,|$)/.test(s || '') || (isNonPerformingCC(d.CCType) && !s),
            afterOk: async (s, r, d, v) => {
                if (!v.ExecFile) return;
                const fileName = String(s || '').split(',')[1];
                if (!fileName) throw new Error('no file name returned for the upload');
                await uploadFileToS3(v.ExecFile, S3_FOLDERS.COST_CENTER_BUDGET, fileName);
            },
        },
    },

    // Legacy /AccountsApproval/VerifyDCABudget (+ VerifyDCABudgetGrid / VerifyDCABudgetView; returned rows → UpdateDcaBudget)
    DCABudget: {
        title: 'Account Head Budget Verification',
        successLabel: 'Account Head Budget',
        noun: 'budget',
        icon: PieChart,
        searchPlaceholder: 'Search by cost center, name, year, type…',
        queue: { route: 'Accounts/GetVerificationDCABudgets', params: ({ roleId, userId }) => ({ Roleid: roleId, Uid: userId }) },
        itemKey: (r) => `${r.CC_Code}|${r.FYyear}`,
        card: {
            title: (r) => `${r.CC_Code}${r.CC_Name ? ` - ${r.CC_Name}` : ''}`,
            subtitle: (r) => [r.CC_Type, r.FYyear !== 'N/A' && r.FYyear].filter(Boolean).join(' · '),
            meta: (r) => (r.BalanceBudget != null ? `Balance ${money(r.BalanceBudget)}` : null),
            amount: (r) => money(r.BudgetValue),
        },
        searchText: (r) => `${r.CC_Code} ${r.CC_Name} ${r.CC_Type} ${r.FYyear} ${r.BudgetValue}`,
        detail: {
            route: (r) => (String(r.Status) === '0' ? 'Accounts/GetDCABudgetUpdationbyId' : 'Accounts/GetVerifyDCABudgetbyId'),
            params: (r, { roleId }) => (String(r.Status) === '0'
                ? { CCCode: r.CC_Code, Year: r.FYyear, CCType: r.CC_Type }
                : { CCCode: r.CC_Code, Year: r.FYyear, CCType: r.CC_Type, RoleId: roleId }),
            // Verify view → list of account-head rows; update view → CC budget header with DCABudgetDetails.
            list: true,
            select: (data) => (Array.isArray(data)
                ? { items: data, ...(data[0] || {}) }
                : data && { ...data, items: data.DCABudgetDetails || [] }),
        },
        rowAux: [
            { name: 'ccDocs', route: 'Accounts/Getccuploadocsexists', params: (r, d, { userId }) => ({ CCCode: r.CC_Code, UID: userId }) },
            { name: 'dcaHeads', route: 'Accounts/GetBudgetDCADetails', params: (r, d) => ({ CcTypeID: d.CCTypeId }), when: (r, d) => String(r.Status) === '0' && !!d.CCTypeId },
        ],
        moid: (r, d) => d.MOID,
        remarksKey: (r, d) => d.Refno,
        // Legacy fills the status dropdown straight from GetApprovalstatus, Return included.
        showReturn: 'Yes',
        isReturned: (r) => String(r.Status) === '0',
        header: {
            title: (r) => `${r.CC_Code}${r.CC_Name ? ` - ${r.CC_Name}` : ''}`,
            subtitle: (r, d) => `Account head total ${money((d.items || []).reduce((a, it) => a + (Number(it.DCABudgetValue) || 0), 0)) || '₹ 0'}`,
            chips: (r) => [r.CC_Type, r.FYyear !== 'N/A' && r.FYyear],
        },
        sections: (r, d) => [{
            fields: [
                ['Cost Center', r.CC_Code], ['Cost Center Name', r.CC_Name], ['Cost Center Type', r.CC_Type],
                r.CC_Type !== 'Performing' && ['Financial Year', r.FYyear],
                ['CC Budget Value', money(r.BudgetValue)], ['CC Budget Balance', money(r.BalanceBudget)],
                d.Remarks && ['Remarks', d.Remarks, true],
            ],
        }],
        extra: (r, d, { aux, ext, setExt }) => {
            const docs = <CCDocsBlock docs={aux.ccDocs} />;
            if (String(r.Status) === '0') return docs;
            const items = d.items || [];
            const total = items.reduce((a, it) => a + (Number(it.DCABudgetValue) || 0), 0);
            const performing = r.CC_Type === 'Performing';
            return (
                <>
                    <CheckedItemsTable
                        title="Account Heads"
                        items={items}
                        ext={ext}
                        setExt={setExt}
                        columns={[
                            { label: 'Account Head Code', render: (it) => it.DCACode },
                            { label: 'Account Head Name', render: (it) => it.DCAName, align: 'left' },
                            { label: 'Budget Value', render: (it) => fmt(it.DCABudgetValue), align: 'right' },
                            { label: 'Date', render: (it) => it.DCABudgetCreationdate },
                            ...(performing ? [] : [{ label: 'Financial Year', render: (it) => it.FYyear }]),
                        ]}
                        footer={(
                            <>
                                <td colSpan={3} className="px-3 py-2 text-xs font-bold text-right text-gray-700 dark:text-gray-300">Total</td>
                                <td className="px-3 py-2 text-xs font-bold text-right text-emerald-600">{fmt(total)}</td>
                                <td colSpan={performing ? 1 : 2}></td>
                            </>
                        )}
                    />
                    {docs}
                </>
            );
        },
        beforeAction: (action, r, d, { ext }) => {
            const items = d.items || [];
            if (!items.length) return ['Invalid'];
            return allItemsChecked(items, ext) ? [] : ['Please Verify'];
        },
        approve: {
            route: 'Accounts/ApproveDCABudget',
            payload: (r, d, { action, note, user, roleId }) => {
                const items = d.items || [];
                return {
                    Budgetidlist: items.map((it) => `${it.DCABudgetId},`).join(''),
                    CCCode: r.CC_Code,
                    FYyear: d.FYyear || '',
                    RoleId: roleId,
                    ApprovalNote: note,
                    CreatedBy: user,
                    DCABudgetValue: items.reduce((a, it) => a + (Number(it.DCABudgetValue) || 0), 0),
                    DcaAmounts: items.map((it) => `${it.DCABudgetValue},`).join(''),
                    Action: action,
                };
            },
            ok: ['Submited', 'Submitted'],
        },
        // Legacy UpdateDCAAssignedBudget → spAssignDCABudget (Action 'Update'): every account head of the CC type,
        // tick + amount per head; the new total may not exceed the CC balance plus what was assigned before.
        resubmit: {
            fields: [
                { key: 'CCCode', label: 'Cost Center', readOnly: true },
                { key: 'FYyear', label: 'Financial Year', readOnly: true, show: (v) => v.CCType !== 'Performing' },
                { key: 'Remarks', label: 'Remarks', type: 'textarea', required: true },
            ],
            initial: (r, d) => {
                const dca = {};
                (d.items || []).forEach((it) => { dca[it.DCACode] = { amount: String(it.DCABudgetValue ?? ''), checked: true }; });
                const old = (d.items || []).reduce((a, it) => a + (Number(it.DCABudgetValue) || 0), 0);
                return { CCCode: d.CCCode || r.CC_Code, FYyear: d.FYyear || '', CCType: d.CCType || r.CC_Type, Remarks: d.Remarks || '', dca, old };
            },
            render: (r, d, { aux, values, setValues }) => {
                const heads = (Array.isArray(aux.dcaHeads) && aux.dcaHeads.length ? aux.dcaHeads : d.items || []);
                const dca = values.dca || {};
                const subTotal = dcaSubTotal(dca);
                const remaining = (Number(d.CCBudgetBalance) || 0) + (Number(values.old) || 0) - subTotal;
                const setRow = (code, patch) => setValues((p) => ({ ...p, dca: { ...(p.dca || {}), [code]: { amount: '', checked: false, ...((p.dca || {})[code]), ...patch } } }));
                return (
                    <div className="space-y-2">
                        <div className="flex flex-wrap gap-4 text-xs text-gray-700 dark:text-gray-300">
                            <span>CC Budget: <b>{fmt(d.CCBudget)}</b></span>
                            <span>CC Balance: <b>{fmt(d.CCBudgetBalance)}</b></span>
                            <span>Sub Total: <b>{fmt(subTotal) || '0'}</b></span>
                            <span className={remaining < 0 ? 'text-red-600 font-semibold' : ''}>Available after this: <b>{fmt(remaining) || '0'}</b></span>
                        </div>
                        <div className="overflow-x-auto rounded-xl border border-amber-200 dark:border-amber-700 bg-white dark:bg-gray-800">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-amber-100 dark:bg-amber-900/30 text-[10px] font-bold uppercase text-amber-800 dark:text-amber-300">
                                        <th className="px-3 py-2 text-center">Check</th>
                                        <th className="px-3 py-2 text-left">Account Head</th>
                                        <th className="px-3 py-2 text-right">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {heads.map((h) => {
                                        const row = dca[h.DCACode] || { amount: '', checked: false };
                                        return (
                                            <tr key={h.DCACode} className="border-t border-gray-100 dark:border-gray-700">
                                                <td className="px-3 py-1.5 text-center">
                                                    <input type="checkbox" checked={!!row.checked} onChange={(e) => setRow(h.DCACode, { checked: e.target.checked })} />
                                                </td>
                                                <td className="px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300">{h.DCACode}, {h.DCAName}</td>
                                                <td className="px-3 py-1.5 text-right">
                                                    <input
                                                        type="text"
                                                        value={row.amount}
                                                        onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) setRow(h.DCACode, { amount: v }); }}
                                                        className="w-36 px-2 py-1 text-xs text-right border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {!heads.length && <tr><td colSpan={3} className="px-3 py-4 text-center text-xs text-gray-500">No Account Heads Found</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            },
            validate: (v, r, d) => {
                const rows = Object.values(v.dca || {});
                const errors = [];
                if (!rows.some((x) => x.checked) || rows.some((x) => !x.checked && x.amount !== '')) errors.push('Check Account Head to Assign budget');
                if (rows.some((x) => x.checked && x.amount === '')) errors.push('Enter Account Head Budget');
                if (dcaSubTotal(v.dca || {}) > (Number(d.CCBudgetBalance) || 0) + (Number(v.old) || 0)) errors.push('Account Head Budget is greater than CC Budget');
                return errors;
            },
            route: 'Accounts/UpdateDCAAssignedBudget',
            payload: (r, d, v, { user, roleId }) => {
                const picked = Object.entries(v.dca || {}).filter(([, x]) => x.checked && x.amount !== '');
                return {
                    UDCACode: picked.map(([code]) => `${code},`).join(''),
                    UCCCode: v.CCCode,
                    UDcaAmounts: picked.map(([, x]) => `${x.amount},`).join(''),
                    ...(v.CCType !== 'Performing' ? { UFYyear: v.FYyear } : {}),
                    URemarks: v.Remarks.trim(),
                    UAction: 'Update',
                    UOldBudgetAmount: Number(v.old || 0).toFixed(2),
                    RoleId: roleId,
                    UCreatedBy: user,
                };
            },
            ok: ['Updated'],
        },
    },

    // Legacy /Accounts/VerifyWorkInProgress (+ VerifyWorkInProgressGrid / VerifyWorkInProgressView; returned rows → EditWorkInProgress)
    WorkInProgress: {
        title: 'Work In Progress Verification',
        successLabel: 'Work Progress',
        noun: 'work progress entry',
        icon: Hammer,
        searchPlaceholder: 'Search by cost center, PO, client, date…',
        queue: { route: 'Accounts/GetVerificationWorkProgress', params: ({ roleId, userId }) => ({ UserId: userId, Roleid: roleId }) },
        itemKey: (r) => r.Id,
        card: {
            title: (r) => r.PONumber || `Entry ${r.Id}`,
            subtitle: (r) => r.CCName,
            meta: (r) => [r.Date, r.ClientName].filter(Boolean).join(' · '),
            amount: (r) => money(r.Amount),
        },
        searchText: (r) => `${r.CCName} ${r.PONumber} ${r.ClientName} ${r.SubClientName} ${r.Date} ${r.Amount}`,
        detail: { route: 'Accounts/GetWorkProgressById', params: (r) => ({ Id: r.Id }) },
        moid: (r, d) => d.MOID || r.MOID,
        remarksKey: (r) => r.Id,
        // Legacy fills the status dropdown straight from GetApprovalstatus, Return included.
        showReturn: 'Yes',
        isReturned: (r) => String(r.Status).trim() === '0',
        header: {
            title: (r, d) => d.PONumber || `Entry ${r.Id}`,
            subtitle: (r, d) => money(d.Amount),
            chips: (r, d) => [d.CCName, d.Date],
        },
        sections: (r, d) => [{
            fields: [
                ['Cost Center', d.CCName], ['Client', d.ClientName], ['Sub Client', d.SubClientName],
                ['PO Number', d.PONumber], ['Date', d.Date], ['Amount', money(d.Amount)],
                ['Description', d.Description, true],
            ],
        }],
        approve: {
            route: 'Accounts/ApproveWorkProgress',
            payload: (r, d, { action, note, user, roleId }) => ({
                Id: d.Id, CCCode: d.CCCode, PONumber: d.PONumber, Action: action, ApprovalNote: note, Amount: d.Amount,
                Roleid: roleId, CreatedBy: user,
            }),
            ok: ['Submited', 'Submitted'],
        },
        // Legacy UpdateWorkProgress → spUpdateWorkProgress. Cost center stays fixed; client / sub client / PO cascade.
        resubmit: {
            fields: [
                { key: 'CCName', label: 'Cost Center', readOnly: true },
                { key: 'Date', label: 'Date', type: 'date', required: true },
                { key: 'Amount', label: 'Amount', required: true, filter: /^\d*\.?\d{0,2}$/ },
                { key: 'Description', label: 'Description', type: 'textarea', required: true },
                { key: 'Remarks', label: 'User Remarks', type: 'textarea', required: true },
            ],
            initial: (r, d) => ({
                CCName: d.CCName || '', Clientcode: d.Clientcode || '', SubClientcode: d.SubClientcode || '', PONumber: d.PONumber || '',
                Date: isoDate(d.Date), Amount: d.Amount != null ? String(d.Amount) : '', Description: d.Description || '', Remarks: '',
            }),
            render: (r, d, { values, setValues }) => (
                <WIPPartyEditor ccCode={d.CCCode} wpId={d.Id} values={values} setValues={setValues} />
            ),
            validate: (v) => {
                const errors = [];
                if (!v.Clientcode) errors.push('Select Client');
                if (!v.SubClientcode) errors.push('Select Sub Client');
                if (!v.PONumber) errors.push('Select PO Number');
                if (!(Number(v.Amount) > 0)) errors.push('Enter Amount');
                if (v.Date && v.Date > isoDate(new Date())) errors.push('Date cannot be in the future');
                return errors;
            },
            route: 'Accounts/UpdateWorkProgress',
            payload: (r, d, v, { user, roleId }) => ({
                Id: d.Id, CCCode: d.CCCode, Clientcode: v.Clientcode, SubClientcode: v.SubClientcode, PONumber: v.PONumber,
                Date: displayDate(v.Date), Amount: parseFloat(v.Amount) || 0, Description: v.Description.trim(), Remarks: v.Remarks.trim(),
                Roleid: roleId, CreatedBy: user,
            }),
            ok: ['Submited', 'Submitted'],
        },
    },

    // Legacy /AccountsApproval/VerifyGeneralPayment (+ VerifyGeneralPaymentGrid / VerifyGeneralPaymentView)
    GeneralPayment: {
        title: 'General Payable Verification',
        successLabel: 'General Payment',
        noun: 'payment',
        icon: Banknote,
        searchPlaceholder: 'Search by transaction, cost center, account head…',
        queue: { route: 'Accounts/GetVerificationGeneralPayments', params: ({ roleId }) => ({ Roleid: roleId }) },
        itemKey: (r) => r.BankTransactionRefNo,
        card: {
            title: (r) => r.CCCodename || r.CCCode,
            subtitle: (r) => r.DCACodename || r.DCACode,
            meta: (r) => [r.SubDcaname || r.SubDCACode, `Txn ${r.BankTransactionRefNo}`].filter(Boolean).join(' · '),
            amount: (r) => money(r.TransactionAmount),
        },
        searchText: (r) => `${r.BankTransactionRefNo} ${r.CCCodename} ${r.DCACodename} ${r.SubDcaname} ${r.TransactionAmount}`,
        detail: { route: 'Accounts/GetGeneralPaymentsbyRefno', params: (r) => ({ TransRefno: r.BankTransactionRefNo }) },
        moid: (r, d) => d.MOID,
        // Legacy passes the payment amount so approval limits apply.
        chkAmt: (r, d) => d.TransactionAmount,
        remarksKey: (r) => r.BankTransactionRefNo,
        // Legacy drops Return from this screen's status list.
        showReturn: 'No',
        excludeActions: () => ['return'],
        isReturned: (r) => String(r.Status).trim() === '0',
        returnedNotice: 'Correct and resubmit it from the General Payment screen — there is nothing to verify until it is resubmitted.',
        header: {
            title: (r, d) => d.Name || d.CCCodename,
            subtitle: (r, d) => money(d.TransactionAmount),
            chips: (r, d) => [d.ModeOfPay, `Txn ${r.BankTransactionRefNo}`, d.TransactionDate],
        },
        sections: (r, d) => [{
            fields: [
                ['Cost Center', d.CCCodename], ['Account Head', d.DCACodename], ['Sub Account Head', d.SubDcaname],
                ['Name', d.Name], ['Bank Name', d.Bank], ['Mode of Pay', d.ModeOfPay],
                ['Transaction Number', d.Number], ['Transaction Date', d.TransactionDate],
                ['Transaction Amount', money(d.TransactionAmount)],
                ['Amount in Words', d.AmountInWords, true],
                d.Remarks && ['Remarks', d.Remarks, true],
            ],
        }],
        approve: {
            route: 'Accounts/ApproveGeneralPayment',
            payload: (r, d, { action, note, user, roleId }) => ({
                BankTransactionRefNo: r.BankTransactionRefNo, Action: action, Roleid: roleId, ApprovalNote: note,
                Createdby: user, Bank: d.Bank, TransactionAmount: d.TransactionAmount,
            }),
            ok: ['Submited', 'Submitted'],
        },
    },

    // Legacy /AccountsApproval/VerifyJournalVoucher (+ VerifyJournalVoucherGrid / VerifyJournalVoucherView)
    JournalVoucher: {
        title: 'Journal Voucher Verification',
        successLabel: 'Journal Voucher',
        noun: 'journal voucher',
        icon: NotebookPen,
        searchPlaceholder: 'Search by transaction number, description, date…',
        queue: { route: 'Accounts/GetVerificationJV', params: ({ roleId }) => ({ Roleid: roleId }) },
        itemKey: (r) => r.TranNo,
        card: {
            title: (r) => `JV ${r.TranNo}`,
            subtitle: (r) => r.JVRemarks,
            meta: (r) => r.Date,
            amount: (r) => money(r.Amount),
        },
        searchText: (r) => `${r.TranNo} ${r.JVRemarks} ${r.Date} ${r.Amount}`,
        detail: { route: 'Accounts/GetVerificationJVbyNo', params: (r) => ({ TranNo: r.TranNo }) },
        rowAux: [
            { name: 'jvLedgers', route: 'Accounts/GetJVledgerDetailsbyTranno', params: (r) => ({ TranNo: r.TranNo }) },
        ],
        moid: (r, d) => d.MOID || r.MOID,
        remarksKey: (r) => r.TranNo,
        // Legacy drops Return from this screen's status list.
        showReturn: 'No',
        excludeActions: () => ['return'],
        isReturned: (r) => String(r.Status).trim() === '0',
        returnedNotice: 'Correct and resubmit it from the Journal Voucher screen — there is nothing to verify until it is resubmitted.',
        header: {
            title: (r) => `JV ${r.TranNo}`,
            subtitle: (r, d) => money(d.Amount),
            chips: (r, d) => [d.Date],
        },
        sections: (r, d) => [{
            fields: [
                ['JV Creation Date', d.Date], ['Transaction Number', d.TranNo],
                ['Credit Amount', money(d.Amount)], ['Debit Amount', money(d.Amount)],
                ['JV Remarks', d.JVRemarks, true],
            ],
        }],
        extra: (r, d, { aux }) => {
            const rows = Array.isArray(aux.jvLedgers) ? aux.jvLedgers : [];
            const sum = (t) => rows.filter((x) => x.Trantypes === t).reduce((a, x) => a + (Number(x.Ledgeramounts) || 0), 0);
            return (
                <Block title="Ledgers">
                    <SimpleTable
                        heads={['Ledger Type', 'Ledger Name', 'Transaction Type', 'Payment Type', 'Invoice No', 'Amount']}
                        rows={rows.map((x) => [x.LedgerFors, x.Ledgers, x.Trantypes, x.Paytypes, x.Invnos, fmt(x.Ledgeramounts)])}
                        alignRight={[5]}
                        foot={rows.length ? ['', '', '', '', `Credit ${fmt(sum('Credit')) || '0'} / Debit`, fmt(sum('Debit')) || '0'] : undefined}
                    />
                </Block>
            );
        },
        approve: {
            route: 'Accounts/ApproveJV',
            payload: (r, d, { action, note, user, roleId }) => ({
                TranNo: r.TranNo, Action: action, ApprovalNote: note, CreatedBy: user, Roleid: roleId,
            }),
            ok: ['Successfull'],
        },
    },

    // Legacy /Accounts/VerifyCCSEPPay (+ VerifyCCSEPPayGrid / VerifyCCSEPPayView) — CC-wise Salary / Wages / PF / ESI payments
    CCSEPPay: {
        title: 'Salary/Wages/PF/ESI Payment Verification',
        successLabel: 'Payment',
        noun: 'payment',
        icon: Landmark,
        searchPlaceholder: 'Search by transaction, payment for, month, year…',
        queue: { route: 'Accounts/GetVerificationSEPPay', params: ({ roleId }) => ({ RoleId: roleId }) },
        itemKey: (r) => r.TransactionRefno,
        card: {
            title: (r) => sepPaymentLabel(r.PaymentFor),
            subtitle: (r) => `${r.MonthName || ''} ${r.Year || ''}`.trim(),
            meta: (r) => `Txn ${r.TransactionRefno}`,
        },
        searchText: (r) => `${r.TransactionRefno} ${r.PaymentFor} ${r.MonthName} ${r.Year}`,
        detail: { route: 'Accounts/GetSEPPaybyRefno', params: (r) => ({ TransactionRefno: r.TransactionRefno }) },
        moid: (r, d) => d.MOID,
        remarksKey: (r) => r.TransactionRefno,
        // Legacy drops Return from this screen's status list; returned rows open the same view (no editor).
        showReturn: 'No',
        excludeActions: () => ['return'],
        header: {
            title: (r, d) => sepPaymentLabel(d.PaymentFor),
            subtitle: (r, d) => money(d.PaymentAmount),
            chips: (r, d) => [`${d.MonthName || ''} ${d.Year || ''}`.trim(), `Txn ${r.TransactionRefno}`, String(r.Status).trim() === '0' && 'Returned'],
        },
        sections: (r, d) => [{
            title: 'Payment For',
            fields: [['Payment For', sepPaymentLabel(d.PaymentFor)], ['Year', d.Year], ['Month', d.MonthName]],
        }, {
            title: 'Payment Details',
            fields: [
                ['Bank', d.BankName], ['Mode of Pay', d.ModeofPay || d.ModeOfPay], ['Payment No', d.PaymentNo],
                ['Payment Date', d.PaymentDate], ['Payment Amount', money(d.PaymentAmount)],
                ['Amount in Words', d.AmountInWords, true],
                d.Remarks && ['Remarks', d.Remarks, true],
            ],
        }],
        extra: (r, d) => {
            const pf = d.PaymentFor === 'StaffPF' || d.PaymentFor === 'LabourPF';
            const ccs = d.CCData || [];
            const total = ccs.reduce((a, x) => a + (Number(x.Amount) || 0), 0);
            // ApprovalNote carries "Role:Employee:Remarks||…" — the first entry is the creator.
            const approvals = String(d.ApprovalNote || '').split('||').filter(Boolean).map((t, i) => {
                const [role = '', emp = '', ...rest] = t.split(':');
                return [i === 0 ? `Created By: ${role.trim()}` : 'Verified By', emp.trim(), rest.join(':').trim()];
            });
            return (
                <>
                    <Block title="Cost Centers">
                        <SimpleTable
                            heads={pf ? ['Cost Center', 'Contribution Type', 'Amount'] : ['Cost Center', 'Amount']}
                            rows={ccs.map((x) => (pf ? [x.CCName, x.ContributeType, fmt(x.Amount)] : [x.CCName, fmt(x.Amount)]))}
                            alignRight={[pf ? 2 : 1]}
                            foot={ccs.length ? (pf ? ['', 'Total', fmt(total)] : ['Total', fmt(total)]) : undefined}
                        />
                    </Block>
                    {approvals.length > 0 && (
                        <Block title="Approvals">
                            <SimpleTable heads={['', 'Employee Name', 'Remarks']} rows={approvals} />
                        </Block>
                    )}
                </>
            );
        },
        approve: {
            route: 'Accounts/ApproveSEPPay',
            payload: (r, d, { action, note, user, roleId }) => ({
                PaymentDate: d.PaymentDate, TransactionRefno: r.TransactionRefno, Action: action, ApprovalNote: note,
                Month: d.Month, Year: d.Year, RoleId: roleId, Createdby: user,
            }),
            ok: ['Submited', 'Submitted'],
        },
    },

    // Legacy /Purchase/VerfiyNewStockIssue (+ VerfiyNewStockIssueGrid / VerfiyNewStockIssueView / ViewNewStockIssueGrid)
    NewStockIssue: {
        title: 'New Stock Issue Verification',
        successLabel: 'New Stock Issue',
        noun: 'stock issue',
        icon: PackageOpen,
        searchPlaceholder: 'Search by transaction, indent, cost center…',
        queue: { route: 'Purchase/GetVerifyNewStockIssueGrid', params: ({ roleId, userId }) => ({ RoleId: roleId, Userid: userId }) },
        itemKey: (r) => r.Rid,
        card: {
            title: (r) => r.Refno,
            subtitle: (r) => `${r.FromCC || ''} → ${r.ToCC || ''}`,
            meta: (r) => [r.Indentno && `Indent ${r.Indentno}`, r.Date].filter(Boolean).join(' · '),
            amount: (r) => money(r.Amount),
        },
        searchText: (r) => `${r.Refno} ${r.Indentno} ${r.FromCC} ${r.ToCC} ${r.Date} ${r.Amount}`,
        detail: {
            route: 'Purchase/GetVerifyNewStockIssueView',
            params: (r) => ({ Rowid: r.Rid }),
            select: (data) => (Array.isArray(data) ? data[0] || null : data),
        },
        rowAux: [
            { name: 'nsiItems', route: 'Purchase/ViewNewStockIssueGrid', params: (r) => ({ Trno: r.Refno }) },
        ],
        moid: (r, d) => d.MOID || r.MOID,
        remarksKey: (r) => r.Refno,
        // Legacy drops Return from this screen's status list; returned rows open the same view.
        showReturn: 'No',
        excludeActions: () => ['return'],
        header: {
            title: (r) => r.Refno,
            subtitle: (r) => money(r.Amount),
            chips: (r) => [r.Indentno && `Indent ${r.Indentno}`, String(r.Status).trim() === '0' && 'Returned'],
        },
        sections: (r) => [{
            fields: [
                ['Transaction No', r.Refno], ['Indent No', r.Indentno],
                ['From Cost Center', r.FromCC], ['To Cost Center', r.ToCC],
                ['Issue Date', r.Date], ['Expected Received Date', r.TransactionDate], ['Amount', money(r.Amount)],
            ],
        }],
        extra: (r, d, { aux, ext, setExt }) => {
            const items = Array.isArray(aux.nsiItems) ? aux.nsiItems : [];
            const total = items.reduce((a, it) => a + (Number(it.OItemamt) || 0), 0);
            return (
                <>
                    <CheckedItemsTable
                        title="Issued Items"
                        items={items}
                        ext={ext}
                        setExt={setExt}
                        onItemClick={(it) => setExt((p) => ({ ...p, popup: it.OItemcode }))}
                        columns={[
                            // Legacy links item codes to the MRR breakup popup (NewStockIssueSummaryPopup).
                            { label: 'Item Code', render: (it) => it.OItemcode, link: true },
                            { label: 'Item Name', render: (it) => it.OItemname, align: 'left' },
                            { label: 'Specification', render: (it) => it.OItemspecification, align: 'left' },
                            { label: 'Units', render: (it) => it.OUnits },
                            { label: 'Qty', render: (it) => it.OItemqty, align: 'right' },
                            { label: 'Description', render: (it) => it.SIndRemarks, align: 'left' },
                            { label: 'Amount', render: (it) => fmt(it.OItemamt), align: 'right' },
                        ]}
                        footer={(
                            <>
                                <td colSpan={7} className="px-3 py-2 text-xs font-bold text-right text-gray-700 dark:text-gray-300">Total</td>
                                <td className="px-3 py-2 text-xs font-bold text-right text-emerald-600">{fmt(items[0]?.OSumTotal ?? total)}</td>
                            </>
                        )}
                    />
                    {ext.popup && (
                        <TradeItemSummaryModal
                            itemCode={ext.popup}
                            route="Purchase/NewStockIssueSummaryPopup"
                            params={{ Itemcode: ext.popup, Refno: r.Refno }}
                            onClose={() => setExt((p) => ({ ...p, popup: null }))}
                        />
                    )}
                </>
            );
        },
        beforeAction: (action, r, d, { aux, ext }) => {
            const items = Array.isArray(aux.nsiItems) ? aux.nsiItems : [];
            if (!items.length) return ['Invalid Submission'];
            return allItemsChecked(items, ext) ? [] : ['Please Verify ItemCodes'];
        },
        approve: {
            route: 'Purchase/ApproveNewStockIssue',
            payload: (r, d, { action, note, user, roleId }) => ({
                Refno: r.Refno, Appstatus: action, ActionRemarks: note, RoleID: String(roleId), Createdby: user,
            }),
            ok: ['Submitted'],
        },
    },

    // Legacy /Purchase/VerifyOldStockIssue (+ VerifyOldStockIssueGrid / VerifyOldStockIssueView / ViewOldStockIssueGrid)
    OldStockIssue: {
        title: 'Old Stock Issue Verification',
        successLabel: 'Old Stock Issue',
        noun: 'stock issue',
        icon: Recycle,
        searchPlaceholder: 'Search by transaction, indent, cost center…',
        queue: { route: 'Purchase/GetVerfiyOldStockIssueGrid', params: ({ roleId, userId }) => ({ RoleId: roleId, Userid: userId }) },
        itemKey: (r) => r.Rid,
        card: {
            title: (r) => r.Refno,
            subtitle: (r) => `${r.FromCC || ''} → ${r.ToCC || ''}`,
            meta: (r) => [r.Indentno && `Indent ${r.Indentno}`, r.Date].filter(Boolean).join(' · '),
            amount: (r) => money(r.Amount),
        },
        searchText: (r) => `${r.Refno} ${r.Indentno} ${r.FromCC} ${r.ToCC} ${r.Date} ${r.Amount}`,
        detail: {
            route: 'Purchase/GetVerifyOldStockIssueView',
            params: (r) => ({ Rowid: r.Rid }),
            select: (data) => (Array.isArray(data) ? data[0] || null : data),
        },
        rowAux: [
            { name: 'osiItems', route: 'Purchase/ViewOldStockIssueGrid', params: (r) => ({ Trno: r.Refno }) },
        ],
        moid: (r, d) => d.MOID || r.MOID,
        remarksKey: (r) => r.Refno,
        // Legacy drops Return from this screen's status list; returned rows open the same view.
        showReturn: 'No',
        excludeActions: () => ['return'],
        header: {
            title: (r) => r.Refno,
            subtitle: (r) => money(r.Amount),
            chips: (r) => [r.Indentno && `Indent ${r.Indentno}`, String(r.Status).trim() === '0' && 'Returned'],
        },
        sections: (r) => [{
            fields: [
                ['Transaction No', r.Refno], ['Indent No', r.Indentno],
                ['From Cost Center', r.FromCC], ['To Cost Center', r.ToCC],
                ['Issue Date', r.Date], ['Expected Received Date', r.TransactionDate], ['Amount', money(r.Amount)],
            ],
        }],
        extra: (r, d, { aux, ext, setExt }) => {
            const items = Array.isArray(aux.osiItems) ? aux.osiItems : [];
            // Legacy builds the depreciation columns from the first item: asset codes (not starting with "1") carry them.
            const dep = items.length > 0 && !String(items[0].OItemcode || '').trim().startsWith('1');
            const sum = (k) => items.reduce((a, it) => a + (Number(it[k]) || 0), 0);
            return (
                <CheckedItemsTable
                    title="Issued Items"
                    items={items}
                    ext={ext}
                    setExt={setExt}
                    columns={[
                        { label: 'Item Code', render: (it) => it.OItemcode },
                        { label: 'Item Name', render: (it) => it.OItemname, align: 'left' },
                        { label: 'Specification', render: (it) => it.OItemspecification, align: 'left' },
                        { label: 'Quantity', render: (it) => it.OItemqty, align: 'right' },
                        { label: 'Amount', render: (it) => fmt(it.BefDepAmt), align: 'right' },
                        ...(dep ? [
                            { label: 'Dep in %', render: (it) => it.Deppercent, align: 'right' },
                            { label: 'Depreciation Value', render: (it) => fmt(it.AftDepAmt), align: 'right' },
                        ] : []),
                    ]}
                    footer={(
                        <>
                            <td colSpan={5} className="px-3 py-2 text-xs font-bold text-right text-gray-700 dark:text-gray-300">Total</td>
                            <td className="px-3 py-2 text-xs font-bold text-right text-emerald-600">{fmt(sum('BefDepAmt'))}</td>
                            {dep && <td></td>}
                            {dep && <td className="px-3 py-2 text-xs font-bold text-right text-emerald-600">{fmt(sum('AftDepAmt'))}</td>}
                        </>
                    )}
                />
            );
        },
        // Legacy only checks the ticks here (no empty-grid guard like New Stock Issue).
        beforeAction: (action, r, d, { aux, ext }) => {
            const items = Array.isArray(aux.osiItems) ? aux.osiItems : [];
            return !items.length || allItemsChecked(items, ext) ? [] : ['Please Verify ItemCodes'];
        },
        approve: {
            route: 'Purchase/ApproveOldStockIssue',
            payload: (r, d, { action, note, user, roleId }) => ({
                Refno: r.Refno, Appstatus: action, ActionRemarks: note, RoleID: String(roleId), Createdby: user,
            }),
            ok: ['Submitted'],
        },
    },

    // Legacy /Purchase/VerifyOldStockReceived (+ VerifyoldStockReceivedGrid / VerifyOldStockReceivedView / ViewOldStockReceivedGrid)
    OldStockReceived: {
        title: 'Old Stock Received Verification',
        successLabel: 'Old Stock Received',
        noun: 'stock receipt',
        icon: PackageCheck,
        searchPlaceholder: 'Search by transaction, indent, cost center…',
        queue: { route: 'Purchase/GetVerfiyOldStockReceivedGrid', params: ({ roleId, userId }) => ({ RoleId: roleId, Userid: userId }) },
        itemKey: (r) => r.Rid,
        card: {
            title: (r) => r.Refno,
            subtitle: (r) => `${r.FromCC || ''} → ${r.ToCC || ''}`,
            meta: (r) => [r.Indentno && `Indent ${r.Indentno}`, r.Date].filter(Boolean).join(' · '),
            amount: (r) => money(r.Amount),
        },
        searchText: (r) => `${r.Refno} ${r.Indentno} ${r.FromCC} ${r.ToCC} ${r.Date} ${r.Amount}`,
        detail: {
            route: 'Purchase/GetVerifyOldStockReceivedView',
            params: (r) => ({ Rowid: r.Rid }),
            select: (data) => (Array.isArray(data) ? data[0] || null : data),
        },
        rowAux: [
            { name: 'osrItems', route: 'Purchase/ViewOldStockReceivedGrid', params: (r) => ({ Trno: r.Refno }) },
        ],
        moid: (r, d) => d.MOID || r.MOID,
        remarksKey: (r) => r.Refno,
        // Legacy drops Return from this screen's status list; returned rows open the same view.
        showReturn: 'No',
        excludeActions: () => ['return'],
        header: {
            title: (r) => r.Refno,
            subtitle: (r) => money(r.Amount),
            chips: (r) => [r.Indentno && `Indent ${r.Indentno}`, String(r.Status).trim() === '0' && 'Returned'],
        },
        sections: (r) => [{
            fields: [
                ['Transaction No', r.Refno], ['Indent No', r.Indentno],
                ['From Cost Center', r.FromCC], ['To Cost Center', r.ToCC],
                ['Date', r.Date], ['Expected Received Date', r.Expdate], ['Amount', money(r.Amount)],
            ],
        }],
        extra: (r, d, { aux, ext, setExt }) => {
            const items = Array.isArray(aux.osrItems) ? aux.osrItems : [];
            // Legacy builds the depreciation columns (and the total) from the first item: asset codes don't start with "1".
            const dep = items.length > 0 && !String(items[0].Itemcode || '').trim().startsWith('1');
            const total = items.reduce((a, it) => a + (Number(it.Amount) || 0), 0);
            return (
                <CheckedItemsTable
                    title="Received Items"
                    items={items}
                    ext={ext}
                    setExt={setExt}
                    columns={[
                        { label: 'Item Code', render: (it) => it.Itemcode },
                        { label: 'Item Name', render: (it) => it.Itemname, align: 'left' },
                        { label: 'Specification', render: (it) => it.Specification, align: 'left' },
                        { label: 'Units', render: (it) => it.Units },
                        { label: 'Quantity', render: (it) => it.Quantity, align: 'right' },
                        { label: 'Basic', render: (it) => fmt(it.Basic), align: 'right' },
                        ...(dep ? [
                            { label: 'Depreciation in %', render: (it) => it.Deppercent, align: 'right' },
                            { label: 'After Dep Amount', render: (it) => fmt(it.Amount), align: 'right' },
                        ] : []),
                    ]}
                    footer={dep ? (
                        <>
                            <td colSpan={8} className="px-3 py-2 text-xs font-bold text-right text-gray-700 dark:text-gray-300">Total</td>
                            <td className="px-3 py-2 text-xs font-bold text-right text-emerald-600">{fmt(total)}</td>
                        </>
                    ) : null}
                />
            );
        },
        // Legacy only checks the ticks here (no empty-grid guard).
        beforeAction: (action, r, d, { aux, ext }) => {
            const items = Array.isArray(aux.osrItems) ? aux.osrItems : [];
            return !items.length || allItemsChecked(items, ext) ? [] : ['Please Verify ItemCodes'];
        },
        approve: {
            route: 'Purchase/ApproveOldStockIssueRecieved',
            payload: (r, d, { action, note, user, roleId }) => ({
                Refno: r.Refno, Appstatus: action, ActionRemarks: note, RoleID: String(roleId), Createdby: user,
            }),
            ok: ['Submitted'],
        },
    },

    // Legacy /Purchase/VerifyNewStockTransfer (+ VerifyNewStockTransferGrid / VerifyNewStockTransferView / ViewNewStockGrid)
    NewStockTransfer: {
        title: 'New Stock Transfer Verification',
        successLabel: 'New Stock Transfer',
        noun: 'stock transfer',
        icon: ArrowLeftRight,
        searchPlaceholder: 'Search by transaction, cost center…',
        queue: { route: 'Purchase/GetVerifyNewStockTransferGrid', params: ({ roleId, userId }) => ({ RoleId: roleId, Userid: userId }) },
        itemKey: (r) => r.Rid,
        card: {
            title: (r) => r.Refno,
            subtitle: (r) => `${r.CCCode || ''} → ${r.RCCCode || ''}`,
            meta: (r) => [r.CCType, r.Date].filter(Boolean).join(' · '),
            amount: (r) => money(r.Amount),
        },
        searchText: (r) => `${r.Refno} ${r.CCCode} ${r.RCCCode} ${r.CCType} ${r.Date} ${r.Amount}`,
        detail: {
            route: 'Purchase/GetVerifyNewStockTransferView',
            params: (r) => ({ Rowid: r.Rid }),
            select: (data) => (Array.isArray(data) ? data[0] || null : data),
        },
        rowAux: [
            { name: 'nstItems', route: 'Purchase/ViewNewStockGrid', params: (r) => ({ Trno: r.Refno }) },
        ],
        moid: (r, d) => d.MOID || r.MOID,
        remarksKey: (r) => r.Refno,
        // Legacy drops Return from this screen's status list; returned rows open the same view.
        showReturn: 'No',
        excludeActions: () => ['return'],
        header: {
            title: (r) => r.Refno,
            subtitle: (r) => money(r.Amount),
            chips: (r) => [r.CCType, String(r.Status).trim() === '0' && 'Returned'],
        },
        sections: (r) => [{
            fields: [
                ['Transaction No', r.Refno], ['Sending Cost Center', r.CCCode],
                ['Receiving Cost Center', r.RCCCode], ['Receiving Cost Center Type', r.CCType],
                ['Date', r.Date], ['Expected Received Date', r.Expdate], ['Amount', money(r.Amount)],
            ],
        }],
        extra: (r, d, { aux, ext, setExt }) => {
            const items = Array.isArray(aux.nstItems) ? aux.nstItems : [];
            const total = items.reduce((a, it) => a + (Number(it.OItemamt) || 0), 0);
            return (
                <>
                    <CheckedItemsTable
                        title="Transferred Items"
                        items={items}
                        ext={ext}
                        setExt={setExt}
                        onItemClick={(it) => setExt((p) => ({ ...p, popup: it.OItemcode }))}
                        columns={[
                            // Legacy links item codes to the MRR breakup popup (NewStockTransferSummaryPopup).
                            { label: 'Item Code', render: (it) => it.OItemcode, link: true },
                            { label: 'Item Name', render: (it) => it.OItemname, align: 'left' },
                            { label: 'Specification', render: (it) => it.OItemspecification, align: 'left' },
                            { label: 'Units', render: (it) => it.OUnits },
                            { label: 'Quantity', render: (it) => it.OItemqty, align: 'right' },
                            { label: 'Remarks', render: (it) => it.SIndRemarks, align: 'left' },
                            { label: 'Amount', render: (it) => fmt(it.OItemamt), align: 'right' },
                        ]}
                        footer={(
                            <>
                                <td colSpan={7} className="px-3 py-2 text-xs font-bold text-right text-gray-700 dark:text-gray-300">Total</td>
                                <td className="px-3 py-2 text-xs font-bold text-right text-emerald-600">{fmt(items[0]?.OSumTotal ?? total)}</td>
                            </>
                        )}
                    />
                    {ext.popup && (
                        <TradeItemSummaryModal
                            itemCode={ext.popup}
                            route="Purchase/NewStockTransferSummaryPopup"
                            params={{ Itemcode: ext.popup, Refno: r.Refno }}
                            onClose={() => setExt((p) => ({ ...p, popup: null }))}
                        />
                    )}
                </>
            );
        },
        // Legacy only checks the ticks here (no empty-grid guard).
        beforeAction: (action, r, d, { aux, ext }) => {
            const items = Array.isArray(aux.nstItems) ? aux.nstItems : [];
            return !items.length || allItemsChecked(items, ext) ? [] : ['Please Verify ItemCodes'];
        },
        approve: {
            route: 'Purchase/ApproveNewStockTransfer',
            payload: (r, d, { action, note, user, roleId }) => ({
                Refno: r.Refno, Appstatus: action, ActionRemarks: note, RoleID: String(roleId), Createdby: user,
            }),
            ok: ['Submitted'],
        },
    },

    // Legacy /Purchase/VerifyItemsTransfer?CCType=PCC|NPCC (+ VerifyItemsTransferGrid / VerifyItemsTransferView /
    // ViewItemTransferDetailsGridView). Level-driven: see the Items Transfer block in verificationParts.jsx.
    ItemsTransfer: {
        title: 'Stock Transfer Verification',
        successLabel: 'Stock Transfer',
        noun: 'transfer',
        icon: Truck,
        searchPlaceholder: 'Search by transfer no, indent, cost center…',
        queue: {
            route: 'Purchase/VerifyItemsTransferGrid',
            params: ({ roleId, userId, path, category }) => ({ Roleid: roleId, Created: String(userId), Userid: userId, CCType: itemsTransferCCType(path, category) }),
        },
        itemKey: (r) => r.ItId,
        card: {
            title: (r) => r.Refno,
            subtitle: (r) => `${r.FromCC || ''} → ${r.ToCC || ''}`,
            meta: (r) => [r.IndentNo && `Indent ${r.IndentNo}`, r.Date].filter(Boolean).join(' · '),
        },
        searchText: (r) => `${r.Refno} ${r.IndentNo} ${r.FromCC} ${r.ToCC} ${r.Date}`,
        // The row's approval levels for this role decide what the verifier sees and sends.
        detail: { route: 'Purchase/GetItemtransferLevels', params: (r, { roleId }) => ({ MOID: r.MOID, Roleid: roleId }) },
        rowAux: [
            {
                name: 'itItems', route: 'Purchase/GetItemstransferDetails',
                params: (r, d) => ({ Refno: r.Refno, PLevel: d.IndentPresentLevel, DLevel: d.IndentDefineLevel }),
            },
            { name: 'itRemarks', route: 'Purchase/GetItemsTransferRemarks', params: (r) => ({ Refno: r.Refno }) },
            {
                name: 'itDep', route: 'Purchase/GetItemDepPercentbyCC', params: (r) => ({ FromCC: r.FromCC, ToCC: r.ToCC }),
                when: (r, d) => Number(d.IndentPresentLevel) >= Number(d.IndentDefineLevel),
            },
        ],
        moid: (r) => r.MOID,
        remarksKey: (r) => r.Refno,
        // Legacy never offers Return here; at the define level of a non-performing transfer only Verify.
        showReturn: 'No',
        excludeActions: (r, d) => (Number(d.IndentPresentLevel) === Number(d.IndentDefineLevel) && r.CCType !== 'Performing'
            ? ['return', 'approve', 'reject'] : ['return']),
        // Returned performing transfers open the full entry editor (UpdateItemsTransferView) in legacy.
        isReturned: (r) => String(r.Status).trim() === '0' && r.CCType === 'Performing',
        returnedNotice: 'Correct and resubmit it from the Items Transfer screen — there is nothing to verify until it is resubmitted.',
        header: {
            title: (r) => r.Refno,
            subtitle: (r) => `${r.FromCC || ''} → ${r.ToCC || ''}`,
            chips: (r) => [r.CCType, r.IndentNo && `Indent ${r.IndentNo}`, String(r.Status).trim() === '0' && 'Returned'],
        },
        sections: (r) => [{
            fields: [
                ['Ref No', r.Refno], ['Indent No', r.IndentNo],
                ['Sending Cost Center', r.FromCC], ['Receiving Cost Center', r.ToCC],
                ['Date', r.Date], ['Expected Received Date', r.Expdate],
            ],
        }],
        extra: (r, d, { aux, ext, setExt }) => {
            const items = Array.isArray(aux.itItems) ? aux.itItems : [];
            const remarks = Array.isArray(aux.itRemarks) ? aux.itRemarks : [];
            const depCfg = aux.itDep || {};
            const atOrAbove = Number(d.IndentPresentLevel) >= Number(d.IndentDefineLevel);
            const depMissing = atOrAbove && aux.itDep !== undefined && !(Number(depCfg.MaxPercent) > 0);
            return (
                <>
                    {depMissing && (
                        <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-700 p-4 text-sm text-red-700 dark:text-red-300">
                            Please Configure Item Depreciation Percentage — this transfer cannot be verified until it is set up.
                        </div>
                    )}
                    <ItemsTransferItemsTable
                        items={items}
                        levels={d}
                        depOptions={itemsTransferDepOptions(depCfg.MaxPercent, r.FromCC, depCfg.CentralStoreCC)}
                        ext={ext}
                        setExt={setExt}
                    />
                    {remarks.length > 0 && (
                        <Block title="Remarks">
                            <SimpleTable heads={['Remarks']} rows={remarks.map((x) => [x.Remarks])} />
                        </Block>
                    )}
                </>
            );
        },
        beforeAction: (action, r, d, { aux, ext }) => {
            const items = Array.isArray(aux.itItems) ? aux.itItems : [];
            const present = Number(d.IndentPresentLevel);
            const define = Number(d.IndentDefineLevel);
            if (!items.length) return ['Invalid Submission'];
            if (!allItemsChecked(items, ext)) return ['Please Verify Transfer Items'];
            if (present < define) return [];
            if (!(Number((aux.itDep || {}).MaxPercent) > 0)) return ['Please Configure Item Depreciation Percentage'];
            // Legacy only looked at the last row; every asset row needs a value.
            const missing = String(action).toLowerCase() !== 'reject' && items.some((it) => !itemsTransferIsConsumable(it) && !itemsTransferDep(it, ext, present > define));
            return missing ? ['Please Select Depriciation Value'] : [];
        },
        approve: {
            route: 'Purchase/VerifyitemstrasnferBelowDefinelevel',
            payload: (r, d, { action, note, user, roleId, aux, ext }) => {
                const present = Number(d.IndentPresentLevel);
                const define = Number(d.IndentDefineLevel);
                const base = {
                    Refno: r.Refno, Appstatus: action, Remarks: note, Createdby: user, RoleID: String(roleId),
                    IndentPresentLevel: present, IndentDefineLevel: define,
                };
                if (present < define) return { ...base, Rowid: String(r.ItId), ItId: String(r.ItId) };
                const items = Array.isArray(aux.itItems) ? aux.itItems : [];
                const t = itemsTransferApprovalAtDefine(items, ext, present > define, action);
                return {
                    ...base, Rowid: t.rowIds, ItId: t.rowIds, DepAmounts: t.depAmounts, EffAmount: String(t.effAmount),
                    Depcsk: t.deps, Amount: String(t.amount), IndentNo: r.IndentNo, SumbeforeDep: String(t.sumBefore), SumAfterDep: String(t.sumAfter),
                };
            },
            ok: ['Submitted'],
        },
    },

    // Legacy /Purchase/VerifyItemsTransferIssue?CCType=PCC|NPCC (+ VerifyItemsTransferIssueGrid / VerifyItemsTransferIssueView /
    // ViewItemTransferissueDetailsGridView)
    ItemsTransferIssue: {
        title: 'Stock Transfer Issue Verification',
        successLabel: 'Stock Transfer Issue',
        noun: 'transfer issue',
        icon: Forklift,
        searchPlaceholder: 'Search by transfer no, cost center…',
        queue: {
            route: 'Purchase/VerifyItemsTransferIssueGrid',
            params: ({ roleId, userId, path, category }) => ({ Roleid: roleId, Created: String(userId), Userid: userId, CCType: itemsTransferCCType(path, category) }),
        },
        itemKey: (r) => r.ItId,
        card: {
            title: (r) => r.Refno,
            subtitle: (r) => `${r.FromCC || ''} → ${r.ToCC || ''}`,
            meta: (r) => r.Date,
        },
        searchText: (r) => `${r.Refno} ${r.FromCC} ${r.ToCC} ${r.Date}`,
        rowAux: [
            { name: 'itiItems', route: 'Purchase/GetItemstransferissueDetails', params: (r) => ({ Refno: r.Refno }) },
            { name: 'itiRemarks', route: 'Purchase/GetItemsTransferissueRemarks', params: (r) => ({ Refno: r.Refno }) },
        ],
        moid: (r) => r.MOID,
        remarksKey: (r) => r.Refno,
        // Legacy drops Return from this screen's status list; returned rows open the same view.
        showReturn: 'No',
        excludeActions: () => ['return'],
        header: {
            title: (r) => r.Refno,
            subtitle: (r) => `${r.FromCC || ''} → ${r.ToCC || ''}`,
            chips: (r) => [r.CCType, String(r.Status).trim() === '0' && 'Returned'],
        },
        sections: (r) => [{
            fields: [
                ['Ref No', r.Refno], ['Sending Cost Center', r.FromCC], ['Receiving Cost Center', r.ToCC],
                ['Date', r.Date], ['Expected Received Date', r.Expdate],
            ],
        }],
        extra: (r, d, { aux, ext, setExt }) => {
            const items = Array.isArray(aux.itiItems) ? aux.itiItems : [];
            const remarks = Array.isArray(aux.itiRemarks) ? aux.itiRemarks : [];
            return (
                <>
                    <CheckedItemsTable
                        title="Transfer Items"
                        items={items}
                        ext={ext}
                        setExt={setExt}
                        columns={[
                            { label: 'Item Code', render: (it) => it.ItemCode },
                            { label: 'Item Name', render: (it) => it.ItemName, align: 'left' },
                            { label: 'Specification', render: (it) => it.Specification, align: 'left' },
                            { label: 'DCA', render: (it) => it.DcaCode },
                            { label: 'Sub DCA', render: (it) => it.SubDcaCode },
                            { label: 'Units', render: (it) => it.Units },
                            { label: 'Issued Qty', render: (it) => it.IssQuantity, align: 'right' },
                            { label: 'Item Status', render: (it) => it.ItemStatus },
                        ]}
                    />
                    {remarks.length > 0 && (
                        <Block title="Remarks">
                            <SimpleTable heads={['Remarks']} rows={remarks.map((x) => [x.Remarks])} />
                        </Block>
                    )}
                </>
            );
        },
        beforeAction: (action, r, d, { aux, ext }) => {
            const items = Array.isArray(aux.itiItems) ? aux.itiItems : [];
            if (!items.length) return ['Invalid Submission'];
            return allItemsChecked(items, ext) ? [] : ['Please Verify Transfer Items'];
        },
        approve: {
            route: 'Purchase/ApproveItemsTransferIssue',
            payload: (r, d, { action, note, user, roleId }) => ({
                Refno: r.Refno, Appstatus: action, Remarks: note, Createdby: user, RoleID: String(roleId),
                Rowid: String(r.ItId), ItId: String(r.ItId),
            }),
            ok: ['Submitted'],
        },
    },

    // Legacy /Purchase/VerifyCapitalStockIssue (+ VerifyCapitalStockIssueGrid / VerifyCapitalStockIssueView /
    // ViewCapitalIssueDetailsGridView) — stock conversion into a capital (master) item
    CapitalStockIssue: {
        title: 'Stock Conversion Verification',
        successLabel: 'Stock Conversion',
        noun: 'stock conversion',
        icon: Combine,
        searchPlaceholder: 'Search by transaction, cost center, master item…',
        queue: { route: 'Purchase/VerifyCapitalStockIssueGrid', params: ({ roleId, userId }) => ({ Roleid: roleId, Created: String(userId), Userid: userId }) },
        itemKey: (r) => r.Rid || r.Tranno,
        card: {
            title: (r) => r.Tranno,
            subtitle: (r) => [r.MasterItemCode, r.ItemName].filter(Boolean).join(' - '),
            meta: (r) => [r.CostCenter, r.Date].filter(Boolean).join(' · '),
        },
        searchText: (r) => `${r.Tranno} ${r.CostCenter} ${r.MasterItemCode} ${r.ItemName} ${r.Date}`,
        rowAux: [
            { name: 'csiItems', route: 'Purchase/GetCapitalIssueDetails', params: (r) => ({ TranNo: r.Tranno }) },
        ],
        moid: (r) => r.MOID,
        remarksKey: (r) => r.Tranno,
        // Legacy drops Return from this screen's status list.
        showReturn: 'No',
        excludeActions: () => ['return'],
        header: {
            title: (r) => r.Tranno,
            subtitle: (r) => [r.MasterItemCode, r.ItemName].filter(Boolean).join(' - '),
            chips: (r) => [r.CostCenter, r.Date],
        },
        sections: (r) => [{
            fields: [
                ['Transaction No', r.Tranno], ['Cost Center', r.CostCenter], ['Date', r.Date],
                ['Master Item Code', r.MasterItemCode], ['Master Item Name', r.ItemName],
            ],
        }],
        extra: (r, d, { aux, ext, setExt }) => {
            const items = Array.isArray(aux.csiItems) ? aux.csiItems : [];
            return (
                <CheckedItemsTable
                    title="Issued Items"
                    items={items}
                    ext={ext}
                    setExt={setExt}
                    columns={[
                        { label: 'Item Code', render: (it) => it.ItemCode },
                        { label: 'Item Name', render: (it) => it.ItemName, align: 'left' },
                        { label: 'Specification', render: (it) => it.Specification, align: 'left' },
                        { label: 'Units', render: (it) => it.Units },
                        { label: 'Basic', render: (it) => fmt(it.Basic), align: 'right' },
                        { label: 'Issue Quantity', render: (it) => it.IssueQty, align: 'right' },
                        { label: 'Transfer Quantity', render: (it) => it.TransferQty, align: 'right' },
                    ]}
                />
            );
        },
        beforeAction: (action, r, d, { aux, ext }) => {
            const items = Array.isArray(aux.csiItems) ? aux.csiItems : [];
            if (!items.length) return ['Invalid Submission'];
            return allItemsChecked(items, ext) ? [] : ['Please Verify Capital Issue Items'];
        },
        approve: {
            route: 'Purchase/ApproveCapitalStockIssue',
            // The API reads the action from Status (legacy controller: IC.Status = appstatus).
            payload: (r, d, { action, note, user, roleId }) => ({
                Tranno: r.Tranno, Status: action, Remarks: note, Createdby: user, RoleID: String(roleId),
            }),
            ok: ['Submitted'],
        },
    },

    // Legacy /Purchase/VerifyDirectStockUpdation (+ VerifyDirectStockUpdationGrid / VerifyDirectStockUpdationView /
    // ViewDirectStockUpdationDetailsGridview)
    DirectStockUpdation: {
        title: 'Direct Stock Updation Verification',
        successLabel: 'Direct Stock Updation',
        noun: 'stock updation',
        icon: PackagePlus,
        searchPlaceholder: 'Search by request no, cost center, date…',
        queue: { route: 'Purchase/VerifyDirectStockUpdationGrid', params: ({ roleId, userId }) => ({ Roleid: roleId, Created: String(userId), Userid: userId }) },
        itemKey: (r) => r.DirectStockUpdationId || r.RequestNo,
        card: {
            title: (r) => r.RequestNo,
            subtitle: (r) => r.RequestCC,
            meta: (r) => r.RequestDate,
            amount: (r) => money(r.Amount),
        },
        searchText: (r) => `${r.RequestNo} ${r.RequestCC} ${r.RequestDate} ${r.Amount}`,
        rowAux: [
            { name: 'dsuItems', route: 'Purchase/GetDirectStockDataDetails', params: (r) => ({ Requestno: r.RequestNo }) },
            { name: 'dsuRemarks', route: 'Purchase/GetDirectStockRemarks', params: (r) => ({ Requestno: r.RequestNo }) },
        ],
        moid: (r) => r.MOID,
        remarksKey: (r) => r.RequestNo,
        // Legacy drops Return from this screen's status list; returned rows open the same view.
        showReturn: 'No',
        excludeActions: () => ['return'],
        header: {
            title: (r) => r.RequestNo,
            subtitle: (r) => money(r.Amount),
            chips: (r) => [r.RequestCC, r.RequestDate, String(r.Status).trim() === '0' && 'Returned'],
        },
        sections: (r) => [{
            fields: [['Request No', r.RequestNo], ['Date', r.RequestDate], ['Cost Center', r.RequestCC], ['Amount', money(r.Amount)]],
        }],
        extra: (r, d, { aux, ext, setExt }) => {
            const items = Array.isArray(aux.dsuItems) ? aux.dsuItems : [];
            const remarks = Array.isArray(aux.dsuRemarks) ? aux.dsuRemarks : [];
            const total = items[0]?.TotalAmount ?? items.reduce((a, it) => a + (Number(it.Amount) || 0), 0);
            return (
                <>
                    <CheckedItemsTable
                        title="Items"
                        items={items}
                        ext={ext}
                        setExt={setExt}
                        columns={[
                            { label: 'Item Code', render: (it) => it.ItemCode },
                            { label: 'Item Name', render: (it) => it.ItemName, align: 'left' },
                            { label: 'Specification', render: (it) => it.Specification, align: 'left' },
                            { label: 'DCA', render: (it) => it.DcaCode },
                            { label: 'Sub DCA', render: (it) => it.SubDcaCode },
                            { label: 'Basic Price', render: (it) => fmt(it.BasicPrice), align: 'right' },
                            { label: 'Units', render: (it) => it.Units },
                            { label: 'Requested Qty', render: (it) => it.Quantity, align: 'right' },
                            { label: 'Amount', render: (it) => fmt(it.Amount), align: 'right' },
                        ]}
                        footer={items.length ? (
                            <>
                                <td colSpan={9} className="px-3 py-2 text-xs font-bold text-right text-gray-700 dark:text-gray-300">Sub Total</td>
                                <td className="px-3 py-2 text-xs font-bold text-right text-emerald-600">{fmt(total)}</td>
                            </>
                        ) : null}
                    />
                    {remarks.length > 0 && (
                        <Block title="Remarks">
                            <SimpleTable heads={['Remarks']} rows={remarks.map((x) => [x.Remarks])} />
                        </Block>
                    )}
                </>
            );
        },
        // Legacy only checks the ticks here (no empty-grid guard).
        beforeAction: (action, r, d, { aux, ext }) => {
            const items = Array.isArray(aux.dsuItems) ? aux.dsuItems : [];
            return !items.length || allItemsChecked(items, ext) ? [] : ['Please Verify Item Codes'];
        },
        approve: {
            route: 'Purchase/ApproveDirectStockUpdation',
            payload: (r, d, { action, note, user, roleId }) => ({
                RequestNo: r.RequestNo, Appstatus: action, Remarks: note, Createdby: user, RoleID: String(roleId),
            }),
            ok: ['Submitted'],
        },
    },

    // Legacy /Purchase/VerifyStoreClosing (+ VerifyStoreClosingGrid / VerifyStoreClosingView). ClosingTypeid:
    // 1 Store Closing, 2 Store Suspend, 3 Store Closing Reschedule, 4 Store Suspend Reschedule.
    StoreClosing: {
        title: 'Store Closing Verification',
        successLabel: 'Store Closing',
        noun: 'store closing',
        icon: Store,
        searchPlaceholder: 'Search by cost center, type, date…',
        queue: { route: 'Purchase/VerifyStoreClosingGrid', params: ({ roleId, userId }) => ({ Roleid: roleId, Created: String(userId), Userid: userId }) },
        itemKey: (r) => `${r.IId}|${r.Tranno}`,
        card: {
            title: (r) => r.CCCode,
            subtitle: (r) => r.ClosingType,
            meta: (r) => r.ClosingDate,
        },
        searchText: (r) => `${r.CCCode} ${r.ClosingType} ${r.ClosingDate} ${r.Tranno}`,
        detail: {
            route: 'Purchase/VerifyStoreClosingView',
            params: (r) => ({ Tranno: r.Tranno, Rid: r.IId, Typeid: r.ClosingTypeid }),
            select: (data) => (Array.isArray(data) ? data[0] || null : data),
        },
        // Same pending checks the Store Closing entry screen runs (legacy /Reports/ViewStorePendingDetails).
        rowAux: [
            { name: 'scPendings', route: 'Accounts/GetCCStorePendings', params: (r) => ({ CCCode: r.CCCode, Type: 'Store' }) },
            { name: 'scBalance', route: 'Accounts/GetCCBalanceStock', params: (r) => ({ CCCode: r.CCCode }) },
        ],
        moid: (r, d) => d.SMOID || r.MOID,
        remarksKey: (r) => r.Tranno,
        // Legacy drops Return from this screen's status list.
        showReturn: 'No',
        excludeActions: () => ['return'],
        header: {
            title: (r, d) => `${d.SCCCode || r.CCCode}${d.SCCName ? ` - ${d.SCCName}` : ''}`,
            subtitle: (r, d) => d.SCCClosingType || r.ClosingType,
            chips: (r) => [r.ClosingDate],
        },
        sections: (r, d) => [{
            fields: [['Closing Type', d.SCCClosingType], ['Cost Center', d.SCCCode], ['Cost Center Name', d.SCCName]],
        }],
        extra: (r, d, { aux, ext, setExt }) => {
            const pendings = Array.isArray(aux.scPendings) ? aux.scPendings : [];
            const balance = Array.isArray(aux.scBalance) ? aux.scBalance : [];
            return (
                <>
                    <StoreCloseEditor d={d} ext={ext} setExt={setExt} />
                    {pendings.length > 0 && (
                        <Block title="Pending Approvals">
                            <SimpleTable
                                heads={['Master Code', 'Pending With', 'Details']}
                                rows={pendings.map((p) => [p.MasterCode, p.PendingRoleName || p.RoleName, p.PendingDetails])}
                            />
                        </Block>
                    )}
                    {balance.length > 0 && (
                        <Block title="Balance Stock">
                            <SimpleTable
                                heads={['Item Code', 'Item Name', 'Specification', 'Quantity']}
                                rows={balance.map((b) => [b.ItemCode, b.ItemName, b.Specification, b.Quantity])}
                                alignRight={[3]}
                            />
                        </Block>
                    )}
                </>
            );
        },
        beforeAction: (action, r, d, { ext }) => {
            const v = storeCloseValues(d, ext);
            const type = String(d.SClosingTypeid);
            const errors = [];
            if ((type === '1' || type === '3') && !v.closingDate) errors.push('Select Closing Date');
            if (type === '2' || type === '4') {
                if (!v.startDate) errors.push('Select Start Date');
                if (!v.endDate) errors.push('Select End Date');
                if (v.startDate && v.endDate && v.endDate < v.startDate) errors.push('End Date cannot be before Start Date');
            }
            if (!v.alertNote.trim()) errors.push('Enter Alert Note');
            return errors;
        },
        approve: {
            route: 'Purchase/ApproveStoreClose',
            payload: (r, d, { action, note, user, roleId, userId, ext }) => {
                const v = storeCloseValues(d, ext);
                const type = String(d.SClosingTypeid);
                return {
                    STranno: d.STranno || r.Tranno, SId: d.SId || r.IId, Appstatus: action, SRemarks: note,
                    SCCClosingDate: type === '1' || type === '3' ? displayDate(v.closingDate) : '',
                    SAlertnote: v.alertNote.trim().toUpperCase(),
                    SCCStartDate: type === '2' || type === '4' ? displayDate(v.startDate) : '',
                    SCCEndDate: type === '2' || type === '4' ? displayDate(v.endDate) : '',
                    SClosingTypeid: type, SCreatedby: user, SRoleID: String(roleId), SUserId: String(userId),
                };
            },
            ok: ['Submitted'],
        },
    },

    // Legacy /Purchase/VerifyCCStockClose (+ VerifyCCStockCloseGrid / VerifyCCStockCloseView)
    CCStockClose: {
        title: 'CC Stock Close Verification',
        successLabel: 'CC Stock Close',
        noun: 'stock close',
        icon: Archive,
        searchPlaceholder: 'Search by cost center, transaction type, dates…',
        queue: { route: 'Purchase/GetVerifyCCStockClose', params: ({ roleId, userId }) => ({ UserId: userId, Roleid: roleId }) },
        itemKey: (r) => r.Transactionrefno,
        card: {
            title: (r) => r.CCName,
            subtitle: (r) => r.TransactionType,
            meta: (r) => [r.FromDate, r.ToDate].filter(Boolean).join(' → '),
            amount: (r) => money(r.Amount),
        },
        searchText: (r) => `${r.Transactionrefno} ${r.CCName} ${r.TransactionType} ${r.FromDate} ${r.ToDate} ${r.Amount}`,
        detail: { route: 'Purchase/GetCCStockClosebyNo', params: (r) => ({ TransNo: r.Transactionrefno }) },
        moid: (r, d) => d.MOID || r.MOID,
        remarksKey: (r) => r.Transactionrefno,
        // Legacy drops Return from this screen's status list.
        showReturn: 'No',
        excludeActions: () => ['return'],
        header: {
            title: (r, d) => d.CCName || r.CCName,
            subtitle: (r, d) => money(d.ClosingAmount),
            chips: (r, d) => [d.TransactionType, [d.FromDate, d.ToDate].filter(Boolean).join(' → ')],
        },
        sections: (r, d) => [{
            fields: [
                ['Cost Center', d.CCName], ['From Date', d.FromDate], ['To Date', d.ToDate],
                ['Stock Amount', money(d.StockAmount)], ['Last Closing Amount', money(d.LastClosingAmount)], ['Current Value', money(d.CurrentValue)],
                ['Transaction Type', d.TransactionType], ['Amount', money(d.Amount)], ['Closing Amount', money(d.ClosingAmount)],
            ],
        }],
        approve: {
            route: 'Purchase/ApproveCCStockClose',
            payload: (r, d, { action, note, user, roleId }) => ({
                Transactionrefno: d.Transactionrefno || r.Transactionrefno, CCCode: d.CCCode, Action: action, Remarks: note,
                Amount: d.Amount, Roleid: roleId, CreatedBy: user,
            }),
            ok: ['Submited', 'Submitted'],
        },
    },

    // Legacy /Purchase/VerifyAssetSale (+ VerifyAssetSaleGrid / VerifyAssetSaleView / ViewAssetSaleDetailsGridview)
    AssetSale: {
        title: 'Asset Sale Verification',
        successLabel: 'Asset Sale',
        noun: 'asset sale',
        icon: HandCoins,
        searchPlaceholder: 'Search by request no, item code, date…',
        queue: { route: 'Purchase/VerifyAssetSaleGrid', params: ({ roleId, userId }) => ({ Roleid: roleId, Created: String(userId), Userid: userId }) },
        itemKey: (r) => `${r.RequestNo}|${r.ItemId}`,
        card: {
            title: (r) => r.RequestNo,
            subtitle: (r) => r.ItemCode,
            meta: (r) => r.RequestDate,
            amount: (r) => money(r.Amount),
        },
        searchText: (r) => `${r.RequestNo} ${r.ItemCode} ${r.RequestDate} ${r.Amount}`,
        detail: {
            route: 'Purchase/GetAssetSaleDetails',
            params: (r) => ({ Requestno: r.RequestNo, Rid: r.ItemId }),
            select: (data) => (Array.isArray(data) ? data[0] || null : data),
        },
        rowAux: [
            { name: 'asItems', route: 'Purchase/GetAssetSaleDataDetails', params: (r) => ({ Requestno: r.RequestNo, Rid: r.ItemId }) },
        ],
        moid: (r, d) => d.MOID || r.MOID,
        remarksKey: (r) => r.RequestNo,
        // Legacy drops Return from this screen's status list.
        showReturn: 'No',
        excludeActions: () => ['return'],
        header: {
            title: (r) => r.RequestNo,
            subtitle: (r, d) => money(d.SellAmount),
            chips: (r) => [r.ItemCode, r.RequestDate],
        },
        sections: (r, d) => [{
            fields: [
                ['Date', d.SubmitDate], ['Book Value Date', d.BookValueDate], ['Amount', money(d.VAmount)],
                ['Asset Selling Amount', money(d.SellAmount)], ['Profit on Sale of Asset', money(d.Profit)], ['Loss on Sale of Asset', money(d.Loss)],
                ['Name of the Buyer', d.PartyName], ['Address of the Buyer', d.PartyAddress, true],
            ],
        }],
        extra: (r, d, { aux, ext, setExt }) => {
            const items = Array.isArray(aux.asItems) ? aux.asItems : [];
            return (
                <CheckedItemsTable
                    title="Items"
                    items={items}
                    ext={ext}
                    setExt={setExt}
                    columns={[
                        { label: 'Item Code', render: (it) => it.ItemCode },
                        { label: 'Item Name', render: (it) => it.ItemName, align: 'left' },
                        { label: 'Specification', render: (it) => it.Specification, align: 'left' },
                        { label: 'Basic Price', render: (it) => fmt(it.BasicPrice), align: 'right' },
                        { label: 'Units', render: (it) => it.Units },
                        { label: 'Amount', render: (it) => fmt(it.Amount), align: 'right' },
                    ]}
                />
            );
        },
        // Legacy only checks the ticks here (no empty-grid guard).
        beforeAction: (action, r, d, { aux, ext }) => {
            const items = Array.isArray(aux.asItems) ? aux.asItems : [];
            return !items.length || allItemsChecked(items, ext) ? [] : ['Please Verify Item Code'];
        },
        approve: {
            route: 'Purchase/ApproveAssetSale',
            payload: (r, d, { action, note, user, roleId, userId }) => ({
                RequestNo: r.RequestNo, ItemId: String(r.ItemId), Appstatus: action, Remarks: note,
                Createdby: user, RoleID: String(roleId), UserId: String(userId),
            }),
            ok: ['Submitted'],
        },
    },

    // Legacy /Purchase/VerifyAssetSaleReceipt (+ VerifyAssetSaleReceiptGrid / VerifyAssetSaleReceiptView)
    AssetSaleReceipt: {
        title: 'Asset Sale Receipt Verification',
        successLabel: 'Asset Sale Receipt',
        noun: 'receipt',
        icon: ReceiptText,
        searchPlaceholder: 'Search by transaction, request no, item code…',
        queue: { route: 'Purchase/VerifyAssetSaleReceiptGrid', params: ({ roleId, userId }) => ({ Roleid: roleId, Created: String(userId), Userid: userId }) },
        itemKey: (r) => `${r.Refno}|${r.IId}`,
        card: {
            title: (r) => r.Refno,
            subtitle: (r) => [r.Requestno && `Request ${r.Requestno}`, r.ItemCode].filter(Boolean).join(' · '),
            meta: (r) => r.RecieptDate,
            amount: (r) => money(r.SellingAmount),
        },
        searchText: (r) => `${r.Refno} ${r.Requestno} ${r.ItemCode} ${r.RecieptDate} ${r.SellingAmount}`,
        detail: {
            route: 'Purchase/VerifyAssetSaleReceiptView',
            params: (r) => ({ Refno: r.Refno, Rid: r.IId }),
            select: (data) => (Array.isArray(data) ? data[0] || null : data),
        },
        moid: (r, d) => d.MOID || r.MOID,
        remarksKey: (r) => r.Refno,
        // Legacy drops Return from this screen's status list.
        showReturn: 'No',
        excludeActions: () => ['return'],
        header: {
            title: (r) => r.Refno,
            subtitle: (r, d) => money(d.BankAmount ?? r.SellingAmount),
            chips: (r) => [r.Requestno && `Request ${r.Requestno}`, r.RecieptDate],
        },
        sections: (r, d) => [{
            title: 'Asset',
            fields: [
                ['Item Code', d.ItemCode], ['Item Name', d.ItemName], ['Specification', d.Specification],
                ['Buyer Name', d.Name], ['Buyer Address', d.Address, true],
            ],
        }, {
            title: 'Payment Details',
            fields: [
                ['Bank Name', d.BankName], ['Payment Date', d.BankDate], ['Mode of Pay', d.Modeofpay],
                ['No', d.BankNo], ['Amount', money(d.BankAmount)], ['Remarks', d.BankRemarks, true],
            ],
        }],
        approve: {
            route: 'Purchase/ApproveAssetSaleReceipt',
            payload: (r, d, { action, note, user, roleId, userId }) => ({
                Refno: r.Refno, ItemId: String(d.IId ?? r.IId), Appstatus: action, Remarks: note,
                Createdby: user, RoleID: String(roleId), UserId: String(userId),
            }),
            ok: ['Submitted'],
        },
    },

    // Legacy /Purchase/VerifyHSNCreation (+ VerifyHSNCreationGrid / VerifyhsnView; returned rows → UpdateHSNCreationView)
    HSNCreation: {
        title: 'HSN/SAC Code Verification',
        successLabel: 'HSN/SAC Code',
        noun: 'HSN/SAC code',
        icon: Barcode,
        searchPlaceholder: 'Search by code, type, category…',
        queue: { route: 'Purchase/Gethsndetails', params: ({ roleId }) => ({ Roleid: roleId }) },
        itemKey: (r) => r.Rowid,
        card: {
            title: (r) => r.HSNCode,
            subtitle: (r) => r.HSNCategory,
            meta: (r) => [r.CodeType, r.Category].filter(Boolean).join(' · '),
        },
        searchText: (r) => `${r.HSNCode} ${r.CodeType} ${r.HSNCategory} ${r.Category}`,
        detail: {
            route: 'Purchase/GethsnverificationbyId',
            params: (r) => ({ Rowid: r.Rowid }),
            select: (data) => (Array.isArray(data) ? data[0] || null : data),
        },
        moid: (r, d) => d.MOID || r.MOID,
        remarksKey: (r) => r.Rowid,
        // Legacy keeps the full status list here, Return included.
        showReturn: 'Yes',
        isReturned: (r) => String(r.Status).trim() === '0',
        header: {
            title: (r, d) => d.HSNCode || r.HSNCode,
            subtitle: (r, d) => d.HSNCategory,
            chips: (r, d) => [d.CodeType || r.CodeType, d.Category],
        },
        sections: (r, d) => [{
            fields: [
                ['Type Category', d.Category], ['HSN Category', d.HSNCategory], ['HSN/SAC Code', d.HSNCode],
                ['CGST Rate', d.Cgstrate], ['SGST Rate', d.Sgstrate], ['IGST Rate', d.Igstrate],
                ['Remarks', d.Remarks, true],
                String(r.Status).trim() === '0' && d.ReturnRemarks && ['Return Remarks', d.ReturnRemarks, true],
            ],
        }],
        approve: {
            route: 'Purchase/Verifyhsn',
            payload: (r, d, { action, note, user, roleId }) => ({
                Rowid: String(r.Rowid), Appstatus: action, Remarks: note, Createdby: user, RoleID: String(roleId),
            }),
            ok: ['Submitted'],
        },
        // Legacy UpdateHSNData → spUpdateHSNDetails: only the GST rates and remarks can change.
        resubmit: {
            fields: [
                { key: 'Cgstrate', label: 'CGST Rate', required: true, filter: /^\d*\.?\d?$/ },
                { key: 'Sgstrate', label: 'SGST Rate', required: true, filter: /^\d*\.?\d?$/ },
                { key: 'Igstrate', label: 'IGST Rate', required: true, filter: /^\d*\.?\d?$/ },
                { key: 'Remarks', label: 'Remarks', type: 'textarea', required: true },
            ],
            initial: (r, d) => ({
                Cgstrate: d.Cgstrate != null ? String(d.Cgstrate) : '', Sgstrate: d.Sgstrate != null ? String(d.Sgstrate) : '',
                Igstrate: d.Igstrate != null ? String(d.Igstrate) : '', Remarks: d.Remarks || '',
            }),
            validate: (v) => [
                !(Number(v.Cgstrate) > 0) && 'Please Enter CGST',
                !(Number(v.Sgstrate) > 0) && 'Please Enter SGST',
                !(Number(v.Igstrate) > 0) && 'Please Enter IGST',
            ].filter(Boolean),
            route: 'Purchase/UpdateHSNData',
            payload: (r, d, v, { user, roleId }) => ({
                Cgstrate: v.Cgstrate, Sgstrate: v.Sgstrate, Igstrate: v.Igstrate, Remarks: v.Remarks.trim(),
                Rowid: String(r.Rowid), RoleID: String(roleId), Createdby: user,
            }),
            ok: ['Successfull'],
        },
    },

    // Legacy /Purchase/VerifyCreditandDebitNote (+ VerifyCreditandDebitNoteGrid / VerifyCreditandDebitNoteView)
    CreditDebitNote: {
        title: 'Credit & Debit Note Verification',
        successLabel: 'Credit/Debit Note',
        noun: 'note',
        icon: FileDiff,
        searchPlaceholder: 'Search by note no, type, client/vendor…',
        queue: { route: 'Purchase/VerifyCreditandDebitNoteGrid', params: ({ roleId, userId }) => ({ Roleid: roleId, Created: String(userId), Userid: userId }) },
        itemKey: (r) => `${r.NoteNo}|${r.Rid}`,
        card: {
            title: (r) => r.NoteNo,
            subtitle: (r) => [r.NoteType, r.NoteFor].filter(Boolean).join(' · '),
            meta: (r) => r.Date,
            amount: (r) => money(r.Amount),
        },
        searchText: (r) => `${r.NoteNo} ${r.NoteType} ${r.NoteFor} ${r.Date} ${r.Amount}`,
        detail: { route: 'Purchase/VerifyCreditandDebitNoteView', params: (r) => ({ Noteno: r.NoteNo, Rid: r.Rid, NoteFor: r.NoteFor }) },
        moid: (r) => r.MOID,
        remarksKey: (r) => r.NoteNo,
        // Legacy drops Return from this screen's status list.
        showReturn: 'No',
        excludeActions: () => ['return'],
        header: {
            title: (r) => r.NoteNo,
            subtitle: (r, d) => money(d.Amount ?? r.Amount),
            chips: (r) => [r.NoteType, r.NoteFor, r.Date],
        },
        sections: (r, d) => {
            if (r.NoteFor === 'Client') {
                return [{
                    title: 'Reason',
                    fields: [
                        d.ClientReason != null && ['Reason for Credit/Debit Note', d.ClientReason],
                        d.Clientpaymentfor != null && ['Payment for', d.Clientpaymentfor],
                        d.ClientReasonDCA != null && ['DCA', d.ClientReasonDCA],
                        d.ClientReasonDCA != null && ['SDCA', d.ClientReasonSDCA],
                    ],
                }, {
                    title: 'Client Invoice',
                    fields: [
                        ['Client', d.Client], ['Sub Client', d.SubClient], ['Client Invoice', d.ClientInv], ['Client PoNo', d.CPONO],
                        ['Client Invoice Date', d.CInvDate], ['Client Invoice Making Date', d.CInvMakingDate], ['Client RA No', d.CRANo],
                        ['Client CCCode', d.CCCCode], ['Client Basic Value', money(d.CBasic)], ['GST Value', money(d.GSTValue)], ['Client Total', money(d.CTotal)],
                    ],
                }, {
                    title: 'Note',
                    fields: [
                        ['Note Amount', money(d.BBasicAmount)],
                        String(d.GSTCount) === '2' && ['CGST Value', money(d.BCGST)],
                        String(d.GSTCount) === '2' && ['SGST Value', money(d.BSGST)],
                        String(d.GSTCount) === '1' && ['IGST Value', money(d.BIGST)],
                        ['Total', money(d.Amount)],
                    ],
                }];
            }
            return [{
                title: 'Reason',
                fields: [
                    d.VendorReason != null && ['Reason for Credit/Debit Note', d.VendorReason],
                    d.Vendorpaymentfor != null && ['Payment for', d.Vendorpaymentfor],
                    d.VendorReasonDCA != null && ['DCA', d.VendorReasonDCA],
                    d.VendorReasonDCA != null && ['SDCA', d.VendorReasonSDCA],
                ],
            }, {
                title: 'Vendor Invoice',
                fields: [
                    ['Vendor Name', d.VendorName], ['Vendor Invoice', d.VendorInv], ['Vendor PoNo', d.VPONO],
                    ['Invoice Date', d.VInvDate], ['Invoice Making Date', d.VInvMakingDate], ['DCA Code', d.VDCA], ['Sub Dca Code', d.Vsubdca],
                    ['IT Code', d.VItCode], ['Amount', money(d.VBasic)], ['GST Value', money(d.VGstValue)], ['Total', money(d.VTotal)],
                ],
            }, {
                title: 'Note',
                fields: [
                    ['Note Value', money(d.BBasicAmount)],
                    String(d.VGSTCount) === '2' && ['CGST Value', money(d.BCGST)],
                    String(d.VGSTCount) === '2' && ['SGST Value', money(d.BSGST)],
                    String(d.VGSTCount) === '1' && ['IGST Value', money(d.BIGST)],
                    ['Total', money(d.Amount)],
                ],
            }];
        },
        approve: {
            route: 'Purchase/ApproveCreditandDebitNote',
            // The API reads the action from Status (legacy controller: IC.Status = appstatus).
            payload: (r, d, { action, note, user, roleId }) => ({
                Rid: String(r.Rid), NoteNo: r.NoteNo, Status: action, Remarks: note, Createdby: user, RoleID: String(roleId),
            }),
            ok: ['Submitted'],
        },
    },

    // Legacy /Purchase/VerifyTDSPayment (+ VerifyTDSPaymentGrid / VerifyTDSPaymentView)
    TDSPayment: {
        title: 'Vendor TDS Payment Verification',
        successLabel: 'TDS Payment',
        noun: 'payment',
        icon: BadgePercent,
        searchPlaceholder: 'Search by transaction, date, amount…',
        queue: { route: 'Purchase/GetVerificationTDSPayment', params: ({ roleId }) => ({ Roleid: roleId }) },
        itemKey: (r) => r.TransactionRefNo,
        card: {
            title: (r) => `Txn ${r.TransactionRefNo}`,
            subtitle: (r) => r.CCName || r.CostCenter,
            meta: (r) => r.TransactionDate,
            amount: (r) => money(r.TransactionAmount),
        },
        searchText: (r) => `${r.TransactionRefNo} ${r.CCName} ${r.CostCenter} ${r.TransactionDate} ${r.TransactionAmount}`,
        detail: { route: 'Purchase/GetTDSPaymentbyNo', params: (r) => ({ Transactionno: r.TransactionRefNo }) },
        moid: (r, d) => (d.TransactionData || {}).MOID,
        remarksKey: (r) => r.TransactionRefNo,
        // Legacy drops Return from this screen's status list.
        showReturn: 'No',
        excludeActions: () => ['return'],
        header: {
            title: (r) => `Txn ${r.TransactionRefNo}`,
            subtitle: (r, d) => money((d.TransactionData || {}).TransactionAmount),
            chips: (r, d) => [(d.TransactionData || {}).ModeOfPay, (d.TransactionData || {}).TransactionDate],
        },
        sections: (r, d) => {
            const t = d.TransactionData || {};
            return [{
                title: 'Payment',
                fields: [
                    ['Bank Name', t.Name], ['Transaction Number', t.Number], ['Transaction Date', t.TransactionDate],
                    ['Transaction Amount', money(t.TransactionAmount)], ['Amount in Words', t.AmountInWords, true],
                ],
            }];
        },
        extra: (r, d) => {
            const rows = d.TaxDeductionData || [];
            // TransactionData.Remarks carries "Role:Employee:Remarks||…" — the first entry is the creator.
            const approvals = String((d.TransactionData || {}).Remarks || '').split('||').filter(Boolean).map((t, i) => {
                const [role, emp, ...rest] = t.split(':');
                return [i === 0 ? 'Created By' : 'Verified By', role, emp, rest.join(':')];
            });
            return (
                <>
                    <Block title="TDS Deductions">
                        <SimpleTable
                            heads={['Date of Payment/Credit', 'Invoice Date', 'Invoice No', 'Vendor Name', 'Cost Center', 'IT Code', 'Basic Amount', 'Total TDS', 'TDS Balance', 'Paid Amount']}
                            rows={rows.map((x) => [x.InoviceMakingDates, x.InoviceDates, x.Inovicenos, x.Name, x.CostCenter, x.ITCode,
                                fmt(x.InvBasicValue), fmt(x.DeductionAmount), fmt(x.DeductionBalance), fmt(x.Amounts)])}
                            alignRight={[6, 7, 8, 9]}
                        />
                    </Block>
                    {approvals.length > 0 && (
                        <Block title="Approvals">
                            <SimpleTable heads={['Action', 'Role Name', 'Employee', 'Remarks']} rows={approvals} />
                        </Block>
                    )}
                </>
            );
        },
        approve: {
            route: 'Purchase/ApproveTDSPayment',
            payload: (r, d, { action, note, user, roleId }) => ({
                TransactionRefNo: r.TransactionRefNo, Remarks: note, Action: action, RoleId: roleId, Createdby: user,
            }),
            ok: ['Submited', 'Submitted'],
        },
    },

    // Legacy /Purchase/VerifyVendorPayableWriteoff (+ VerifyVendorPayableWriteoffGrid / VerifyVendorPayableWriteoffView /
    // ViewVendorPayablewoDetailsGridView; invoice → tax deductions popup via GetVendorInvoiceTaxDeductionWOGridVerify)
    VendorPayableWriteoff: {
        title: 'Vendor Payable Write-off Verification',
        successLabel: 'Vendor Payable Write-off',
        noun: 'write-off',
        icon: Eraser,
        searchPlaceholder: 'Search by transaction, vendor…',
        queue: { route: 'Purchase/VerifyVendorPayableWriteoffGrid', params: ({ roleId, userId }) => ({ Roleid: roleId, Created: String(userId), Userid: userId }) },
        itemKey: (r) => r.Tranno,
        card: {
            title: (r) => r.Tranno,
            subtitle: (r) => [r.VendorCode, r.VendorName].filter(Boolean).join(' - '),
            meta: (r) => r.Date,
            amount: (r) => money(r.Amount),
        },
        searchText: (r) => `${r.Tranno} ${r.VendorCode} ${r.VendorName} ${r.Date} ${r.Amount}`,
        rowAux: [
            { name: 'vwoInvoices', route: 'Purchase/ViewVendorPayablewoDetailsGridView', params: (r) => ({ TranNo: r.Tranno }) },
        ],
        moid: (r) => r.MOID,
        remarksKey: (r) => r.Tranno,
        // Legacy drops Return from this screen's status list.
        showReturn: 'No',
        excludeActions: () => ['return'],
        header: {
            title: (r) => r.Tranno,
            subtitle: (r) => money(r.Amount),
            chips: (r) => [r.VendorName, r.Date],
        },
        sections: (r) => [{
            fields: [['Transaction No', r.Tranno], ['Date', r.Date], ['Vendor Code', r.VendorCode], ['Vendor Name', r.VendorName], ['Amount', money(r.Amount)]],
        }],
        extra: (r, d, { aux, ext, setExt }) => {
            const items = Array.isArray(aux.vwoInvoices) ? aux.vwoInvoices : [];
            const total = items.reduce((a, it) => a + (Number(it.TotalBalance) || 0), 0);
            const invoiceCell = (it) => (it.TaxApplicable === 'Yes' ? (
                <button type="button" onClick={() => setExt((p) => ({ ...p, taxInvoice: it.InvoiceNo }))}
                    className="text-indigo-600 dark:text-indigo-400 font-semibold hover:underline">{it.InvoiceNo}</button>
            ) : it.InvoiceNo);
            return (
                <>
                    <CheckedItemsTable
                        title="Write-off Invoices"
                        items={items}
                        ext={ext}
                        setExt={setExt}
                        columns={[
                            { label: 'Cost Center', render: (it) => it.CCCode },
                            { label: 'Invoice No', render: invoiceCell },
                            { label: 'PO No', render: (it) => it.PONumber },
                            { label: 'Basic Balance', render: (it) => fmt(it.InvoiceBalance), align: 'right' },
                            { label: 'Hold Balance', render: (it) => fmt(it.HoldBalance), align: 'right' },
                            { label: 'Retention Balance', render: (it) => fmt(it.RetentionBalance), align: 'right' },
                            { label: 'Total Balance', render: (it) => fmt(it.TotalBalance), align: 'right' },
                        ]}
                        footer={items.length ? (
                            <>
                                <td colSpan={7} className="px-3 py-2 text-xs font-bold text-right text-gray-700 dark:text-gray-300">Total</td>
                                <td className="px-3 py-2 text-xs font-bold text-right text-emerald-600">{fmt(total)}</td>
                            </>
                        ) : null}
                    />
                    {ext.taxInvoice && (
                        <RouteTableModal
                            title={`Invoice ${ext.taxInvoice} — Tax Deductions`}
                            route="Purchase/GetVendorInvoiceTaxDeductionWOGridVerify"
                            params={{ InvoiceNo: ext.taxInvoice }}
                            heads={['Cost/Job Center', 'Account Head', 'Sub Account Head', 'RegNo', 'Tax Type', 'Type', 'Balance', 'Amount']}
                            row={(x) => [x.VCCCode, x.DCACode, x.SubDCACode, x.TaxNo, x.TaxType, x.Type, fmt(x.VBalance), fmt(x.VAmount)]}
                            alignRight={[6, 7]}
                            onClose={() => setExt((p) => ({ ...p, taxInvoice: null }))}
                        />
                    )}
                </>
            );
        },
        beforeAction: (action, r, d, { aux, ext }) => {
            const items = Array.isArray(aux.vwoInvoices) ? aux.vwoInvoices : [];
            if (!items.length) return ['Invalid Submission'];
            return allItemsChecked(items, ext) ? [] : ['Please Verify Writeoff Invoices'];
        },
        approve: {
            route: 'Purchase/ApproveVendorPayableWriteoff',
            // The API reads the action from Status and the role from VRoleID (legacy controller mapping).
            payload: (r, d, { action, note, user, roleId }) => ({
                Tranno: r.Tranno, Status: action, Remarks: note, Createdby: user, VRoleID: String(roleId),
            }),
            ok: ['Submitted'],
        },
    },

    // Legacy /Purchase/VendorCMSPaymentVerification (+ VendorCMSPaymentVerificationGrid / VendorCMSPaymentVerificationView /
    // ViewCMSPaymentAddedVerificationData / …InnerData). On Approve legacy downloads the bank CMS sheet.
    VendorCMSPayment: {
        title: 'Vendor CMS Payment Verification',
        successLabel: 'Vendor CMS Payment',
        noun: 'payment',
        icon: Building2,
        searchPlaceholder: 'Search by transaction, bank, date…',
        queue: { route: 'Purchase/VendorCMSPaymentVerificationGrid', params: ({ roleId, userId }) => ({ Roleid: roleId, Created: String(userId), Userid: userId }) },
        itemKey: (r) => r.TransactionNo,
        card: {
            title: (r) => r.TransactionNo,
            subtitle: (r) => r.BankName,
            meta: (r) => r.TransactionDate,
            amount: (r) => money(r.Amount),
        },
        searchText: (r) => `${r.TransactionNo} ${r.BankName} ${r.TransactionDate} ${r.Amount}`,
        detail: { route: 'Purchase/VendorCMSPaymentVerificationView', params: (r) => ({ TranNo: r.TransactionNo, MOID: r.MOID, RID: r.RID }) },
        moid: (r, d) => d.MOID || r.MOID,
        remarksKey: (r) => r.TransactionNo,
        // Legacy drops Return from this screen's status list.
        showReturn: 'No',
        excludeActions: () => ['return'],
        header: {
            title: (r) => r.TransactionNo,
            subtitle: (r, d) => d.BankName,
            chips: (r, d) => [d.ModeofPay, d.TransactionDate],
        },
        sections: (r, d) => [{
            title: 'Payment',
            fields: [
                ['Bank Name', d.BankName], ['Mode of Pay', d.ModeofPay], ['Number', d.Number], ['Transaction Date', d.TransactionDate],
                ['Amount in Words', d.AmountInWords, true],
            ],
        }],
        extra: (r, d, { ext, setExt, toast }) => (
            <VendorCMSVerifyGrid tranNo={r.TransactionNo} ext={ext} setExt={setExt} toast={toast} />
        ),
        beforeAction: (action, r, d, { ext }) => (Object.values(ext.cmsVendors || {}).some(Boolean) ? [] : ['Select Vendors for payment']),
        approve: {
            route: 'Purchase/ApproveVendorCMSPayment',
            // The API reads the action from TransactionStatus (legacy controller mapping).
            payload: (r, d, { action, note, user, roleId }) => ({
                RID: String(d.RID ?? r.RID), TransactionNo: r.TransactionNo, TransactionStatus: action, Remarks: note, Createdby: user, Roleid: roleId,
            }),
            ok: ['Submitted'],
            afterOk: async (s, r, d, { action }) => {
                if (String(action).toLowerCase() === 'approve') await downloadVendorCMSExcel(r.TransactionNo);
            },
        },
    },

    // Legacy /Purchase/BOESettelmentVerification (+ BOESettelmentVerificationGrid / BOESettelmentVerificationView /
    // ViewBOEPaymentVerificationData; each LC expands to BOEPaymentInnerData — shown here as a popup)
    BOESettlement: {
        title: 'BOE Settlement Verification',
        successLabel: 'BOE Settlement',
        noun: 'settlement',
        icon: Ship,
        searchPlaceholder: 'Search by transaction, bank, date…',
        queue: { route: 'Purchase/BOESettelmentVerificationGrid', params: ({ roleId, userId }) => ({ Roleid: roleId, Created: String(userId), Userid: userId }) },
        itemKey: (r) => r.TransactionNo,
        card: {
            title: (r) => r.TransactionNo,
            subtitle: (r) => r.BankName,
            meta: (r) => r.TransactionDate,
            amount: (r) => money(r.Amount),
        },
        searchText: (r) => `${r.TransactionNo} ${r.BankName} ${r.TransactionDate} ${r.Amount}`,
        detail: { route: 'Purchase/BOESettelmentVerificationView', params: (r) => ({ TranNo: r.TransactionNo, MOID: r.MOID, RID: r.RID }) },
        rowAux: [
            { name: 'boeLcs', route: 'Purchase/ViewBOEPaymentVerificationData', params: (r) => ({ Trno: r.TransactionNo }) },
        ],
        moid: (r, d) => d.MOID || r.MOID,
        remarksKey: (r) => r.TransactionNo,
        // Legacy drops Return from this screen's status list.
        showReturn: 'No',
        excludeActions: () => ['return'],
        header: {
            title: (r) => r.TransactionNo,
            subtitle: (r, d) => d.BankName,
            chips: (r, d) => [d.ModeofPay, d.TransactionDate],
        },
        sections: (r, d) => [{
            title: 'Payment',
            fields: [
                ['Bank Name', d.BankName], ['Mode of Pay', d.ModeofPay], ['Number', d.Number], ['Transaction Date', d.TransactionDate],
                ['Transaction Amount', money(d.TransactionAmount)], ['Amount in Words', d.AmountInWords, true],
            ],
        }],
        extra: (r, d, { aux, ext, setExt }) => {
            const lcs = Array.isArray(aux.boeLcs) ? aux.boeLcs : [];
            const total = lcs.reduce((a, x) => a + (Number(x.Amount) || 0), 0);
            return (
                <>
                    <Block title="LC Settlements">
                        <SimpleTable
                            heads={['LC No', 'Transaction No', 'BOE Date', 'BOE Settlement Date', 'Amount', '']}
                            rows={lcs.map((x) => [x.LCNO, x.TranNo, x.PaidDate, x.ValidUptoDate, fmt(x.Amount), (
                                <button key="v" type="button" onClick={() => setExt((p) => ({ ...p, lc: x }))}
                                    className="text-indigo-600 dark:text-indigo-400 font-semibold hover:underline">Invoices</button>
                            )])}
                            alignRight={[4]}
                            foot={lcs.length ? ['', '', '', 'Total', fmt(total), ''] : undefined}
                        />
                    </Block>
                    {ext.lc && (
                        <RouteTableModal
                            title={`LC ${ext.lc.LCNO} — Invoices`}
                            route="Purchase/BOEPaymentInnerData"
                            params={{ LCNO: ext.lc.LCNO, Tranno: ext.lc.TranNo }}
                            heads={['Cost Center', 'Invoice No', 'PO No', 'Amount']}
                            row={(x) => [x.CCCode, x.InvoiceNo, x.PoNo, fmt(x.DetAmount)]}
                            alignRight={[3]}
                            onClose={() => setExt((p) => ({ ...p, lc: null }))}
                        />
                    )}
                </>
            );
        },
        approve: {
            route: 'Purchase/ApproveVendorBOEPayment',
            // The API reads the action from TransactionStatus (legacy controller mapping).
            payload: (r, d, { action, note, user, roleId }) => ({
                RID: String(d.RID ?? r.RID), TransactionNo: r.TransactionNo, TransactionStatus: action, Remarks: note, Createdby: user, Roleid: roleId,
            }),
            ok: ['Submitted'],
        },
    },

    // Legacy /Purchase/LCBGCreationVerification (+ LCBGCreationVerificationGrid / LCBGCreationVerificationView)
    LCBGCreation: {
        title: 'LC / BG Creation Verification',
        successLabel: 'LC/BG',
        noun: 'LC/BG',
        icon: Landmark,
        searchPlaceholder: 'Search by LC/BG no, type, dates…',
        queue: { route: 'Purchase/LCBGCreationVerificationGrid', params: ({ roleId, userId }) => ({ Roleid: roleId, Created: String(userId), Userid: userId }) },
        itemKey: (r) => `${r.LCBGno}|${r.RID}`,
        card: {
            title: (r) => r.LCBGno,
            subtitle: (r) => lcbgTypeName(r.LCBGType),
            meta: (r) => [r.OpeningDate, r.ValidityDate].filter(Boolean).join(' → '),
            amount: (r) => money(r.TotalValue),
        },
        searchText: (r) => `${r.LCBGno} ${r.LCBGType} ${r.OpeningDate} ${r.ValidityDate} ${r.TotalValue}`,
        detail: { route: 'Purchase/LCBGCreationVerificationView', params: (r) => ({ LCBGno: r.LCBGno, MOID: r.MOID, RID: r.RID }) },
        moid: (r, d) => d.MOID || r.MOID,
        remarksKey: (r) => r.LCBGno,
        // Legacy drops Return from this screen's status list.
        showReturn: 'No',
        excludeActions: () => ['return'],
        header: {
            title: (r) => r.LCBGno,
            subtitle: (r, d) => money(d.TotalValue ?? r.TotalValue),
            chips: (r, d) => [lcbgTypeName(d.LCBGType || r.LCBGType)],
        },
        // LC is raised for a vendor, BG for a client / sub client (legacy view branches on LCBGType).
        sections: (r, d) => [{
            fields: [
                ['Type', lcbgTypeName(d.LCBGType)],
                d.LCBGType === 'LC' && ['Vendor code and Name', d.VendorCode],
                d.LCBGType !== 'LC' && ['Client code and Name', d.ClientCode],
                d.LCBGType !== 'LC' && ['Sub-Client code and Name', d.SubClientCode],
                ['PO Number', d.PONO, true],
                ['LC/BG No', d.LCBGno], ['Opening Date', d.OpeningDate], ['Validity Date', d.ValidityDate], ['Tolerance', d.Tolerance],
                ['Margin FD', d.FD], ['Basic Value', money(d.BasicValue)], ['GST Value', money(d.GSTValue)], ['Charges', money(d.Charges)],
                ['Total Value', money(d.TotalValue)], ['Bank Remarks', d.BankRemarks, true],
            ],
        }],
        approve: {
            route: 'Purchase/ApproveLCBG',
            // The API reads the action from Status (legacy controller mapping).
            payload: (r, d, { action, note, user, roleId }) => ({
                RID: Number(r.RID), LCBGno: r.LCBGno, Status: action, Remarks: note, CreatedBy: user, RoleId: roleId,
            }),
            ok: ['Submitted'],
        },
    },

    // Legacy /Purchase/LCBGAmendVerification (+ LCBGAmendVerificationGrid / LCBGAmendVerificationView)
    LCBGAmend: {
        title: 'LC / BG Amend / Close Verification',
        successLabel: 'LC/BG',
        noun: 'LC/BG',
        icon: Landmark,
        searchPlaceholder: 'Search by LC/BG no, type, amend / close…',
        queue: { route: 'Purchase/LCBGAmendVerificationGrid', params: ({ roleId, userId }) => ({ Roleid: roleId, Created: String(userId), Userid: userId }) },
        itemKey: (r) => `${r.LCBGNo}|${r.RID}`,
        card: {
            title: (r) => r.LCBGNo,
            subtitle: (r) => [lcbgTypeName(r.LCBGType), r.LCBGStatusType].filter(Boolean).join(' · '),
            meta: (r) => r.Date,
            amount: (r) => money(r.TotalValue),
        },
        searchText: (r) => `${r.LCBGNo} ${r.LCBGType} ${r.LCBGStatusType} ${r.Date} ${r.TotalValue}`,
        detail: { route: 'Purchase/LCBGAmendVerificationView', params: (r) => ({ LCBGno: r.LCBGNo, MOID: r.MOID, RID: r.RID }) },
        moid: (r, d) => d.MOID || r.MOID,
        // Legacy loads remarks with Trno = the amendment's TransactionNo (returned as Refno).
        remarksKey: (r, d) => d.Refno,
        // Legacy drops Return from this screen's status list.
        showReturn: 'No',
        excludeActions: () => ['return'],
        header: {
            title: (r) => r.LCBGNo,
            subtitle: (r, d) => money(d.TotalValue ?? r.TotalValue),
            chips: (r, d) => [lcbgTypeName(d.LCBGType || r.LCBGType), d.LCBGStatusType || r.LCBGStatusType],
        },
        // LC is raised for a vendor, BG for a client / sub client; Amend shows the value split, Close only the total.
        sections: (r, d) => {
            const isAmend = (d.LCBGStatusType || r.LCBGStatusType) === 'Amend';
            return [{
                fields: [
                    ['Type', lcbgTypeName(d.LCBGType)],
                    d.LCBGType === 'LC' && ['Vendor code and Name', d.VendorCode],
                    d.LCBGType !== 'LC' && ['Client code and Name', d.ClientCode],
                    d.LCBGType !== 'LC' && ['Sub-Client code and Name', d.SubClientCode],
                    ['PO Number', d.PONO, true],
                    isAmend && d.LCVENPOS && ['New POs added in this amendment', d.LCVENPOS, true],
                    ['LC/BG No', d.LCBGNo], ['Status Type', d.LCBGStatusType], [isAmend ? 'Amend Date' : 'Close Date', d.Date],
                    isAmend && ['Tolerance', d.Tolerance],
                    isAmend && ['Basic Value', money(d.BasicValue)],
                    isAmend && ['GST Value', money(d.GSTValue)],
                    isAmend && ['Charges', money(d.Charges)],
                    ['Total Value', money(d.TotalValue)],
                    ['Bank Remarks', d.BankRemarks, true],
                ],
            }];
        },
        approve: {
            route: 'Purchase/ApproveLCBGAmend',
            // The API reads the action from Status (legacy controller mapping).
            payload: (r, d, { action, note, user, roleId }) => ({
                RID: Number(r.RID), LCBGNo: r.LCBGNo, Status: action, Remarks: note, CreatedBy: user, RoleId: roleId,
            }),
            ok: ['Submitted'],
        },
    },

    // Legacy /AccountsApproval/VerifyClientScrapSaleInvoice (+ VerifyClientScrapSaleInvoiceGrid / VerifyClientScrapSaleInvoiceView)
    ClientScrapSaleInvoice: {
        title: 'Client Scrap Sale Invoice Verification',
        successLabel: 'Scrap Sale Invoice',
        noun: 'invoice',
        icon: Recycle,
        searchPlaceholder: 'Search by invoice, request no, cost center…',
        queue: { route: 'Accounts/GetVerificationClientScrapSaleInvoice', params: ({ roleId, userId }) => ({ Roleid: roleId, UID: userId }) },
        itemKey: (r) => r.ClientInvoiceNo,
        card: {
            title: (r) => r.ClientInvoiceNo,
            subtitle: (r) => [r.RequestNumber && `Request ${r.RequestNumber}`, r.InvoiceSubType].filter(Boolean).join(' · '),
            meta: (r) => r.CCCode,
        },
        searchText: (r) => `${r.ClientInvoiceNo} ${r.RequestNumber} ${r.CCCode} ${r.InvoiceSubType}`,
        detail: { route: 'Accounts/GetVerificationClientScrapTypeInvoicebyNo', params: (r) => ({ InvoiceNo: r.ClientInvoiceNo }) },
        moid: (r, d) => d.MOID,
        remarksKey: (r) => r.ClientInvoiceNo,
        // Legacy drops Return from this screen's status list.
        showReturn: 'No',
        excludeActions: () => ['return'],
        header: {
            title: (r) => r.ClientInvoiceNo,
            subtitle: (r, d) => money(d.Total),
            chips: (r, d) => [d.InvoiceSubType, d.CCCode],
        },
        sections: (r, d) => {
            const gst = d.IsGstApplicable === 'Yes';
            const intra = d.Statecheck === true || String(d.Statecheck).toLowerCase() === 'true';
            return [{
                title: 'Invoice',
                fields: [
                    ['Client Invoice Number', d.ClientInvoiceNo], ['Request Number', d.RequestNumber], ['Cost Center', d.CCCode],
                    ['Invoice Sub Type', d.InvoiceSubType], ['Client', d.ClientName], ['Sub Client', d.SubClientName],
                    ['Running Account Number', d.RANO], ['Invoice Date', d.VerificationInvDate], ['Invoice Making Date', d.VerificationMakingDate],
                    ['Basic Value', money(d.BasicValue)], ['Is GST Applicable', d.IsGstApplicable], ['Is TCS Applicable', d.IsTCSApplicable],
                    gst && ['GST Type', d.GSTType], gst && ['Company GST', d.CompanyGST], gst && ['Client GST', d.ClientGST],
                ],
            }, {
                title: 'GST',
                fields: gst ? [
                    ['Account Head', d.Taxdcas],
                    intra && ['CGST Sub Account Head', d.Cgstsdca], intra && ['CGST Amount', money(d.Cgstsdcaamt)],
                    intra && ['SGST Sub Account Head', d.Sgstsdca], intra && ['SGST Amount', money(d.Sgstsdcaamt)],
                    !intra && ['IGST Sub Account Head', d.Igstsdca], !intra && ['IGST Amount', money(d.Igstsdcaamt)],
                    ['Total Tax', money(d.TaxTotal)],
                ] : [],
            }, {
                title: 'TCS',
                fields: d.IsTCSApplicable === 'Yes'
                    ? [['Account Head', d.TCSDCA], ['Sub Account Head', d.TCSSDCA], ['Amount', money(d.TCSAmount)]]
                    : [],
            }, {
                fields: [['Transaction Amount', money(d.Total)], d.InvoiceRemarks && ['Remarks', d.InvoiceRemarks, true]],
            }];
        },
        approve: {
            route: 'Accounts/ApproveScrapClientInvoice',
            payload: (r, d, { action, note, user, roleId }) => ({
                ClientInvoiceNo: r.ClientInvoiceNo, Action: action, ApprovalNote: note, CreatedBy: user, Roleid: roleId,
            }),
            // spApproveClientScrapInvoice answers "Submitted+<invoice no>".
            ok: (s) => String(s || '').split('+')[0] === 'Submitted',
        },
    },

    // Legacy /Purchase/VerifySPPOInvoice (+ VerifySPPOInvoiceGrid / VerifySPPOInvoiceView; returned rows → EditSPPOInvoice)
    SPPOInvoice: {
        title: 'SP Invoice Verification',
        successLabel: 'SP Invoice',
        noun: 'invoice',
        icon: FileSpreadsheet,
        searchPlaceholder: 'Search by invoice, PO, vendor, cost center…',
        queue: { route: 'Purchase/GetVerificationSPPOInvoice', params: ({ roleId, userId }) => ({ RoleId: roleId, Userid: userId }) },
        itemKey: (r) => r.InvoiceId || r.SPPOInvoiceNo,
        card: {
            title: (r) => r.SPPOInvoiceNo,
            subtitle: (r) => r.VendorName,
            meta: (r) => [r.SPPONo, r.CCName].filter(Boolean).join(' · '),
            amount: (r) => money(r.NetAmount),
        },
        searchText: (r) => `${r.SPPOInvoiceNo} ${r.SPPONo} ${r.VendorName} ${r.CCName} ${r.NetAmount}`,
        detail: { route: 'Purchase/GetSPPOInvoiceByNo', method: 'post', params: (r) => ({ InvoiceNo: r.SPPOInvoiceNo }) },
        moid: (r, d) => d.MOID,
        // Legacy passes the net amount so approval limits apply.
        chkAmt: (r, d) => d.NetAmount,
        remarksKey: (r) => r.SPPOInvoiceNo,
        // Legacy never offers Return on the verify view.
        showReturn: 'No',
        excludeActions: () => ['return'],
        isReturned: (r) => String(r.Status).trim() === '0',
        returnedNotice: 'Correct and resubmit it from the SP Invoice screen — there is nothing to verify until it is resubmitted.',
        header: {
            title: (r) => r.SPPOInvoiceNo,
            subtitle: (r, d) => money(d.NetAmount),
            chips: (r, d) => [d.VendorName, d.SPPONo && `PO ${d.SPPONo}`],
        },
        sections: (r, d) => {
            const tax = d.TaxApplicable === 'Yes';
            const intra = d.Statecheck === true || String(d.Statecheck).toLowerCase() === 'true';
            return [{
                title: 'Invoice',
                fields: [
                    ['Vendor', d.VendorName], ['PO Number', d.SPPONo], ['Cost Center', d.CCCode], ['Account Head', d.DCACode],
                    ['Sub Account Head', d.SubDCACode], ['Invoice No', d.SPPOInvoiceNo], ['Invoice Date', d.SPPOInvoiceDate],
                    ['Invoice Making Date', d.SPPOInvoiceMakingDate], ['Basic Value', money(d.SPPOBasicValue)], ['Is Tax Applicable?', d.TaxApplicable],
                    tax && ['GST Type', d.GSTType], tax && ['GST Nos', d.CompanyGST], tax && ['Vendor GST No', d.VendorGST],
                ],
            }, {
                title: 'GST',
                fields: tax && d.Taxdcas != null ? [
                    ['Account Head', d.Taxdcas],
                    intra && ['CGST Sub Account Head', d.Cgstsdca], intra && ['CGST Amount', money(d.Cgstsdcaamt)],
                    intra && ['SGST Sub Account Head', d.Sgstsdca], intra && ['SGST Amount', money(d.Sgstsdcaamt)],
                    !intra && ['IGST Sub Account Head', d.Igstsdca], !intra && ['IGST Amount', money(d.Igstsdcaamt)],
                    ['Total Tax', money(sppoTaxTotal(d))],
                ] : [],
            }, {
                title: 'Totals',
                fields: [
                    ['Advance', money(d.Advance)], ['Retention Value', money(d.Retention)], ['Hold Value', money(d.Hold)],
                    ['Invoice Value', money(d.InvoiceValue)], ['Net Amount', money(d.NetAmount)], ['Description', d.Description, true],
                ],
            }];
        },
        extra: (r, d) => {
            const lines = (list) => list || [];
            const sum = (list) => lines(list).reduce((a, x) => a + (Number(x.Amount) || 0), 0);
            // ApprovedUser carries "Role:Employee||…" — the first entry is the creator.
            const approvals = String(d.ApprovedUser || '').split('||').filter(Boolean).map((t, i) => {
                const [role, emp] = t.split(':');
                return [i === 0 ? 'Created By' : 'Verified By', role, emp];
            });
            const chargeTable = (title, list) => lines(list).length > 0 && (
                <Block title={title}>
                    <SimpleTable
                        heads={['Cost Center', 'Account Head', 'Sub Account Head', 'Amount']}
                        rows={lines(list).map((x) => [x.CCCode, x.DCACode, x.SubDCACode, fmt(x.Amount)])}
                        alignRight={[3]}
                        foot={['', '', 'Sub Total', fmt(sum(list))]}
                    />
                </Block>
            );
            return (
                <>
                    {d.FileName && (
                        <a href={buildVendorInvoiceUrl(d.FileName)} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold shadow hover:shadow-lg">
                            <FileText className="w-4 h-4" /> View Invoice Document
                        </a>
                    )}
                    {chargeTable('Other Charges', d.OtherChargeList)}
                    {chargeTable('Deductions', d.DeductionList)}
                    {approvals.length > 0 && (
                        <Block title="Approvals">
                            <SimpleTable heads={['Action', 'Role Name', 'Employee']} rows={approvals} />
                        </Block>
                    )}
                </>
            );
        },
        approve: {
            route: 'Purchase/ApproveSPPOInvoice',
            // Legacy ApproveSPPOInvoice (Purchase.js): tax as one type/DCA/total, charges and deductions as trailing-comma CSVs.
            payload: (r, d, { action, note, user, roleId }) => ({
                SPPOInvoiceNo: d.SPPOInvoiceNo, SPPONo: d.SPPONo, SPPOInvoiceDate: d.SPPOInvoiceDate, SPPOBasicValue: d.SPPOBasicValue,
                CCCode: d.CCCode, Action: action, ApprovalNote: note, CreatedBy: user, RoleId: roleId,
                Taxtypes: d.GSTType, Taxdcas: d.Taxdcas, Taxamounts: sppoTaxTotal(d),
                Otherchargedcas: (d.OtherChargeList || []).map((x) => `${x.DCACode},`).join(''),
                Otherchargeamounts: (d.OtherChargeList || []).map((x) => `${x.Amount},`).join(''),
                Deductiondcas: (d.DeductionList || []).map((x) => `${x.DCACode},`).join(''),
                Deductionamounts: (d.DeductionList || []).map((x) => `${x.Amount},`).join(''),
                TaxApplicable: d.TaxApplicable,
            }),
            ok: ['Submited', 'Submitted'],
        },
    },

    // Legacy /AccountsApproval/VerifyClientPOAmend (+ VerifyClientPOAmendGrid / VerifyClientPOAmendView; returned rows
    // edit inline via UpdateAmendPO → spInsertAmendPO 'Update')
    ClientPOAmend: {
        title: 'Client PO Amendment Verification',
        successLabel: 'Client PO Amendment',
        noun: 'amendment',
        icon: FilePen,
        searchPlaceholder: 'Search by amend PO, PO, cost center…',
        queue: { route: 'Accounts/GetVerificationClientPOAmend', params: ({ roleId, userId }) => ({ Roleid: roleId, Userid: userId }) },
        itemKey: (r) => r.Amendpono,
        card: {
            title: (r) => r.Amendpono,
            subtitle: (r) => `PO ${r.oldPONO}`,
            meta: (r) => r.CostCenter,
            amount: (r) => money(r.Amendtotalvalue),
        },
        searchText: (r) => `${r.Amendpono} ${r.oldPONO} ${r.CostCenter} ${r.Amendtotalvalue}`,
        detail: { route: 'Accounts/GetVerificationClientPOAmendbyno', params: (r) => ({ Amendpono: r.Amendpono }) },
        moid: (r, d) => d.MOID,
        remarksKey: (r) => r.Amendpono,
        // Legacy keeps the full status list here, Return included.
        showReturn: 'Yes',
        isReturned: (r) => String(r.Status).trim() === '0',
        header: {
            title: (r) => r.Amendpono,
            subtitle: (r, d) => money(d.Amendtotalvalue),
            chips: (r, d) => [`PO ${d.oldPONO || r.oldPONO}`, d.CostCenter],
        },
        sections: (r, d) => [{
            fields: [
                ['Amend Purchase Order No', d.Amendpono], ['Purchase Order No', d.oldPONO], ['Cost Center', d.CostCenter],
                ['Completion Date', d.amendpocompletiondate], ['Amount', money(d.Amendpovalue)], ['GST', money(d.Amendegst)],
                ['Total Amount', money(d.Amendtotalvalue)],
            ],
        }],
        approve: {
            route: 'Accounts/ApproveClientPOAmend',
            payload: (r, d, { action, note, user, roleId }) => ({
                oldPONO: d.oldPONO, Amendpono: d.Amendpono, Action: action, ApprovalNote: note, RoleId: roleId, Createdby: user,
            }),
            ok: ['Submited', 'Submitted'],
        },
        // Legacy UpdateClientPOAmend. Its date picker never initialised (a commented-out Razor block held `status`),
        // so the completion date could not really be changed there; here it can, from today on.
        resubmit: {
            fields: [
                { key: 'CostCenter', label: 'Cost Center', readOnly: true },
                { key: 'oldPONO', label: 'Purchase Order No', readOnly: true },
                { key: 'Amendpono', label: 'Amend Purchase Order No', readOnly: true },
                { key: 'CompletionDate', label: 'Completion Date', type: 'date', required: true },
                { key: 'Amendpovalue', label: 'Amend PO Value', required: true, filter: /^\d*\.?\d{0,2}$/ },
                { key: 'Amendegst', label: 'GST Value', required: true, filter: /^\d*\.?\d{0,2}$/ },
                { key: 'Amendtotalvalue', label: 'Total Amend Value', readOnly: true },
            ],
            initial: (r, d) => ({
                CostCenter: d.CostCenter || '', oldPONO: d.oldPONO || '', Amendpono: d.Amendpono || '',
                CompletionDate: isoDate(d.amendpocompletiondate),
                Amendpovalue: d.Amendpovalue != null ? String(d.Amendpovalue) : '', Amendegst: d.Amendegst != null ? String(d.Amendegst) : '',
                Amendtotalvalue: d.Amendtotalvalue != null ? String(d.Amendtotalvalue) : '',
            }),
            derive: (v) => ({ ...v, Amendtotalvalue: ((parseFloat(v.Amendpovalue) || 0) + (parseFloat(v.Amendegst) || 0)).toFixed(2) }),
            validate: (v) => [
                v.CompletionDate && v.CompletionDate < isoDate(new Date()) && 'Completion Date cannot be in the past',
                !(Number(v.Amendtotalvalue) > 0) && 'Invalid Total Amend Value',
            ].filter(Boolean),
            route: 'Accounts/UpdateAmendPO',
            payload: (r, d, v, { user, roleId }) => ({
                oldPONO: d.oldPONO, amendpocompletiondate: v.CompletionDate, Amendpono: d.Amendpono,
                Amendpovalue: parseFloat(v.Amendpovalue) || 0, Amendtotalvalue: parseFloat(v.Amendtotalvalue) || 0, Amendegst: parseFloat(v.Amendegst) || 0,
                Action: 'Update', RoleId: roleId, Createdby: user,
            }),
            ok: ['Submited', 'Submitted'],
        },
    },

    // Legacy /Purchase/VerifyItemCodeUpdation (+ VerifyItemCodeUpdationGrid / VerifyItemCodeUpdationView). The verifier may
    // correct the requested values; approval first re-runs ItemCodeCheckPendings, then ApproveUpdateItemCode.
    ItemCodeUpdation: {
        title: 'Item Code Updation Verification',
        successLabel: 'Item Code Updation',
        noun: 'item code update',
        icon: PackageSearch,
        searchPlaceholder: 'Search by item code, name…',
        queue: { route: 'Purchase/VerifyItemCodeUpdationGrid', params: ({ roleId }) => ({ Roleid: roleId }) },
        itemKey: (r) => r.Rowid,
        card: {
            title: (r) => r.ItemCode,
            subtitle: (r) => r.Itemname,
            amount: (r) => money(r.Basicprice),
        },
        searchText: (r) => `${r.ItemCode} ${r.Itemname} ${r.Basicprice}`,
        detail: {
            route: 'Purchase/GetViewItemCodeverificatioUpdation',
            params: (r) => ({ Status: r.Status, Rid: r.Rowid }),
            select: (data) => (Array.isArray(data) ? data[0] || null : data),
        },
        rowAux: [
            { name: 'icuHsn', route: 'Purchase/GetHsnupd', params: (r) => ({ Itemcode: r.ItemCode }) },
            { name: 'icuUnits', route: 'Purchase/GetAllUnitsupd', params: () => ({}) },
            { name: 'icuRemarks', route: 'Purchase/GetItemCodeRemarks', params: (r, d) => ({ TranNo: d.TranNo }), when: (r, d) => !!d.TranNo },
        ],
        moid: (r, d) => d.MOID,
        // Legacy drops Return from this screen's status list.
        showReturn: 'No',
        excludeActions: () => ['return'],
        header: {
            title: (r) => r.ItemCode,
            subtitle: (r, d) => d.Itemname || r.Itemname,
        },
        sections: (r, d) => [{
            title: 'Connected Item',
            fields: d.ConnectedTanNo != null
                ? [['Connected Item Code', d.ConnectedItemcode], ['Connected DCA', d.ConnectedDCA], ['Connected Sub DCA', d.ConnectedSDca]]
                : [],
        }],
        extra: (r, d, { aux, ext, setExt }) => {
            const remarks = Array.isArray(aux.icuRemarks) ? aux.icuRemarks : [];
            return (
                <>
                    <ItemCodeUpdationEditor d={d} aux={aux} ext={ext} setExt={setExt} />
                    {remarks.length > 0 && (
                        <Block title="Remarks">
                            <SimpleTable heads={['Action', 'Role', 'By', 'Remarks']} rows={remarks.map((x) => [x.Action, x.ActionRole, x.ActionBy, x.ActionRemarks])} />
                        </Block>
                    )}
                </>
            );
        },
        beforeAction: async (action, r, d, { ext }) => {
            const v = itemCodeUpdValues(d, ext);
            const errors = [
                !v.Itemname.trim() && 'Please Enter Item Name',
                !(Number(v.Basicprice) > 0) && 'Invalid Basic Price',
                !v.HSNCode && 'Select HSN',
                !v.Specification.trim() && 'Please Enter Specification',
                !v.Units && 'Select Units',
            ].filter(Boolean);
            if (errors.length) return errors;
            // Legacy re-checks for other pending updates of the same item before approving.
            try {
                const res = await postRoute('Purchase/ItemCodeCheckPendings', { ItemCode: r.ItemCode });
                const st = typeof res === 'string' ? res : res?.Data;
                return st === 'Successfull' ? [] : [st || 'This item code has pending transactions'];
            } catch (e) {
                return [typeof e === 'string' ? e : 'Could not check pending transactions for this item'];
            }
        },
        approve: {
            route: 'Purchase/ApproveUpdateItemCode',
            payload: (r, d, { action, note, user, roleId, ext }) => {
                const v = itemCodeUpdValues(d, ext);
                return {
                    UItemCode: r.ItemCode, UItemname: v.Itemname.trim().toUpperCase(), UBasicprice: String(v.Basicprice), UHSNCode: String(v.HSNCode),
                    USpecification: v.Specification.trim(), UUnits: String(v.Units), UDate: new Date().toString(), URemarks: note,
                    URid: String(r.Rowid), UAppstatus: action, URoleID: String(roleId), UCreatedby: user,
                };
            },
            ok: ['Submitted'],
        },
    },
};

// Legacy /HR/VerifyLBCCSEPPay (+ VerifyLBCCSEPPayGrid / VerifyLBCCSEPPayView) — Labour Wages / PF / ESI payments
// (SEPPayments WorkFlowLevelId 662). Legacy's views are the Accounts CCSEPPay screen on the HR endpoints, same
// queue/detail shapes and approve payload, minus the Amount in Words field — so this reuses CCSEPPay.
VERIFICATION_CONFIGS.LBCCSEPPay = {
    ...VERIFICATION_CONFIGS.CCSEPPay,
    title: 'Labour Salary/PF/ESI Payment Verification',
    queue: { route: 'HR/GetVerificationLBSEPPay', params: ({ roleId }) => ({ RoleId: roleId }) },
    detail: { route: 'HR/GetLBSEPPaybyRefno', params: (r) => ({ TransactionRefno: r.TransactionRefno }) },
    sections: (r, d) => [{
        title: 'Payment For',
        fields: [['Payment For', sepPaymentLabel(d.PaymentFor)], ['Year', d.Year], ['Month', d.MonthName]],
    }, {
        title: 'Payment Details',
        fields: [
            ['Bank', d.BankName], ['Mode of Pay', d.ModeofPay || d.ModeOfPay], ['Payment No', d.PaymentNo],
            ['Payment Date', d.PaymentDate], ['Payment Amount', money(d.PaymentAmount)],
            d.Remarks && ['Remarks', d.Remarks, true],
        ],
    }],
    approve: { ...VERIFICATION_CONFIGS.CCSEPPay.approve, route: 'HR/ApproveLBSEPPay' },
};

// Inbox path (anchored) / exact category → config key.
export const CONFIG_VERIFICATION_ROUTES = [
    { key: 'ShareIssuance', path: /\/accountsapproval\/verifyshareissuance(?:$|[?#/])/, categories: ['share issuance verification'] },
    { key: 'ESOPFaceValueReceipt', path: /\/accountsapproval\/verifyesopfacevaluereceipt(?:$|[?#/])/, categories: ['esop facevalue receipt verification'] },
    { key: 'Client', path: /\/accountsapproval\/verifyclient(?:$|[?#/])/, categories: ['client creation'] },
    { key: 'SubClient', path: /\/accountsapproval\/verifysubclient(?:$|[?#/])/, categories: ['subclientcreation'] },
    { key: 'ClientInvoice', path: /\/accountsapproval\/verifyclientinvoice(?:$|[?#/])/, categories: ['client invoice creation'] },
    { key: 'ClientManufacturingInvoice', path: /\/accountsapproval\/verifyclientmanufacturinginvoice(?:$|[?#/])/, categories: ['client manufacturing invoice'] },
    { key: 'ClientTradingInvoice', path: /\/accountsapproval\/verifyclienttradinginvoice(?:$|[?#/])/, categories: ['client trading invoice'] },
    { key: 'ClientBadDebt', path: /\/accountsapproval\/verifyclientbaddebtrecievable(?:$|[?#/])/, categories: ['bad debt'] },
    { key: 'DCA', path: /\/accountsapproval\/verifydca(?:$|[?#/])/, categories: ['accounthead creation'] },
    { key: 'SubDCA', path: /\/accountsapproval\/verifysubdca(?:$|[?#/])/, categories: ['subaccounthead creation'] },
    { key: 'MasterGroups', path: /\/accountsapproval\/verifymastergroups(?:$|[?#/])/, categories: ['group creation'] },
    { key: 'SubGroups', path: /\/accountsapproval\/verifysubgroups(?:$|[?#/])/, categories: ['subgroup creation'] },
    { key: 'ChildGroups', path: /\/accountsapproval\/verifychildgroups(?:$|[?#/])/, categories: ['childgroup creation'] },
    { key: 'Ledger', path: /\/accountsapproval\/verifyledger(?:$|[?#/])/, categories: ['ledger creation'] },
    { key: 'TaxGeneral', path: /\/accountsapproval\/verifytaxgeneral(?:$|[?#/])/, categories: ['gst creation', 'general taxes creation'] },
    { key: 'ITCode', path: /\/accountsapproval\/approveit(?:$|[?#/])/, categories: ['itcode creation'] },
    { key: 'CCBudget', path: /\/accountsapproval\/costcenterbudgetapproval(?:$|[?#/])/, categories: ['cost center budget(pcc)', 'cost center budget(npcc/other capital)'] },
    { key: 'DCABudget', path: /\/accountsapproval\/verifydcabudget(?:$|[?#/])/, categories: ['account head budget(pcc)', 'account head budget(npcc/other capital)'] },
    { key: 'WorkInProgress', path: /\/accounts\/verifyworkinprogress(?:$|[?#/])/, categories: ['work in progress'] },
    { key: 'GeneralPayment', path: /\/accountsapproval\/verifygeneralpayment(?:$|[?#/])/, categories: ['general payable'] },
    { key: 'JournalVoucher', path: /\/accountsapproval\/verifyjournalvoucher(?:$|[?#/])/, categories: ['jv verification'] },
    { key: 'CCSEPPay', path: /\/accounts\/verifyccseppay(?:$|[?#/])/, categories: ['salary/wages/pf/esi payments'] },
    { key: 'LBCCSEPPay', path: /\/hr\/verifylbccseppay(?:$|[?#/])/, categories: ['labour salary/pf/esi payments'] },
    { key: 'NewStockIssue', path: /\/purchase\/verfiynewstockissue(?:$|[?#/])/, categories: ['new stock issue(pcc)', 'new stock issue(npcc)'] },
    { key: 'OldStockIssue', path: /\/purchase\/verifyoldstockissue(?:$|[?#/])/, categories: ['old stock issue'] },
    { key: 'OldStockReceived', path: /\/purchase\/verifyoldstockreceived(?:$|[?#/])/, categories: ['old stock received(pcc)', 'old stock received(npcc)'] },
    { key: 'NewStockTransfer', path: /\/purchase\/verifynewstocktransfer(?:$|[?#/])/, categories: ['new stock transfer'] },
    { key: 'ItemsTransfer', path: /\/purchase\/verifyitemstransfer(?:$|[?#/])/, categories: ['stocktransfer (pcc)', 'stocktransfer (npcc)'] },
    { key: 'ItemsTransferIssue', path: /\/purchase\/verifyitemstransferissue(?:$|[?#/])/, categories: ['stocktransferissue (pcc)', 'stocktransferissue (npcc)'] },
    { key: 'CapitalStockIssue', path: /\/purchase\/verifycapitalstockissue(?:$|[?#/])/, categories: ['stock conversion(capital)', 'stock conversion(pcc)'] },
    { key: 'DirectStockUpdation', path: /\/purchase\/verifydirectstockupdation(?:$|[?#/])/, categories: ['direct stock updation'] },
    { key: 'StoreClosing', path: /\/purchase\/verifystoreclosing(?:$|[?#/])/, categories: ['store closing'] },
    { key: 'CCStockClose', path: /\/purchase\/verifyccstockclose(?:$|[?#/])/, categories: ['cc stock close'] },
    { key: 'AssetSale', path: /\/purchase\/verifyassetsale(?:$|[?#/])/, categories: ['asset sale'] },
    { key: 'AssetSaleReceipt', path: /\/purchase\/verifyassetsalereceipt(?:$|[?#/])/, categories: ['asset sale receipt'] },
    { key: 'HSNCreation', path: /\/purchase\/verifyhsncreation(?:$|[?#/])/, categories: ['hsn/sac code verification'] },
    { key: 'CreditDebitNote', path: /\/purchase\/verifycreditanddebitnote(?:$|[?#/])/, categories: ['credit and debit note'] },
    { key: 'TDSPayment', path: /\/purchase\/verifytdspayment(?:$|[?#/])/, categories: ['vendor tds payment'] },
    { key: 'VendorPayableWriteoff', path: /\/purchase\/verifyvendorpayablewriteoff(?:$|[?#/])/, categories: ['vendor payable writeoff'] },
    { key: 'VendorCMSPayment', path: /\/purchase\/vendorcmspaymentverification(?:$|[?#/])/, categories: ['vendor cms payment'] },
    { key: 'BOESettlement', path: /\/purchase\/boesettelmentverification(?:$|[?#/])/, categories: ['boe settlement'] },
    { key: 'LCBGCreation', path: /\/purchase\/lcbgcreationverification(?:$|[?#/])/, categories: ['lc and bg creation'] },
    { key: 'LCBGAmend', path: /\/purchase\/lcbgamendverification(?:$|[?#/])/, categories: ['lc and bg amend/close'] },
    { key: 'ClientScrapSaleInvoice', path: /\/accountsapproval\/verifyclientscrapsaleinvoice(?:$|[?#/])/, categories: ['scrap sale invoice'] },
    { key: 'SPPOInvoice', path: /\/purchase\/verifysppoinvoice(?:$|[?#/])/, categories: ['sp invoice(pcc)'] },
    { key: 'ClientPOAmend', path: /\/accountsapproval\/verifyclientpoamend(?:$|[?#/])/, categories: ['amend client po'] },
    { key: 'ItemCodeUpdation', path: /\/purchase\/verifyitemcodeupdation(?:$|[?#/])/, categories: ['item code updation'] },
];

export const getConfigVerificationKey = (path, category) => {
    const hit = CONFIG_VERIFICATION_ROUTES.find((r) => r.path.test(path) || r.categories.includes(category));
    return hit ? hit.key : null;
};
